import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Avatar,
  Alert,
  Snackbar,
  Link,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CallEndIcon from "@mui/icons-material/CallEnd";
import LogoutIcon from "@mui/icons-material/Logout";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PersonIcon from "@mui/icons-material/Person";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  openAgentVideoJoinUrl,
  readVideoAppReturnPath,
  returnFromAgentVideoToApp,
} from "../../utils/activeVideoUi";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import ClosedCaptionOutlinedIcon from "@mui/icons-material/ClosedCaptionOutlined";
import ClosedCaptionDisabledOutlinedIcon from "@mui/icons-material/ClosedCaptionDisabledOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import { useBrand } from "../../context/BrandContext";
import { readTabUserData } from "../../utils/tabAuthStorage";
import {
  fetchPublicVideoRoom,
  fetchPublicVideoLobby,
  startPublicVideoLobby,
  fetchPublicVideoAccessToken,
  postPublicVideoTranscript,
  postPublicVideoCaptions,
  postPublicVideoLeave,
  postPublicVideoLeaveKeepalive,
  postPublicVideoEnd,
  setPublicVideoRoomPrivacy,
  fetchPublicVideoMessages,
  postPublicVideoMessage,
  postPublicVideoReact,
  uploadPublicVideoFile,
  joinVirtualOfficeRoom,
  listVirtualOfficeRooms,
  leaveVirtualOfficeRoom,
  leaveVirtualOfficeRoomKeepalive,
  patchVirtualOfficePresence,
  patchVirtualOfficePresenceKeepalive,
  patchVirtualOfficePresenceOrRejoin,
  fetchVirtualOfficePresence,
  signalVirtualOfficeFloor,
  admitVirtualOfficePrivateRoom,
  createVirtualOfficeHuddle,
  listVirtualOfficeHuddles,
  listVirtualOfficeBirthdayWishes,
  sendVirtualOfficeBirthdayWish,
  joinVirtualOfficeHuddle,
  listVirtualOfficeHuddleJoinRequests,
  resolveVirtualOfficeHuddleJoinRequest,
} from "../../api/client";
import {
  VirtualOfficeHuddleGrid,
  VirtualOfficeOtherCallsList,
} from "../../components/VirtualOfficeHuddleCard";
import { peopleInOtherCalls } from "../../components/virtualOfficeOtherCalls";
import { parsePresencePayload } from "../VirtualOffice/virtualOfficePresenceAlerts";
import { useSocketContext } from "../../context/SocketContext";
import {
  publishOtherMeetingEnded,
  publishOtherMeetingStarted,
  publishVirtualOfficePauseTracks,
  publishVirtualOfficeResumeTracks,
  readVirtualOfficeSession,
  subscribeVirtualOfficeBridge,
  writeVirtualOfficeSession,
} from "../../utils/virtualOfficeSessionBridge";
import { useTranslation } from "react-i18next";
import PhoneField from "../../components/PhoneField";
import { phoneDigitsOnly, toE164Digits } from "../../utils/phone";
import VideoBackgroundPicker, {
  VideoBackgroundControlButton,
} from "./VideoBackgroundPicker";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import LocalCafeOutlinedIcon from "@mui/icons-material/LocalCafeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import VideoInCallChat, { VideoInCallChatButton } from "./VideoInCallChat";
import VideoPreJoinParticipants from "./VideoPreJoinParticipants";
import {
  closeVideoCallDocumentPip,
  isDocumentPipOpen,
  isDocumentPipSupported,
  openVideoCallDocumentPip,
} from "./videoDocumentPip";
import {
  applyVideoBackground,
  attachLocalCameraPreview,
  clearVideoBackground,
  isVideoBackgroundSupported,
  loadImageFromFile,
  resolvePresetBackgroundImage,
  buildThemeVideoBackgroundPreset,
  DEFAULT_VIDEO_BACKGROUND_MODE,
  THEME_VIDEO_BACKGROUND_ID,
  VIDEO_BACKGROUND_PRESETS,
  shouldMirrorLocalPreview,
  dimensionsFromVideoTracks,
  backgroundFitForPreset,
} from "./videoBackground";
import {
  resolveCallContext,
  roomLabelForContext,
  endedCallCopy,
  classifyJoinVideoError,
  connectingCopy,
  errorScreenCopy,
  preJoinCopy,
  waitingInCallCopy,
  recordingConsentCopy,
  mediaControlCopy,
  crowdCapacityCopy,
  CALL_CONTEXT_SUPPORT,
  CALL_CONTEXT_VIRTUAL_OFFICE,
  isTeamCallContext,
  isAgendaCallContext,
  isVirtualOfficeCallContext,
  isVoSideRoomCallContext,
  isAnyVirtualOfficeVideoContext,
} from "./videoCallCopy";
import { isClockPolicy, virtualOfficeCopy } from "../VirtualOffice/attendanceCopy";
import {
  floorJoinCaption,
  isVoCommBusyStatus,
  isVoCommPausedStatus,
  normalizeVoCommStatus,
  resolveClockTimezone,
  voCommStatusLabel,
  voCommStatusOptions,
  voCommStatusPauseTitle,
} from "../VirtualOffice/voCommunicationStatus";
import { useVirtualOfficeJornadaAlerts } from "../VirtualOffice/useVirtualOfficeJornadaAlerts";
import VirtualOfficeJornadaAlerts from "../VirtualOffice/VirtualOfficeJornadaAlerts";
import {
  layoutMode,
  remoteGridSx,
  voFloorRugSx,
  overflowParticipantCount,
  shouldWarnCrowd,
  resolveSpotlightSid,
  selectVisibleRemoteSids,
  MAX_MAIN_GRID_TILES,
} from "./videoRemoteLayout";
import VoFloorScene from "./VoFloorScene";
import VoFloorSeat from "./VoFloorSeat";
import VoFloorCamPeek from "./VoFloorCamPeek";
import VoFloorReactions from "./VoFloorReactions";
import VoFloorPrompt from "./VoFloorPrompt";
import VoFloorSpark from "./VoFloorSpark";
import { useVoFloorOfficeLife } from "./useVoFloorOfficeLife";
import { useVoFloorSparks } from "./useVoFloorSparks";
import { sparkEchoCopy } from "./voFloorSparks";
import { userIdFromAgentIdentity } from "./voOfficeLife";
import {
  countVideoChatUnread,
  maxVideoChatMessageId,
  mergeVideoChatMessageLists,
  pickVideoChatMedia,
  readVideoChatLastRead,
  toggleVideoChatReactions,
  videoChatLastReadStorageKey,
  videoChatMediaTypeFromFile,
  videoChatMessagesMatch,
  writeVideoChatLastRead,
} from "./videoChatThread";
import {
  MAX_VO_FLOOR_SEATS,
  MAX_VO_FLOOR_OTHERS,
  VO_FLOOR_REACTIONS,
  VO_FLOOR_REACTION_MAX,
  createVoFloorReaction,
  freezeVoFloorReactionTimers,
  resumeVoFloorReactionTimers,
  tickVoFloorReactions,
  nextVoSignalId,
  promptForDate,
  readVoFloorPromptHidden,
  shouldUseVoFloorScene,
  voFloorDaypart,
  voFloorSceneTokens,
  formatFloorClock,
  voFloorFirstName,
  reactionSenderLabel,
  sanitizeCargoName,
  presenceCargoOf,
  findSelfPresence,
  writeVoFloorPromptHidden,
  resolveVoFloorPerson,
} from "./voFloorLiving";
import {
  countVoLiveVideoPeers,
  countVoPresencePeers,
  listVoPresenceGhosts,
  listVoPresencePeers,
  userIdsOffVoFloorRug,
  shouldJoinVoTwilio,
  shouldKeepVoOfficeOnPageHide,
  shouldKeepVoTwilioLive,
  shouldSkipVoTwilioOnJoin,
  voPresencePollMs,
  voTwilioHoldGraceMs,
  VO_PRESENCE_HEARTBEAT_MS,
} from "./voTwilioEconomy";
import {
  canPlayVoChatSound,
  isVoFloorAudioUnlocked,
  playVoFloorSound,
  setVoFloorSpeakerMuted,
  soundKindForReaction,
  unlockVoFloorAudio,
} from "./voFloorSounds";
import {
  encodeChatMessageBody,
  isChatMentionForMe,
  mergeChatMentionPeople,
  parseChatMentions,
} from "./videoChatMentions";
import { applyRemoteVideoPriorities } from "./videoTrackPriority";
import { useThemeContext } from "../../context/ThemeContext";
import { useTranslatedThemeOptions } from "../../hooks/useTranslatedThemeOptions";
import VoBirthdayBar from "./VoBirthdayBar";
import VoBirthdayDialog from "./VoBirthdayDialog";
import { birthdayPeople, birthdayGiftById } from "./voBirthday";
import VoHighlightsBar from "./VoHighlightsBar";
import VoHighlightDialog from "./VoHighlightDialog";
import { useVoFloorHighlights } from "./useVoFloorHighlights";
import {
  appendHighlightReaction,
  cheerCopy,
  groupHighlightReactions,
  honoreeSeal,
  highlightSeatLabel,
  highlightSeatTitle,
  speakCopy,
} from "./voHighlights";
import {
  createLocalScreenShareTracks,
  friendlyScreenShareError,
  isScreenShareSupported,
  isScreenShareTrack,
  presenterLabelFromIdentity,
  screenShareUnsupportedMessage,
  stopLocalScreenShareTracks,
} from "./videoScreenShare";
import {
  isPlaceholderVideoLabel,
  participantLabelFromIdentity,
  resolveCaptionSpeakerLabel,
} from "./videoParticipantLabel";
import VideoCameraOffPresence from "./VideoCameraOffPresence";
import {
  cameraRequiredJoinCopy,
  classifyRequiredCameraError,
  localTracksHaveCamera,
  parseRequireCameraFlag,
  splitLocalAudioTracks,
  splitLocalCameraTracks,
} from "./videoCameraPolicy";
import {
  applyRemoteSpeakerMute,
  applySeatBoxStyle,
  CAMERA_TILE_ASPECT,
  clearRemoteParticipantTiles,
  collectRemoteSeatKeys,
  ensureParticipantTileChrome,
  evictRemoteTilesForIdentity,
  firstEnabledCameraTrack,
  mediaStateFromParticipant,
  resolveTileMediaState,
  sanitizeAvatarUrl,
  styleTileCameraMedia,
  syncParticipantTile,
  syncTileFocus,
  voFloorCrowdTier,
  voFloorSeatFrameSx,
} from "./videoMediaPresence";
import {
  bindLocalMediaTrackEnded,
  buildAudioConstraint,
  buildGuestShareUrl,
  buildVideoConstraint,
  enumerateVideoCallDevices,
  getPreferredAudioDeviceId,
  getPreferredVideoDeviceId,
  isVoSpeakerMuted,
  isWaitSoundMuted,
  localTrackDeviceId,
  playWaitBeep,
  resolveAudioDeviceAfterChange,
  resolveVideoDeviceAfterChange,
  setPreferredAudioDeviceId,
  setPreferredVideoDeviceId,
  setVoSpeakerMuted,
  setWaitSoundMuted,
} from "./videoDevices";
import {
  excludeSelfFromParticipants,
  getStoredJoinIdentity,
  setStoredJoinIdentity,
} from "./videoJoinSession";

function extractApiMessage(err) {
  return (
    err?.userFacing?.message ||
    err?.response?.data?.message ||
    err?.message ||
    ""
  );
}

function extractApiCode(err) {
  return (
    err?.userFacing?.code ||
    err?.response?.data?.code ||
    err?.code ||
    ""
  );
}

function extractApiAction(err) {
  return (
    err?.userFacing?.action ||
    err?.userFacingAction ||
    err?.response?.data?.userAction ||
    ""
  );
}

function buildJoinErrorGuide(err, { role, callContext, missingCompany = false } = {}) {
  return classifyJoinVideoError(extractApiMessage(err), {
    code: extractApiCode(err),
    action: extractApiAction(err),
    missingCompany,
    role,
    callContext,
  });
}

function roleFromParticipantIdentity(identity) {
  const id = String(identity || "");
  if (/^agent_/i.test(id)) return "agent";
  if (/^guest_/i.test(id)) return "guest";
  return null;
}

function chatMessageKey(msg) {
  if (!msg) return "";
  if (msg.clientId) return `c:${msg.clientId}`;
  if (msg.id != null && Number(msg.id) > 0) return `i:${msg.id}`;
  return `t:${msg.senderIdentity || ""}:${msg.createdAt || ""}:${msg.body || ""}`;
}

function normalizeReplyPreview(raw) {
  const preview = raw?.replyPreview && typeof raw.replyPreview === "object" ? raw.replyPreview : null;
  const replyToId = Number(preview?.id || raw?.replyToId || raw?.reply_to_id);
  if (!Number.isFinite(replyToId) || replyToId <= 0) {
    return { replyToId: null, replyPreview: null };
  }
  if (!preview && !raw?.replyPreview) {
    return { replyToId, replyPreview: { id: replyToId, senderName: "", body: "" } };
  }
  const media = pickVideoChatMedia(preview || raw || {});
  return {
    replyToId,
    replyPreview: {
      id: replyToId,
      senderName: String(preview?.senderName || preview?.authorName || ""),
      body: String(preview?.body || preview?.content || ""),
      messageType: media.messageType,
      mediaUrl: media.mediaUrl || null,
      fileName: media.fileName || null,
    },
  };
}

function normalizeChatReactions(raw) {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const identity = String(item.identity || item.senderIdentity || "").trim();
      const emoji = String(item.emoji || "").trim();
      if (!identity || !emoji) return null;
      return {
        identity,
        senderName: String(item.senderName || item.userName || "").trim(),
        emoji,
      };
    })
    .filter(Boolean);
}

function normalizeChatMessage(raw) {
  if (!raw || typeof raw !== "object") return null;
  const media = pickVideoChatMedia(raw);
  const body = String(raw.body || raw.text || raw.message || "").trim();
  if (!body && !media.mediaUrl) return null;
  const id = Number(raw.messageId || raw.id);
  const parsed = parseChatMentions(body, []);
  const mentions = Array.isArray(raw.mentions) && raw.mentions.length ? raw.mentions : parsed.mentions;
  const reply = normalizeReplyPreview(raw);
  const reactions = normalizeChatReactions(raw.reactions);
  return {
    id: Number.isFinite(id) && id > 0 ? id : null,
    clientId: raw.clientId ? String(raw.clientId) : null,
    senderIdentity: String(raw.senderIdentity || raw.identity || ""),
    senderName: String(raw.senderName || raw.displayName || raw.name || "Participante"),
    senderRole: String(raw.senderRole || raw.role || roleFromParticipantIdentity(raw.senderIdentity) || "guest"),
    body,
    messageType: media.messageType,
    mediaUrl: media.mediaUrl || null,
    fileName: media.fileName || null,
    createdAt: raw.createdAt || raw.created_at || new Date().toISOString(),
    pending: Boolean(raw.pending),
    mentions,
    mentionAll: Boolean(raw.mentionAll) || parsed.mentionAll,
    replyToId: reply.replyToId,
    replyPreview: reply.replyPreview,
    ...(reactions ? { reactions } : {}),
  };
}

function newChatClientId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function attachTrack(track, container, { mirror = false, contain = false } = {}) {
  if (!track || !container) return null;
  try {
    track.detach().forEach((el) => el.remove());
  } catch {
    /* ignore */
  }
  const el = track.attach();
  if (track.kind === "video") {
    // contain: o quadro inteiro cabe no tile 16:9, independente do nº de pessoas.
    styleTileCameraMedia(el);
    if (mirror && !isScreenShareTrack(track) && !contain) {
      el.style.transform = "scaleX(-1)";
    }
    el.muted = true;
    el.playsInline = true;
    el.setAttribute("playsinline", "true");
    const play = el.play?.();
    if (play && typeof play.catch === "function") play.catch(() => {});
  } else {
    el.style.width = "100%";
    el.style.height = "100%";
  }
  container.appendChild(el);
  return el;
}

function clearMediaContainer(container) {
  if (!container) return;
  container.querySelectorAll("video, audio").forEach((el) => {
    el.srcObject = null;
    el.remove();
  });
  container.querySelectorAll("canvas[data-video-bg-preview]").forEach((el) => {
    el.remove();
  });
}

function detachTrackElements(track) {
  if (!track) return;
  try {
    track.detach().forEach((el) => el.remove());
  } catch {
    /* ignore */
  }
}

function stopTracks(tracks) {
  (tracks || []).forEach((t) => {
    try {
      t.stop();
      t.detach().forEach((el) => el.remove());
    } catch {
      /* ignore */
    }
  });
}

function detachParticipant(participant, containerMap) {
  const node = containerMap.get(participant.sid);
  if (node) {
    node.querySelectorAll("video, audio").forEach((el) => {
      el.srcObject = null;
      el.remove();
    });
  }
  participant.tracks.forEach((pub) => {
    if (pub.track) pub.track.detach().forEach((el) => el.remove());
  });
}

function ControlButton({
  activeOff,
  activeOn,
  title,
  onClick,
  children,
  danger = false,
  disabled = false,
  buttonRef = null,
}) {
  const btn = (
    <IconButton
      ref={buttonRef}
      onClick={onClick}
      aria-label={title}
      disabled={disabled}
      sx={{
        width: { xs: 42, md: 48 },
        height: { xs: 42, md: 48 },
        bgcolor: danger
          ? "#d93025"
          : activeOff
            ? "#ea4335"
            : activeOn
              ? "#8ab4f8"
              : "rgba(255,255,255,0.12)",
        color: activeOn ? "#202124" : "#fff",
        opacity: disabled ? 0.55 : 1,
        "&:hover": {
          bgcolor: danger
            ? "#c5221f"
            : activeOff
              ? "#d93025"
              : activeOn
                ? "#aecbfa"
                : "rgba(255,255,255,0.2)",
        },
        "&.Mui-disabled": {
          bgcolor: "rgba(255,255,255,0.12)",
          color: "#fff",
        },
      }}
    >
      {children}
    </IconButton>
  );
  return (
    <Tooltip title={title}>
      {disabled ? (
        <Box component="span" sx={{ display: "inline-flex" }}>
          {btn}
        </Box>
      ) : (
        btn
      )}
    </Tooltip>
  );
}

/**
 * Página pública de vídeo Twilio — estrutura Meet + identidade XPro 4
 * /video/:token ou /video/lobby/:lobbyToken
 */
export default function PublicVideoPage({ mode = "room" }) {
  const theme = useTheme();
  const brand = useBrand();
  const { socket } = useSocketContext() || {};
  const { currentTheme } = useThemeContext() || {};
  const themeOptions = useTranslatedThemeOptions();
  const navigate = useNavigate();
  const { token, lobbyToken } = useParams();
  const [searchParams] = useSearchParams();
  const company = String(searchParams.get("company") || "").trim().toLowerCase();
  const nameFromQuery = String(
    searchParams.get("name") || searchParams.get("displayName") || ""
  )
    .trim()
    .slice(0, 80);
  const joinRole =
    String(searchParams.get("role") || "").trim().toLowerCase() === "agent"
      ? "agent"
      : "guest";
  const contextFromQuery = String(searchParams.get("context") || "").trim();
  const voRoomIdFromQuery = Number(searchParams.get("voRoomId") || 0);

  const [phase, setPhase] = useState("loading");
  /** Rota do app de onde a sala foi aberta (mobile/PWA, mesma janela). */
  const [appReturnPath] = useState(() => readVideoAppReturnPath());
  const [channelName, setChannelName] = useState("");
  const [callContext, setCallContext] = useState(() =>
    resolveCallContext({
      searchParam: contextFromQuery,
      fromLobby: mode === "lobby",
    })
  );
  const [virtualOfficeRoomId, setVirtualOfficeRoomId] = useState(() =>
    Number.isFinite(voRoomIdFromQuery) && voRoomIdFromQuery > 0 ? voRoomIdFromQuery : null
  );
  const [voFloorName, setVoFloorName] = useState(() =>
    String(readVirtualOfficeSession()?.roomName || "").trim()
  );
  const isVirtualOfficeContext =
    contextFromQuery === "virtual_office" ||
    isVirtualOfficeCallContext(callContext) ||
    (Number.isFinite(Number(virtualOfficeRoomId)) && Number(virtualOfficeRoomId) > 0);
  const [displayName, setDisplayName] = useState(nameFromQuery);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [errorGuide, setErrorGuide] = useState(null);
  const [errorCanRetryJoin, setErrorCanRetryJoin] = useState(true);
  const [muted, setMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(() => {
    const initial = isVoSpeakerMuted();
    setVoFloorSpeakerMuted(initial);
    return initial;
  });
  const [peerMediaByIdentity, setPeerMediaByIdentity] = useState({});
  const [camOff, setCamOff] = useState(false);
  const [camBusy, setCamBusy] = useState(false);
  const [micBusy, setMicBusy] = useState(false);
  const [voPausedForOtherMeeting, setVoPausedForOtherMeeting] = useState(false);
  const [voOtherMeetingTitle, setVoOtherMeetingTitle] = useState("");
  const [voLeaveDialogOpen, setVoLeaveDialogOpen] = useState(false);
  const [voLeaveBusy, setVoLeaveBusy] = useState(false);
  const voJornadaAlerts = useVirtualOfficeJornadaAlerts({
    enabled: isVirtualOfficeContext && phase === "incall" && !voPausedForOtherMeeting,
    present:
      isVirtualOfficeContext &&
      phase === "incall" &&
      Boolean(virtualOfficeRoomId) &&
      !voPausedForOtherMeeting,
  });
  const [remoteCount, setRemoteCount] = useState(0);
  const [remoteTileUserIds, setRemoteTileUserIds] = useState([]);
  const remoteTileUserIdsRef = useRef("");
  const [remoteTileIdentities, setRemoteTileIdentities] = useState([]);
  const remoteTileIdentitiesRef = useRef("");
  const [dominantSid, setDominantSid] = useState(null);
  const [pinnedSid, setPinnedSid] = useState(null);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  /** self | remote | already_ended — quem encerrou, na perspectiva local */
  const [endCause, setEndCause] = useState(null);
  const [remoteLeftRole, setRemoteLeftRole] = useState(null);
  const [previewReady, setPreviewReady] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [copiedLink, setCopiedLink] = useState(false);
  /** Path do lobby de origem (para reabrir nova solicitação após a sala encerrar). */
  const [lobbyReturnPath, setLobbyReturnPath] = useState(null);
  /** true se esta sessão começou pelo link público do canal. */
  const [fromLobbyEntry, setFromLobbyEntry] = useState(mode === "lobby");
  const [bgMode, setBgMode] = useState(DEFAULT_VIDEO_BACKGROUND_MODE);
  const [bgBusy, setBgBusy] = useState(false);
  const [bgSupported, setBgSupported] = useState(true);
  const [bgAnchorEl, setBgAnchorEl] = useState(null);
  const [bgError, setBgError] = useState("");
  const [sharingScreen, setSharingScreen] = useState(false);
  const [localPreviewNonce, setLocalPreviewNonce] = useState(0);
  const [remotePresenting, setRemotePresenting] = useState(false);
  const [screenError, setScreenError] = useState("");
  const [presenterLabel, setPresenterLabel] = useState("");
  const [screenSupported] = useState(() => isScreenShareSupported());
  const [isPrivateRoom, setIsPrivateRoom] = useState(false);
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const [voPrivateOpen, setVoPrivateOpen] = useState(false);
  const [voPrivateBusy, setVoPrivateBusy] = useState(false);
  const [voPrivatePresence, setVoPrivatePresence] = useState([]);
  const [voPrivateSelected, setVoPrivateSelected] = useState([]);
  const [voPrivateError, setVoPrivateError] = useState("");
  const [voPrivateMode, setVoPrivateMode] = useState("start"); // start | admit
  const [voHuddleTitle, setVoHuddleTitle] = useState("Café");
  const [voHuddleVisibility, setVoHuddleVisibility] = useState("public");
  const [voHuddleRequests, setVoHuddleRequests] = useState([]);
  const [voHuddleRequestsOpen, setVoHuddleRequestsOpen] = useState(false);
  const [voFloorHuddles, setVoFloorHuddles] = useState([]);
  const [voFloorPresence, setVoFloorPresence] = useState([]);
  const [voFloorClock, setVoFloorClock] = useState(() => formatFloorClock());
  const [voFloorNow, setVoFloorNow] = useState(() => Date.now());
  const [voClockTimezone, setVoClockTimezone] = useState("");
  const [voStatusMenuAnchor, setVoStatusMenuAnchor] = useState(null);
  const [voTwilioHeld, setVoTwilioHeld] = useState(false);
  const [remoteWantsMedia, setRemoteWantsMedia] = useState(false);
  const [voFloorHuddleActingId, setVoFloorHuddleActingId] = useState(null);
  const [remoteCameraOnCount, setRemoteCameraOnCount] = useState(0);
  const [voReactions, setVoReactions] = useState([]);
  const [voArrivals, setVoArrivals] = useState([]);
  const [voPromptAnswers, setVoPromptAnswers] = useState([]);
  const [voPromptHidden, setVoPromptHidden] = useState(() =>
    readVoFloorPromptHidden(typeof window !== "undefined" ? window.localStorage : null)
  );
  const [voReactionMenuAnchor, setVoReactionMenuAnchor] = useState(null);
  const [voHouseBanners, setVoHouseBanners] = useState([]);
  const [voBirthdayWishes, setVoBirthdayWishes] = useState([]);
  const [voBirthdayTarget, setVoBirthdayTarget] = useState(null);
  const [voBirthdayBusy, setVoBirthdayBusy] = useState(false);
  const [voBirthdayError, setVoBirthdayError] = useState("");
  const [voHighlightTarget, setVoHighlightTarget] = useState(null);
  const [voPersonMenu, setVoPersonMenu] = useState(null);
  const [chatSeedMention, setChatSeedMention] = useState(null);
  const floorHighlightsRefetchRef = useRef(null);
  const birthdayUserIdsRef = useRef(new Set());
  const kudosByUserRef = useRef(new Map());
  const cargoByUserRef = useRef(new Map());
  const identityCargosRef = useRef(new Map());
  const localCargoRef = useRef("");
  const [twilioDbRoomId, setTwilioDbRoomId] = useState(null);
  const [privacySnack, setPrivacySnack] = useState("");
  const [mediaSnack, setMediaSnack] = useState("");
  const [privateForbidden, setPrivateForbidden] = useState(false);
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [consentDetailsOpen, setConsentDetailsOpen] = useState(false);
  const [recordingVisibility, setRecordingVisibility] = useState("space");
  const [requireCamera, setRequireCamera] = useState(false);
  const [localAvatarUrl, setLocalAvatarUrl] = useState(() => {
    try {
      const u = readTabUserData();
      return sanitizeAvatarUrl(u?.imagem || u?.avatarUrl);
    } catch {
      return "";
    }
  });
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [chatLastReadId, setChatLastReadId] = useState(0);
  const [localIdentity, setLocalIdentity] = useState(() => getStoredJoinIdentity(token));
  const [roomParticipants, setRoomParticipants] = useState([]);
  const [pipSupported] = useState(() => isDocumentPipSupported());
  const [pipActive, setPipActive] = useState(false);
  const [pipError, setPipError] = useState("");
  const [floorPeek, setFloorPeek] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDeviceId, setAudioDeviceId] = useState(() => getPreferredAudioDeviceId());
  const [videoDeviceId, setVideoDeviceId] = useState(() => getPreferredVideoDeviceId());
  const [waitSoundMuted, setWaitSoundMutedState] = useState(() => isWaitSoundMuted());
  const [networkQuality, setNetworkQuality] = useState(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [liveCaption, setLiveCaption] = useState("");
  const [captionHint, setCaptionHint] = useState("");
  const { t: tTeam } = useTranslation("team");
  const { t: tChat } = useTranslation("chat");

  const resolveAgentUserId = useCallback(() => {
    try {
      const u = readTabUserData();
      const id = Number(u?.id || u?.userId || u?.usuario_id);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }, []);

  // Agente XPro já logado nesta aba/origem: pré-preenche o nome se a URL não trouxe.
  useEffect(() => {
    if (nameFromQuery) return;
    try {
      const u = readTabUserData();
      const n = String(u?.nome || u?.name || "").trim().slice(0, 80);
      if (n) setDisplayName((prev) => (prev?.trim() ? prev : n));
    } catch {
      /* ignore */
    }
  }, [nameFromQuery]);

  const roomRef = useRef(null);
  const localTracksRef = useRef([]);
  const previewTracksRef = useRef([]);
  const previewVideoRef = useRef(null);
  const localPipRef = useRef(null);
  const remoteGridRef = useRef(null);
  const remoteContainersRef = useRef(new Map());
  const publishRemoteSeatKeys = useCallback(() => {
    const { userIds, identities } = collectRemoteSeatKeys(remoteContainersRef.current);
    const tileKey = userIds.slice().sort((a, b) => a - b).join(",");
    const identKey = identities.slice().sort().join("\n");
    if (tileKey !== remoteTileUserIdsRef.current) {
      remoteTileUserIdsRef.current = tileKey;
      setRemoteTileUserIds(userIds);
    }
    if (identKey !== remoteTileIdentitiesRef.current) {
      remoteTileIdentitiesRef.current = identKey;
      setRemoteTileIdentities(identities);
    }
  }, []);
  const publicTokenRef = useRef(token || null);
  const transcriptBufferRef = useRef([]);
  const transcriptFlushTimerRef = useRef(null);
  const displayNameRef = useRef(displayName);
  const endRequestedRef = useRef(false);
  const leftBySelfRef = useRef(false);
  const lastRemoteLeftRoleRef = useRef(null);
  const bgModeRef = useRef(DEFAULT_VIDEO_BACKGROUND_MODE);
  const bgImageRef = useRef(null);
  const bgApplySeqRef = useRef(0);
  const bgLogoSrcRef = useRef("/brands/xpro/logowhite.png");
  const themePresetRef = useRef(null);
  const presentationRef = useRef(null);
  const screenLocalRef = useRef({ video: null, audio: null, endedHandler: null });
  const remoteScreensRef = useRef(new Map());
  const stopScreenShareRef = useRef(async () => {});
  const leaveRef = useRef(() => {});
  const toggleScreenShareRef = useRef(() => {});
  const dataTrackRef = useRef(null);
  const chatOpenRef = useRef(false);
  const localIdentityRef = useRef("");
  const identityNamesRef = useRef(new Map());
  const identityAvatarsRef = useRef(new Map());
  const identityMediaRef = useRef(new Map());
  const remoteCameraOnCountRef = useRef(0);
  const voFloorSceneRef = useRef(false);
  const voFloorCrowdRef = useRef("roomy");
  const voArrivedSentRef = useRef(false);
  const localAvatarUrlRef = useRef(localAvatarUrl);
  const chatSeenKeysRef = useRef(new Set());
  const chatMentionPeopleRef = useRef([]);
  const voFloorPresenceRef = useRef([]);
  voFloorPresenceRef.current = voFloorPresence;
  const voClockTimezoneRef = useRef("");
  voClockTimezoneRef.current = voClockTimezone;
  const voFloorNowRef = useRef(Date.now());
  voFloorNowRef.current = voFloorNow;
  const openVoPersonMenuRef = useRef(() => {});
  const mutedRef = useRef(muted);
  const speakerMutedRef = useRef(speakerMuted);
  const camOffRef = useRef(camOff);
  const camBusyRef = useRef(false);
  const micBusyRef = useRef(false);
  const requireCameraRef = useRef(requireCamera);
  const sharingScreenRef = useRef(sharingScreen);
  const pinnedSidRef = useRef(null);
  const dominantSidRef = useRef(null);
  const roomLabelRef = useRef("");
  const pipOpeningRef = useRef(false);
  const teamMuteAppliedRef = useRef(false);
  const leaveButtonRef = useRef(null);
  const phaseRef = useRef(phase);
  const companyRef = useRef(company);
  const joinRoleRef = useRef(joinRole);
  /** Voltar ao app depois do diálogo de ponto do escritório virtual. */
  const returnAfterLeaveRef = useRef(false);
  phaseRef.current = phase;
  companyRef.current = company;
  joinRoleRef.current = joinRole;
  const reconnectingRef = useRef(false);
  const voIdleHoldRef = useRef(false);
  const voTwilioBusyRef = useRef(false);
  const voParkedAtRef = useRef(0);
  const voWakeUntilRef = useRef(0);
  const voSignalSeenRef = useRef(new Set());
  const voPresenceIdsRef = useRef(new Set());
  const isVoFloorForHuddlesRef = useRef(false);
  const audioDeviceIdRef = useRef(audioDeviceId);
  const videoDeviceIdRef = useRef(videoDeviceId);
  const audioDevicesRef = useRef([]);
  const prevAudioDeviceIdsRef = useRef(new Set());
  const unbindAudioEndedRef = useRef(null);
  const switchingAudioRef = useRef(false);
  const liveCaptionClearRef = useRef(null);
  /** Evita punch duplo (diálogo + pagehide). */
  const voLeaveCompletedRef = useRef(false);
  const virtualOfficeRoomIdRef = useRef(virtualOfficeRoomId);
  const isVirtualOfficeContextRef = useRef(isVirtualOfficeContext);

  useEffect(() => {
    virtualOfficeRoomIdRef.current = virtualOfficeRoomId;
  }, [virtualOfficeRoomId]);

  useEffect(() => {
    isVirtualOfficeContextRef.current = isVirtualOfficeContext;
  }, [isVirtualOfficeContext]);

  useEffect(() => {
    displayNameRef.current = displayName;
  }, [displayName]);

  useEffect(() => {
    localAvatarUrlRef.current = localAvatarUrl;
  }, [localAvatarUrl]);

  const rememberParticipantName = useCallback((identity, name) => {
    const id = String(identity || "").trim();
    const n = String(name || "").trim().slice(0, 80);
    if (!id || !n || isPlaceholderVideoLabel(n)) return false;
    if (identityNamesRef.current.get(id) === n) return false;
    identityNamesRef.current.set(id, n);
    return true;
  }, []);

  const rememberParticipantCargo = useCallback((identity, cargo) => {
    const id = String(identity || "").trim();
    const role = sanitizeCargoName(cargo);
    if (!id || !role) return false;
    if (identityCargosRef.current.get(id) === role) return false;
    identityCargosRef.current.set(id, role);
    return true;
  }, []);

  const rememberParticipantAvatar = useCallback((identity, url) => {
    const id = String(identity || "").trim();
    const src = sanitizeAvatarUrl(url);
    if (!id || !src) return false;
    if (identityAvatarsRef.current.get(id) === src) return false;
    identityAvatarsRef.current.set(id, src);
    return true;
  }, []);

  const rememberParticipantMedia = useCallback((identity, patch = {}) => {
    const id = String(identity || "").trim();
    if (!id) return false;
    const prev = identityMediaRef.current.get(id) || {};
    const next = {
      camOff: patch.camOff != null ? Boolean(patch.camOff) : prev.camOff,
      muted: patch.muted != null ? Boolean(patch.muted) : prev.muted,
      speakerMuted:
        patch.speakerMuted != null ? Boolean(patch.speakerMuted) : Boolean(prev.speakerMuted),
    };
    if (
      prev.camOff === next.camOff &&
      prev.muted === next.muted &&
      Boolean(prev.speakerMuted) === next.speakerMuted
    ) {
      return false;
    }
    identityMediaRef.current.set(id, next);
    setPeerMediaByIdentity((map) => ({ ...map, [id]: next }));
    return true;
  }, []);

  const labelForIdentity = useCallback((identity) => {
    return participantLabelFromIdentity(
      identity,
      identityNamesRef.current.get(String(identity || "").trim())
    );
  }, []);

  const applyRemoteTiles = useCallback(() => {
    const room = roomRef.current;
    const grid = remoteGridRef.current;
    let cameraOn = 0;
    let wantsMedia = false;
    const hiddenIds = voFloorSceneRef.current
      ? userIdsOffVoFloorRug(voFloorPresenceRef.current)
      : new Set();
    remoteContainersRef.current.forEach((box, sid) => {
      if (!box) return;
      if (grid && box.parentNode !== grid) grid.appendChild(box);
      applySeatBoxStyle(box, voFloorSceneRef.current);
      const participant = room?.participants?.get?.(sid) || null;
      const identity = String(participant?.identity || box.dataset.identity || "");
      const fromTracks = participant
        ? mediaStateFromParticipant(participant, isScreenShareTrack)
        : { hasCameraTrack: false, hasAudioTrack: false, camOff: true, muted: false };
      const announced = identityMediaRef.current.get(identity);
      const { camOff: tileCamOff, muted: tileMuted, speakerMuted: tileSpeakerMuted } =
        resolveTileMediaState(fromTracks, announced);
      const peerId = userIdFromAgentIdentity(identity);
      const person =
        Number(peerId) > 0
          ? voFloorPresenceRef.current.find((row) => Number(row.userId) === Number(peerId))
          : null;
      const hideOnFloor = Number(peerId) > 0 && hiddenIds.has(Number(peerId));
      if (hideOnFloor) {
        box.dataset.voFloorHidden = "1";
        box.style.display = "none";
        return;
      }
      if (box.dataset.voFloorHidden) delete box.dataset.voFloorHidden;
      box.style.display = "";
      syncParticipantTile(box, {
        name: labelForIdentity(identity),
        avatarUrl: identityAvatarsRef.current.get(identity) || "",
        camOff: tileCamOff,
        muted: tileMuted,
        speakerMuted: tileSpeakerMuted,
        voFloor: voFloorSceneRef.current,
        birthday: Boolean(peerId && birthdayUserIdsRef.current.has(Number(peerId))),
        kudosEmoji: peerId ? kudosByUserRef.current.get(Number(peerId))?.emoji || "" : "",
        kudosLabel: peerId ? highlightSeatLabel(kudosByUserRef.current.get(Number(peerId))) : "",
        kudosTitle: peerId ? highlightSeatTitle(kudosByUserRef.current.get(Number(peerId))) : "",
        cargo:
          (peerId && cargoByUserRef.current.get(Number(peerId))) ||
          identityCargosRef.current.get(identity) ||
          "",
        crowd: voFloorCrowdRef.current,
        status: person?.status || "present",
        joinedAt: person?.joinedAt || person?.joined_at || null,
        now: voFloorNowRef.current,
        timeZone: person?.timezone || voClockTimezoneRef.current,
      });
      if (!tileCamOff) cameraOn += 1;
      if (!tileCamOff || !tileMuted) wantsMedia = true;
    });
    publishRemoteSeatKeys();
    if (cameraOn !== remoteCameraOnCountRef.current) {
      remoteCameraOnCountRef.current = cameraOn;
      setRemoteCameraOnCount(cameraOn);
    }
    setRemoteWantsMedia(wantsMedia);
  }, [labelForIdentity, publishRemoteSeatKeys]);

  const pushVoReaction = useCallback((emoji, fromName, fromIdentity) => {
    const id = nextVoSignalId();
    const x = 18 + Math.round(Math.random() * 64);
    const y = 18 + Math.round(Math.random() * 28);
    const who = reactionSenderLabel(fromName);
    const hidden = typeof document !== "undefined" && document.hidden;
    setVoReactions((prev) => [
      ...prev.slice(-(VO_FLOOR_REACTION_MAX - 1)),
      createVoFloorReaction({
        id,
        emoji,
        x,
        y,
        fromName: who,
        fromIdentity,
        hidden,
      }),
    ]);
    playVoFloorSound(soundKindForReaction(emoji));
  }, []);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const hidden = typeof document !== "undefined" && document.hidden;
      setVoReactions((prev) => tickVoFloorReactions(prev, { now, hidden }));
    };
    const onVisibility = () => {
      const now = Date.now();
      const hidden = typeof document !== "undefined" && document.hidden;
      setVoReactions((prev) => {
        if (!prev.length) return prev;
        const next = hidden
          ? freezeVoFloorReactionTimers(prev, now)
          : resumeVoFloorReactionTimers(prev, now);
        return tickVoFloorReactions(next, { now, hidden });
      });
    };
    const id = window.setInterval(tick, 400);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const pushVoHouseBanner = useCallback((text) => {
    const line = String(text || "").trim();
    if (!line) return;
    const id = nextVoSignalId();
    setVoHouseBanners((prev) => [...prev.slice(-3), { id, text: line }]);
    playVoFloorSound("pulse");
    window.setTimeout(() => {
      setVoHouseBanners((prev) => prev.filter((item) => item.id !== id));
    }, 2800);
  }, []);

  const pushVoArrival = useCallback((name, identity) => {
    const id = nextVoSignalId();
    const label = voFloorFirstName(name || "Colega");
    setVoArrivals((prev) => [...prev.slice(-3), { id, name: label, identity }]);
    playVoFloorSound("arrive");
    window.setTimeout(() => {
      setVoArrivals((prev) => prev.filter((item) => item.id !== id));
    }, 2800);
  }, []);

  const publishVoData = useCallback((payload) => {
    if (!payload) return false;
    const body = payload.id ? payload : { ...payload, id: nextVoSignalId() };
    const track = dataTrackRef.current;
    if (track) {
      try {
        track.send(JSON.stringify(body));
        return true;
      } catch (e) {
        console.warn("[PublicVideo] vo DataTrack send:", e?.message || e);
      }
    }
    const voId = Number(virtualOfficeRoomIdRef.current) || 0;
    if (isVoFloorForHuddlesRef.current && voId > 0) {
      void signalVirtualOfficeFloor(voId, body).catch((e) => {
        console.warn("[PublicVideo] vo floor signal:", e?.message || e);
      });
      return true;
    }
    return false;
  }, []);

  const publishVoTwilioWake = useCallback(() => {
    const voId = Number(virtualOfficeRoomIdRef.current) || 0;
    if (!isVoFloorForHuddlesRef.current || voId <= 0) return;
    void signalVirtualOfficeFloor(voId, {
      type: "vo-twilio-wake",
      id: nextVoSignalId(),
      identity: localIdentityRef.current || "",
    }).catch(() => {});
  }, []);

  const publishVoArrived = useCallback(() => {
    const identity =
      localIdentityRef.current ||
      roomRef.current?.localParticipant?.identity ||
      "";
    const name = String(displayNameRef.current || "").trim();
    if (!identity || voArrivedSentRef.current) return;
    const sent = publishVoData({
      type: "vo-arrived",
      identity,
      displayName: name,
    });
    if (sent) {
      voArrivedSentRef.current = true;
      playVoFloorSound("welcome");
    }
  }, [publishVoData]);

  const publishVoReaction = useCallback(
    (emoji) => {
      const identity =
        localIdentityRef.current ||
        roomRef.current?.localParticipant?.identity ||
        "";
      const name = String(displayNameRef.current || "").trim();
      pushVoReaction(emoji, name, identity);
      publishVoData({
        type: "vo-reaction",
        emoji,
        from: identity,
        fromName: name,
      });
    },
    [publishVoData, pushVoReaction]
  );

  const announceLocalMedia = useCallback((type = "media_state") => {
    const identity =
      localIdentityRef.current ||
      roomRef.current?.localParticipant?.identity ||
      "";
    if (!identity) return;
    const payload = {
      type,
      id: nextVoSignalId(),
      identity,
      camOff: Boolean(camOffRef.current),
      muted: Boolean(mutedRef.current),
      speakerMuted: Boolean(speakerMutedRef.current),
    };
    if (type === "hello") {
      const name = String(displayNameRef.current || "").trim();
      if (!name) return;
      payload.displayName = name;
      payload.avatarUrl = localAvatarUrlRef.current || "";
      payload.cargo = localCargoRef.current || "";
    }
    const track = dataTrackRef.current;
    if (track) {
      try {
        track.send(JSON.stringify(payload));
      } catch (e) {
        console.warn(`[PublicVideo] ${type} DataTrack send:`, e?.message || e);
      }
    }
    const voId = Number(virtualOfficeRoomIdRef.current) || 0;
    if (isVoFloorForHuddlesRef.current && voId > 0) {
      void signalVirtualOfficeFloor(voId, payload).catch((e) => {
        console.warn(`[PublicVideo] ${type} floor signal:`, e?.message || e);
      });
    }
  }, []);

  const publishHelloOnDataTrack = useCallback(() => {
    announceLocalMedia("hello");
  }, [announceLocalMedia]);

  const publishMediaState = useCallback(() => {
    announceLocalMedia("media_state");
  }, [announceLocalMedia]);

  useEffect(() => {
    let changed = false;
    for (const p of Array.isArray(roomParticipants) ? roomParticipants : []) {
      if (rememberParticipantName(p?.identity, p?.displayName)) changed = true;
      if (rememberParticipantAvatar(p?.identity, p?.avatarUrl)) changed = true;
    }
    if (changed) applyRemoteTiles();
  }, [roomParticipants, rememberParticipantName, rememberParticipantAvatar, applyRemoteTiles]);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  useEffect(() => {
    speakerMutedRef.current = speakerMuted;
    setVoFloorSpeakerMuted(speakerMuted);
    applyRemoteSpeakerMute(
      [remoteGridRef.current, presentationRef.current],
      speakerMuted
    );
  }, [speakerMuted]);

  useEffect(() => {
    camOffRef.current = camOff;
  }, [camOff]);

  useEffect(() => {
    requireCameraRef.current = requireCamera;
    if (requireCamera) {
      setCamOff(false);
      camOffRef.current = false;
    }
  }, [requireCamera]);

  useEffect(() => {
    audioDeviceIdRef.current = audioDeviceId;
  }, [audioDeviceId]);

  useEffect(() => {
    videoDeviceIdRef.current = videoDeviceId;
  }, [videoDeviceId]);

  // Chamada de equipe: entra mudo (pode desmutar depois).
  useEffect(() => {
    if (teamMuteAppliedRef.current) return;
    if (!isTeamCallContext(callContext)) return;
    teamMuteAppliedRef.current = true;
    setMuted(true);
    mutedRef.current = true;
  }, [callContext]);

  useEffect(() => {
    sharingScreenRef.current = sharingScreen;
  }, [sharingScreen]);

  const refreshLocalCameraPreview = useCallback(() => {
    setLocalPreviewNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    pinnedSidRef.current = pinnedSid;
  }, [pinnedSid]);

  useEffect(() => {
    dominantSidRef.current = dominantSid;
  }, [dominantSid]);

  useEffect(() => {
    chatOpenRef.current = chatOpen;
  }, [chatOpen]);

  const chatLastReadKey = videoChatLastReadStorageKey({
    token: publicTokenRef.current || token,
    identity: localIdentity,
  });

  useEffect(() => {
    setChatLastReadId(readVideoChatLastRead(chatLastReadKey));
  }, [chatLastReadKey]);

  const markChatReadUpTo = useCallback(
    (messages) => {
      const maxId = maxVideoChatMessageId(messages);
      if (maxId <= 0) {
        setChatUnread(0);
        return;
      }
      setChatLastReadId((prev) => (maxId > prev ? maxId : prev));
      writeVideoChatLastRead(chatLastReadKey, maxId);
      setChatUnread(0);
    },
    [chatLastReadKey]
  );

  useEffect(() => {
    if (chatOpen) {
      markChatReadUpTo(chatMessages);
      return;
    }
    setChatUnread(
      countVideoChatUnread(chatMessages, {
        lastReadId: chatLastReadId,
        localIdentity,
      })
    );
  }, [chatOpen, chatMessages, chatLastReadId, localIdentity, markChatReadUpTo]);

  useEffect(() => {
    localIdentityRef.current = localIdentity;
  }, [localIdentity]);

  useEffect(() => {
    if (phase !== "incall") {
      setChatOpen(false);
    }
  }, [phase]);

  useEffect(() => {
    bgModeRef.current = bgMode;
  }, [bgMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await isVideoBackgroundSupported();
      if (!cancelled) setBgSupported(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (token) publicTokenRef.current = token;
  }, [token]);

  const requestCallEnd = useCallback(async (opts = {}) => {
    const tkn = publicTokenRef.current || token;
    if (!tkn || !company || endRequestedRef.current) return;
    endRequestedRef.current = true;
    try {
      const endedByRole =
        opts.endedByRole === "agent" || opts.endedByRole === "guest"
          ? opts.endedByRole
          : undefined;
      await postPublicVideoEnd(tkn, company, {
        ...(endedByRole ? { endedByRole, role: endedByRole } : {}),
      });
    } catch (e) {
      endRequestedRef.current = false;
      console.warn("[PublicVideo] end:", e?.message || e);
    }
  }, [token, company]);

  const flushTranscript = useCallback(
    async (finalize = false) => {
      const tkn = publicTokenRef.current || token;
      if (!tkn || !company) return;
      if (transcriptFlushTimerRef.current) {
        clearTimeout(transcriptFlushTimerRef.current);
        transcriptFlushTimerRef.current = null;
      }
      const chunks = transcriptBufferRef.current.splice(0);
      if (!chunks.length && !finalize) return;
      try {
        await postPublicVideoTranscript(tkn, company, { chunks, finalize });
      } catch (e) {
        if (chunks.length) transcriptBufferRef.current.unshift(...chunks);
        console.warn("[PublicVideo] transcript flush:", e?.message || e);
      }
    },
    [token, company]
  );

  const queueTranscriptChunk = useCallback(
    (chunk) => {
      if (!chunk?.text) return;
      transcriptBufferRef.current.push(chunk);
      if (transcriptFlushTimerRef.current) clearTimeout(transcriptFlushTimerRef.current);
      transcriptFlushTimerRef.current = setTimeout(() => {
        flushTranscript(false);
      }, 4000);
    },
    [flushTranscript]
  );

  const requestLiveCaptions = useCallback(
    async (nextOn) => {
      setCaptionsOn(nextOn);
      if (nextOn && !liveCaption) setCaptionHint("Aguardando legendas…");
      if (!nextOn) setCaptionHint("");
      const tkn = publicTokenRef.current || token;
      if (!tkn || !company) return;
      try {
        const res = await postPublicVideoCaptions(tkn, company, { enabled: nextOn });
        if (nextOn && res?.reason === "context_disabled") {
          setCaptionsOn(false);
          setCaptionHint("Legendas não ficam ligadas no escritório virtual.");
        }
      } catch (e) {
        console.warn("[PublicVideo] captions:", e?.message || e);
        if (nextOn) setCaptionHint("Não foi possível ligar as legendas agora.");
      }
    },
    [company, liveCaption, token]
  );

  const rememberChatSeenKeys = useCallback((messages) => {
    const seen = new Set();
    (messages || []).forEach((m) => {
      const k = chatMessageKey(m);
      if (k) seen.add(k);
      if (m?.id != null) seen.add(`i:${m.id}`);
      if (m?.clientId) seen.add(`c:${m.clientId}`);
    });
    chatSeenKeysRef.current = seen;
  }, []);

  const upsertChatMessage = useCallback((raw, { fromRemote = false } = {}) => {
    const msg = normalizeChatMessage(raw);
    if (!msg) return;
    const key = chatMessageKey(msg);
    if (!key) return;

    let alreadyKnown =
      chatSeenKeysRef.current.has(key) ||
      (msg.id != null && chatSeenKeysRef.current.has(`i:${msg.id}`)) ||
      (msg.clientId && chatSeenKeysRef.current.has(`c:${msg.clientId}`));

    setChatMessages((prev) => {
      alreadyKnown = alreadyKnown || prev.some((row) => videoChatMessagesMatch(row, msg));
      const next = mergeVideoChatMessageLists(prev, [msg]);
      rememberChatSeenKeys(next);
      return next;
    });

    const fromOther =
      Boolean(msg.senderIdentity) && msg.senderIdentity !== localIdentityRef.current;
    if (!alreadyKnown && fromRemote && fromOther) {
      if (canPlayVoChatSound({ unlocked: isVoFloorAudioUnlocked() })) {
        const mentioned = isChatMentionForMe(msg, {
          identity: localIdentityRef.current,
          userId: resolveAgentUserId(),
        });
        playVoFloorSound(mentioned ? "chat_mention" : "chat");
      }
    }
  }, [rememberChatSeenKeys, resolveAgentUserId]);

  const applyChatReactionToMessage = useCallback((messageId, updater) => {
    const mid = Number(messageId);
    if (!Number.isFinite(mid) || mid <= 0) return;
    setChatMessages((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (Number(row.id) !== mid) return row;
        changed = true;
        const patch = typeof updater === "function" ? updater(row) : updater;
        return patch ? { ...row, ...patch } : row;
      });
      return changed ? next : prev;
    });
  }, []);

  const applyChatReactionFromRemote = useCallback(
    (data) => {
      const messageId = Number(data?.messageId);
      if (!Number.isFinite(messageId) || messageId <= 0) return;
      const identity = String(data?.senderIdentity || data?.identity || data?.from || "").trim();
      const mine = identity && identity === String(localIdentityRef.current || "");
      if (mine) return;
      const reactions = normalizeChatReactions(data?.reactions);
      if (reactions) {
        applyChatReactionToMessage(messageId, { reactions });
        return;
      }
      const emoji = String(data?.emoji || "").trim();
      if (!identity || !emoji) return;
      applyChatReactionToMessage(messageId, (row) => ({
        reactions: toggleVideoChatReactions(row.reactions, {
          identity,
          senderName: data.senderName || data.fromName || "",
          emoji,
        }),
      }));
    },
    [applyChatReactionToMessage]
  );

  const handleIncomingChatPayload = useCallback(
    (payload) => {
      let data = payload;
      if (typeof payload === "string") {
        try {
          data = JSON.parse(payload);
        } catch {
          return;
        }
      }
      if (!data || typeof data !== "object") return;
      const signalId = String(data.id || "").trim();
      if (signalId) {
        if (voSignalSeenRef.current.has(signalId)) return;
        voSignalSeenRef.current.add(signalId);
        if (voSignalSeenRef.current.size > 80) {
          const first = voSignalSeenRef.current.values().next().value;
          voSignalSeenRef.current.delete(first);
        }
      }
      if (data.type === "vo-twilio-wake") {
        voWakeUntilRef.current = Date.now() + 20000;
        return;
      }
      if (data.type === "hello" || data.type === "media_state") {
        const changedName = rememberParticipantName(
          data.identity,
          data.displayName || data.senderName
        );
        const changedAvatar = rememberParticipantAvatar(data.identity, data.avatarUrl);
        const changedCargo = rememberParticipantCargo(data.identity, data.cargo || data.userCargo);
        const changedMedia = rememberParticipantMedia(data.identity, {
          camOff: data.camOff,
          muted: data.muted,
          speakerMuted: data.speakerMuted,
        });
        if (changedName || changedAvatar || changedCargo || changedMedia) applyRemoteTiles();
        if (!data.camOff || !data.muted) setRemoteWantsMedia(true);
        if (data.type === "hello") return;
        if (data.type === "media_state") return;
      }
      if (data.type === "vo-reaction") {
        const mine =
          data.from &&
          String(data.from) === String(localIdentityRef.current || "");
        if (!mine && data.emoji) {
          pushVoReaction(data.emoji, data.fromName, data.from);
        }
        return;
      }
      if (data.type === "vo-arrived") {
        const mine =
          data.identity &&
          String(data.identity) === String(localIdentityRef.current || "");
        if (!mine) {
          rememberParticipantName(data.identity, data.displayName);
          pushVoArrival(data.displayName, data.identity);
          publishHelloOnDataTrack();
        }
        return;
      }
      if (data.type === "vo-prompt-answer") {
        const identity = String(data.from || data.identity || "").trim();
        const answer = String(data.answer || "").trim();
        const name = voFloorFirstName(data.fromName || data.displayName || "Colega");
        if (!identity || !answer) return;
        setVoPromptAnswers((prev) => {
          const next = prev.filter((item) => item.identity !== identity);
          next.push({ identity, name, answer });
          return next.slice(-12);
        });
        playVoFloorSound("prompt");
        return;
      }
      if (data.type === "vo-birthday-wish") {
        const mine =
          data.fromUserId &&
          Number(data.fromUserId) === Number(resolveAgentUserId());
        if (!mine) {
          playVoFloorSound("react_party");
          pushVoReaction(birthdayGiftById(data.gift).emoji, data.fromName, data.from);
          if (data.message) {
            pushVoHouseBanner(
              `${voFloorFirstName(data.fromName || "Colega")}: ${data.message}`
            );
          }
          setVoBirthdayWishes((prev) => {
            const id = data.id || nextVoSignalId();
            if (prev.some((item) => String(item.id) === String(id))) return prev;
            return [
              {
                id,
                toUserId: data.toUserId,
                fromUserId: data.fromUserId,
                fromName: data.fromName,
                gift: data.gift,
                message: data.message || "",
              },
              ...prev,
            ].slice(0, 40);
          });
        }
        return;
      }
      if (data.type === "vo-kudos-react") {
        const mine =
          data.fromUserId &&
          Number(data.fromUserId) === Number(resolveAgentUserId());
        if (!mine && data.emoji) {
          pushVoReaction(data.emoji, data.fromName, data.from);
          if (data.toName) pushVoHouseBanner(cheerCopy(data.fromName, data.toName, data.emoji));
          setVoHighlightTarget((prev) =>
            prev &&
            (!data.kind || prev.kind === data.kind) &&
            Number(prev.userId) === Number(data.toUserId)
              ? appendHighlightReaction(prev, {
                  emoji: data.emoji,
                  fromUserId: data.fromUserId,
                  fromName: data.fromName,
                })
              : prev
          );
          void floorHighlightsRefetchRef.current?.();
        }
        return;
      }
      if (data.type === "vo-spark-echo") {
        const mine =
          data.from &&
          String(data.from) === String(localIdentityRef.current || "");
        if (!mine) {
          playVoFloorSound("spark");
          pushVoReaction("✨", data.fromName, data.from);
          pushVoHouseBanner(sparkEchoCopy(data.fromName));
        }
        return;
      }
      if (data.type === "vo-kudos-speak") {
        const mine =
          data.fromUserId &&
          Number(data.fromUserId) === Number(resolveAgentUserId());
        if (!mine && data.toName) {
          pushVoHouseBanner(speakCopy(data.fromName, data.toName));
        }
        return;
      }
      if (data.type === "caption" || data.type === "transcription") {
        const text = String(data.text || data.transcription || data.body || "").trim();
        if (!text) return;
        const who = resolveCaptionSpeakerLabel({
          identity: data.identity || data.senderIdentity,
          participantSid: data.participantSid || data.participant,
          displayName: data.displayName || data.senderName,
          room: roomRef.current,
          labelForIdentity,
        });
        setLiveCaption(who ? `${who}: ${text}` : text);
        setCaptionHint("");
        if (liveCaptionClearRef.current) clearTimeout(liveCaptionClearRef.current);
        liveCaptionClearRef.current = setTimeout(() => setLiveCaption(""), 8000);
        return;
      }
      if (data.type === "chat_reaction") {
        applyChatReactionFromRemote(data);
        return;
      }
      if (data.type !== "chat_message") return;
      rememberParticipantName(data.senderIdentity, data.senderName);
      upsertChatMessage(data, { fromRemote: true });
    },
    [
      upsertChatMessage,
      applyChatReactionFromRemote,
      rememberParticipantName,
      rememberParticipantAvatar,
      rememberParticipantCargo,
      rememberParticipantMedia,
      applyRemoteTiles,
      labelForIdentity,
      pushVoReaction,
      pushVoArrival,
      pushVoHouseBanner,
      publishHelloOnDataTrack,
    ]
  );

  const handleIncomingChatPayloadRef = useRef(null);
  handleIncomingChatPayloadRef.current = handleIncomingChatPayload;

  const boundDataTracksRef = useRef(new WeakSet());

  const bindRemoteDataTrack = useCallback((track) => {
    if (!track || track.kind !== "data") return;
    try {
      if (boundDataTracksRef.current.has(track)) return;
      boundDataTracksRef.current.add(track);
    } catch {
      /* WeakSet unsupported edge — still bind once via flag */
      if (track.__xproChatBound) return;
      track.__xproChatBound = true;
    }
    track.on("message", (data) => {
      handleIncomingChatPayloadRef.current?.(data);
    });
    publishHelloOnDataTrack();
  }, [publishHelloOnDataTrack]);

  const bindParticipantChatTracks = useCallback(
    (participant) => {
      if (!participant) return;
      participant.tracks?.forEach?.((pub) => {
        if (pub?.kind === "data" && pub.isSubscribed && pub.track) {
          bindRemoteDataTrack(pub.track);
        }
      });
    },
    [bindRemoteDataTrack]
  );

  const loadChatHistory = useCallback(async (opts = {}) => {
    const tkn = publicTokenRef.current || token;
    if (!tkn || !company) return;
    const silent = Boolean(opts?.silent);
    if (!silent) setChatLoading(true);
    try {
      const res = await fetchPublicVideoMessages(tkn, company);
      const list = Array.isArray(res?.messages) ? res.messages : [];
      const normalized = list.map(normalizeChatMessage).filter(Boolean);
      // Merge (não replace): evita apagar mensagens DataTrack que chegaram durante o GET.
      setChatMessages((prev) => {
        const out = mergeVideoChatMessageLists(prev, normalized);
        rememberChatSeenKeys(out);
        const lastReadKey = videoChatLastReadStorageKey({
          token: tkn,
          identity: localIdentityRef.current,
        });
        const storedRead = readVideoChatLastRead(lastReadKey);
        if (!storedRead) {
          const maxId = maxVideoChatMessageId(out);
          if (maxId > 0) {
            writeVideoChatLastRead(lastReadKey, maxId);
            setChatLastReadId(maxId);
          }
        } else {
          setChatLastReadId((prev) => (storedRead > prev ? storedRead : prev));
        }
        return out;
      });
    } catch (e) {
      console.warn("[PublicVideo] chat history:", e?.message || e);
    } finally {
      setChatLoading(false);
    }
  }, [token, company, rememberChatSeenKeys]);

  const publishChatOnDataTrack = useCallback((message, clientId) => {
    if (!message) return;
    const payload = {
      type: "chat_message",
      id: clientId || message.clientId || (message.id != null ? `chat_${message.id}` : nextVoSignalId()),
      messageId: message.id,
      clientId: clientId || message.clientId || null,
      body: message.body,
      senderIdentity: message.senderIdentity,
      senderName: message.senderName,
      senderRole: message.senderRole,
      createdAt: message.createdAt,
      mentions: Array.isArray(message.mentions) ? message.mentions : [],
      mentionAll: Boolean(message.mentionAll),
      replyToId: message.replyToId || null,
      replyPreview: message.replyPreview || null,
      messageType: message.messageType || "text",
      mediaUrl: message.mediaUrl || null,
      fileName: message.fileName || null,
    };
    const track = dataTrackRef.current;
    if (track) {
      try {
        track.send(JSON.stringify(payload));
      } catch (e) {
        console.warn("[PublicVideo] chat DataTrack send:", e?.message || e);
      }
    }
    const voId = Number(virtualOfficeRoomIdRef.current) || 0;
    if (isVoFloorForHuddlesRef.current && voId > 0) {
      void signalVirtualOfficeFloor(voId, payload).catch((e) => {
        console.warn("[PublicVideo] chat floor signal:", e?.message || e);
      });
    }
  }, []);

  const publishChatReactionOnDataTrack = useCallback((message, { identity, senderName, emoji } = {}) => {
    const messageId = Number(message?.id);
    if (!Number.isFinite(messageId) || messageId <= 0 || !emoji) return;
    const payload = {
      type: "chat_reaction",
      id: nextVoSignalId(),
      messageId,
      emoji,
      senderIdentity: identity || message.senderIdentity,
      senderName: senderName || displayNameRef.current || "Participante",
      reactions: Array.isArray(message.reactions) ? message.reactions : [],
    };
    const track = dataTrackRef.current;
    if (track) {
      try {
        track.send(JSON.stringify(payload));
      } catch (e) {
        console.warn("[PublicVideo] chat reaction DataTrack send:", e?.message || e);
      }
    }
    const voId = Number(virtualOfficeRoomIdRef.current) || 0;
    if (isVoFloorForHuddlesRef.current && voId > 0) {
      void signalVirtualOfficeFloor(voId, payload).catch((e) => {
        console.warn("[PublicVideo] chat reaction floor signal:", e?.message || e);
      });
    }
  }, []);

  const sendChatMessage = useCallback(async (opts = {}) => {
    unlockVoFloorAudio();
    const rawBody = String(opts?.body ?? chatDraft ?? "").trim();
    const files = Array.isArray(opts?.files) ? opts.files.filter(Boolean) : [];
    if (!rawBody && !files.length) return;
    const encoded = encodeChatMessageBody(rawBody, {
      people: chatMentionPeopleRef.current || [],
      picked: Array.isArray(opts?.mentions) ? opts.mentions : [],
    });
    const body = encoded.body;
    const tkn = publicTokenRef.current || token;
    const identity =
      localIdentityRef.current ||
      roomRef.current?.localParticipant?.identity ||
      "";
    if (!tkn || !company || !identity) return;

    const replyToId = Number(opts?.replyToId);
    const replyPreview =
      opts?.replyPreview && typeof opts.replyPreview === "object"
        ? {
            id: Number(opts.replyPreview.id || replyToId) || null,
            senderName: String(opts.replyPreview.senderName || ""),
            body: String(opts.replyPreview.body || ""),
            messageType: opts.replyPreview.messageType || "text",
            mediaUrl: opts.replyPreview.mediaUrl || null,
            fileName: opts.replyPreview.fileName || null,
          }
        : null;
    const hasReply = Number.isFinite(replyToId) && replyToId > 0;

    const basePayload = {
      senderIdentity: identity,
      senderName: displayNameRef.current || "Participante",
      senderRole: joinRole,
      mentions: encoded.mentions,
      mentionAll: encoded.mentionAll,
      ...(hasReply ? { replyToId } : {}),
    };

    const items = [];
    if (files.length) {
      files.forEach((file, index) => {
        items.push({
          file,
          body: index === 0 ? body : "",
          mentions: index === 0 ? encoded.mentions : [],
          mentionAll: index === 0 ? encoded.mentionAll : false,
        });
      });
    } else {
      items.push({
        file: null,
        body,
        mentions: encoded.mentions,
        mentionAll: encoded.mentionAll,
      });
    }

    setChatDraft("");
    setChatSending(true);

    const failedClientIds = [];
    try {
      for (const item of items) {
        const clientId = newChatClientId();
        let media = { messageType: "text", mediaUrl: "", fileName: "" };
        let localPreviewUrl = "";
        if (item.file) {
          media = {
            messageType: videoChatMediaTypeFromFile(item.file),
            mediaUrl: "",
            fileName: item.file.name || "",
          };
          try {
            localPreviewUrl = URL.createObjectURL(item.file);
            media.mediaUrl = localPreviewUrl;
          } catch {
            /* ignore */
          }
        }
        const optimistic = {
          clientId,
          id: null,
          body: item.body || media.fileName || "",
          messageType: media.messageType,
          mediaUrl: media.mediaUrl || null,
          fileName: media.fileName || null,
          senderIdentity: identity,
          senderName: displayNameRef.current || "Participante",
          senderRole: joinRole,
          createdAt: new Date().toISOString(),
          pending: true,
          mentions: item.mentions,
          mentionAll: item.mentionAll,
          replyToId: hasReply ? replyToId : null,
          replyPreview: hasReply ? replyPreview : null,
          reactions: [],
        };
        upsertChatMessage(optimistic);
        try {
          if (item.file) {
            const uploaded = await uploadPublicVideoFile(tkn, company, item.file);
            media.mediaUrl = uploaded?.url || "";
            if (!media.mediaUrl) throw new Error("Falha ao enviar mídia.");
          }
          const res = await postPublicVideoMessage(tkn, company, {
            ...basePayload,
            body: item.body,
            mentions: item.mentions,
            mentionAll: item.mentionAll,
            ...(media.mediaUrl
              ? {
                  messageType: media.messageType,
                  mediaUrl: media.mediaUrl,
                  fileName: media.fileName,
                }
              : {}),
          });
          const saved = res?.message || res;
          const merged = normalizeChatMessage({
            ...saved,
            clientId,
            pending: false,
            mentions: item.mentions,
            mentionAll: item.mentionAll,
            replyToId: saved?.replyToId ?? (hasReply ? replyToId : null),
            replyPreview: saved?.replyPreview || (hasReply ? replyPreview : null),
            reactions: Array.isArray(saved?.reactions) ? saved.reactions : [],
          });
          if (merged) {
            upsertChatMessage(merged);
            publishChatOnDataTrack(merged, clientId);
          }
        } catch (e) {
          console.warn("[PublicVideo] chat send:", e?.message || e);
          failedClientIds.push(clientId);
          setChatMessages((prev) => prev.filter((m) => m.clientId !== clientId));
        } finally {
          if (localPreviewUrl) {
            try {
              URL.revokeObjectURL(localPreviewUrl);
            } catch {
              /* ignore */
            }
          }
        }
      }
      if (failedClientIds.length === items.length && rawBody) {
        setChatDraft(rawBody);
      }
    } finally {
      setChatSending(false);
    }
  }, [
    chatDraft,
    token,
    company,
    joinRole,
    upsertChatMessage,
    publishChatOnDataTrack,
  ]);

  const reactToChatMessage = useCallback(
    async (message, emoji) => {
      const messageId = Number(message?.id);
      const nextEmoji = String(emoji || "").trim();
      const tkn = publicTokenRef.current || token;
      const identity =
        localIdentityRef.current ||
        roomRef.current?.localParticipant?.identity ||
        "";
      if (!tkn || !company || !identity || !Number.isFinite(messageId) || messageId <= 0 || !nextEmoji) {
        return;
      }
      const senderName = displayNameRef.current || "Participante";
      const previous = Array.isArray(message.reactions) ? message.reactions : [];
      const optimistic = toggleVideoChatReactions(previous, {
        identity,
        senderName,
        emoji: nextEmoji,
      });
      applyChatReactionToMessage(messageId, { reactions: optimistic });
      try {
        const res = await postPublicVideoReact(tkn, company, messageId, {
          emoji: nextEmoji,
          senderIdentity: identity,
          senderName,
        });
        const saved = res?.message || res;
        const reactions = normalizeChatReactions(saved?.reactions) || optimistic;
        applyChatReactionToMessage(messageId, { reactions });
        publishChatReactionOnDataTrack(
          { id: messageId, reactions },
          { identity, senderName, emoji: nextEmoji }
        );
      } catch (e) {
        console.warn("[PublicVideo] chat react:", e?.message || e);
        applyChatReactionToMessage(messageId, { reactions: previous });
      }
    },
    [token, company, applyChatReactionToMessage, publishChatReactionOnDataTrack]
  );

  const openChatPanel = useCallback(() => {
    unlockVoFloorAudio();
    setChatOpen(true);
    markChatReadUpTo(chatMessages);
    void loadChatHistory({ silent: true });
  }, [chatMessages, loadChatHistory, markChatReadUpTo]);

  const closeChatPanel = useCallback(() => {
    setChatOpen(false);
  }, []);

  const primary = theme.palette.primary.main || "#1976d2";
  const logoSrc =
    brand?.assets?.logo?.light || brand?.assets?.logo?.dark || "/brands/xpro/logo.png";
  /** Logo clara para o fundo escuro padrão da câmera. */
  const bgLogoSrc =
    brand?.assets?.logo?.dark || brand?.assets?.logo?.light || "/brands/xpro/logowhite.png";
  const brandName = brand?.name || "XPro";
  const roomLabel = roomLabelForContext(
    isVirtualOfficeContext && voFloorName ? voFloorName : channelName,
    {
    callContext:
      isVirtualOfficeContext || isVirtualOfficeCallContext(callContext)
        ? CALL_CONTEXT_VIRTUAL_OFFICE
        : callContext,
    fromLobby: fromLobbyEntry || mode === "lobby",
  });
  roomLabelRef.current = roomLabel;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const guestShareUrl = useMemo(() => buildGuestShareUrl(shareUrl), [shareUrl]);
  const teamCall = isTeamCallContext(callContext);
  const isGuestWaiter = joinRole !== "agent" && !teamCall;

  const themePreset = useMemo(() => {
    const themeLabel =
      themeOptions.find((o) => o.value === currentTheme)?.label || currentTheme || "";
    return buildThemeVideoBackgroundPreset(theme, {
      themeKey: currentTheme || "",
      themeLabel,
    });
  }, [theme, currentTheme, themeOptions]);

  useEffect(() => {
    bgLogoSrcRef.current = bgLogoSrc;
  }, [bgLogoSrc]);

  useEffect(() => {
    themePresetRef.current = themePreset;
  }, [themePreset]);

  const cleanupLocalScreenShare = useCallback(() => {
    const { video, audio, endedHandler } = screenLocalRef.current;
    if (video && endedHandler) {
      try {
        video.mediaStreamTrack?.removeEventListener?.("ended", endedHandler);
      } catch {
        /* ignore */
      }
      try {
        video.removeListener?.("stopped", endedHandler);
      } catch {
        /* ignore */
      }
    }
    const room = roomRef.current;
    if (room?.localParticipant) {
      for (const t of [video, audio]) {
        if (!t) continue;
        try {
          room.localParticipant.unpublishTrack(t);
        } catch {
          /* ignore */
        }
      }
    }
    stopLocalScreenShareTracks({ videoTrack: video, audioTrack: audio });
    screenLocalRef.current = { video: null, audio: null, endedHandler: null };
  }, []);

  const syncPresentationState = useCallback(() => {
    setRemotePresenting(remoteScreensRef.current.size > 0);
    if (screenLocalRef.current.video) {
      setPresenterLabel("Você está compartilhando a tela");
      return;
    }
    const first = remoteScreensRef.current.values().next().value;
    if (first?.track) {
      const name = presenterLabelFromIdentity(
        first.participant?.identity,
        identityNamesRef.current.get(String(first.participant?.identity || "").trim())
      );
      setPresenterLabel(`${name} está compartilhando a tela`);
      return;
    }
    setPresenterLabel("");
  }, []);

  const attachFeaturedScreen = useCallback(() => {
    const box = presentationRef.current;
    if (!box) return;
    const featured =
      screenLocalRef.current.video ||
      remoteScreensRef.current.values().next().value?.track ||
      null;
    if (featured) {
      attachTrack(featured, box, { mirror: false, contain: true });
    } else {
      clearMediaContainer(box);
    }
  }, []);

  const registerRemoteScreen = useCallback(
    (participant, track) => {
      if (!track) return;
      const key = track.sid || `${participant?.sid || "p"}-screen`;
      remoteScreensRef.current.set(key, { track, participant });
      syncPresentationState();
    },
    [syncPresentationState]
  );

  const unregisterRemoteScreen = useCallback(
    (track, participant) => {
      for (const [key, value] of remoteScreensRef.current) {
        if (
          value.track === track ||
          (track?.sid && key === track.sid) ||
          (participant && value.participant?.sid === participant.sid && !track)
        ) {
          remoteScreensRef.current.delete(key);
        }
      }
      syncPresentationState();
    },
    [syncPresentationState]
  );

  const cleanupRoom = useCallback(() => {
    cleanupLocalScreenShare();
    remoteScreensRef.current.clear();
    setSharingScreen(false);
    setRemotePresenting(false);
    setPresenterLabel("");
    setScreenError("");
    clearMediaContainer(presentationRef.current);
    clearVideoBackground(localTracksRef.current);
    stopTracks(localTracksRef.current);
    localTracksRef.current = [];
    dataTrackRef.current = null;
    try {
      boundDataTracksRef.current = new WeakSet();
    } catch {
      /* ignore */
    }
    try {
      closeVideoCallDocumentPip();
    } catch {
      /* ignore */
    }
    setPipActive(false);
    if (roomRef.current) {
      try {
        roomRef.current.disconnect();
      } catch {
        /* ignore */
      }
      roomRef.current = null;
    }
    clearRemoteParticipantTiles(remoteContainersRef.current, remoteGridRef.current);
    setRemoteCount(0);
    remoteTileUserIdsRef.current = "";
    setRemoteTileUserIds([]);
    remoteTileIdentitiesRef.current = "";
    setRemoteTileIdentities([]);
    remoteCameraOnCountRef.current = 0;
    setRemoteCameraOnCount(0);
    voArrivedSentRef.current = false;
    setVoReactions([]);
    setVoArrivals([]);
    setVoHouseBanners([]);
    setDominantSid(null);
    setPinnedSid(null);
    setFloorPeek(null);
    setPeopleOpen(false);
    setMoreMenuAnchor(null);
    setChatMessages([]);
    setChatUnread(0);
    setChatDraft("");
    setLocalIdentity("");
    localIdentityRef.current = "";
    identityNamesRef.current = new Map();
    identityAvatarsRef.current = new Map();
    identityMediaRef.current = new Map();
    setPeerMediaByIdentity({});
    chatSeenKeysRef.current = new Set();
  }, [cleanupLocalScreenShare]);

  const cleanupPreview = useCallback(() => {
    clearVideoBackground(previewTracksRef.current);
    stopTracks(previewTracksRef.current);
    previewTracksRef.current = [];
    clearMediaContainer(previewVideoRef.current);
    setPreviewReady(false);
  }, []);

  const collectLocalVideoTracks = useCallback(() => {
    const out = [];
    const seen = new Set();
    for (const t of [...previewTracksRef.current, ...localTracksRef.current]) {
      if (t?.kind === "video" && !isScreenShareTrack(t) && !seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
    return out;
  }, []);

  const setCameraBackground = useCallback(
    async ({ mode, image = null, presetId = null, preset = null } = {}) => {
      const seq = ++bgApplySeqRef.current;
      setBgBusy(true);
      setBgError("");
      try {
        const tracks = collectLocalVideoTracks();
        if (!mode || mode === "none") {
          clearVideoBackground(tracks);
          if (seq !== bgApplySeqRef.current) return;
          bgImageRef.current = null;
          bgModeRef.current = "none";
          setBgMode("none");
          refreshLocalCameraPreview();
          return;
        }
        if (mode === "blur") {
          await applyVideoBackground({ mode: "blur", tracks, blurRadius: 15 });
          if (seq !== bgApplySeqRef.current) return;
          bgImageRef.current = null;
          bgModeRef.current = "blur";
          setBgMode("blur");
          refreshLocalCameraPreview();
          return;
        }

        let bgImage = image;
        const resolvedPreset =
          preset ||
          (presetId === THEME_VIDEO_BACKGROUND_ID
            ? themePresetRef.current
            : null) ||
          (presetId
            ? VIDEO_BACKGROUND_PRESETS.find((p) => p.id === presetId)
            : null);
        const dims = dimensionsFromVideoTracks(tracks);
        // Logo/tema: regenera no tamanho da câmera (Cover cortava a marca).
        if (
          !bgImage ||
          resolvedPreset?.kind === "logo" ||
          resolvedPreset?.kind === "theme"
        ) {
          if (resolvedPreset) {
            bgImage = await resolvePresetBackgroundImage(resolvedPreset, {
              logoSrc: bgLogoSrcRef.current,
              ...dims,
            });
          }
        }
        if (!bgImage) throw new Error("Imagem de fundo obrigatória.");

        await applyVideoBackground({
          mode: "image",
          tracks,
          backgroundImage: bgImage,
          fitType: backgroundFitForPreset(resolvedPreset),
        });
        if (seq !== bgApplySeqRef.current) return;
        bgImageRef.current = bgImage;
        const nextId = resolvedPreset?.id || presetId;
        const nextMode = nextId ? `preset:${nextId}` : "custom";
        bgModeRef.current = nextMode;
        setBgMode(nextMode);
        refreshLocalCameraPreview();
      } catch (e) {
        if (seq !== bgApplySeqRef.current) return;
        setBgError(
          e?.code === "video.background_unsupported"
            ? e.message
            : e?.message || "Não foi possível aplicar o fundo."
        );
      } finally {
        if (seq === bgApplySeqRef.current) setBgBusy(false);
      }
    },
    [collectLocalVideoTracks, refreshLocalCameraPreview]
  );

  const releaseOwnedCameraTracks = useCallback((tracks) => {
    const { camera, rest } = splitLocalCameraTracks(tracks, isScreenShareTrack);
    if (!camera.length) return rest;
    const room = roomRef.current;
    try {
      clearVideoBackground(camera);
    } catch {
      /* ignore */
    }
    for (const track of camera) {
      try {
        room?.localParticipant?.unpublishTrack(track);
      } catch {
        /* ignore */
      }
      try {
        track.stop();
      } catch {
        /* ignore */
      }
      detachTrackElements(track);
    }
    return rest;
  }, []);

  const applyCurrentBackgroundToTracks = useCallback(async (tracks) => {
    const mode = bgModeRef.current;
    if (!mode || mode === "none" || !tracks.length) return;
    if (mode === "blur") {
      await applyVideoBackground({ mode: "blur", tracks, blurRadius: 15 });
      return;
    }
    const presetId = String(mode).startsWith("preset:") ? String(mode).slice("preset:".length) : null;
    const preset =
      (presetId === THEME_VIDEO_BACKGROUND_ID ? themePresetRef.current : null) ||
      (presetId ? VIDEO_BACKGROUND_PRESETS.find((p) => p.id === presetId) : null);
    let backgroundImage = bgImageRef.current;
    if (preset && (preset.kind === "logo" || preset.kind === "theme" || !backgroundImage)) {
      backgroundImage = await resolvePresetBackgroundImage(preset, {
        logoSrc: bgLogoSrcRef.current,
        ...dimensionsFromVideoTracks(tracks),
      });
      bgImageRef.current = backgroundImage;
    }
    if (!backgroundImage) return;
    await applyVideoBackground({
      mode: "image",
      tracks,
      backgroundImage,
      fitType: backgroundFitForPreset(preset),
    });
  }, []);

  const setLocalCameraEnabled = useCallback(
    async (on) => {
      if (requireCameraRef.current || camBusyRef.current) return;
      const wantOn = Boolean(on);
      const hasCam = collectLocalVideoTracks().length > 0;
      if (wantOn === !camOffRef.current && wantOn === hasCam) return;
      camBusyRef.current = true;
      setCamBusy(true);
      try {
        if (!wantOn) {
          previewTracksRef.current = releaseOwnedCameraTracks(previewTracksRef.current);
          localTracksRef.current = releaseOwnedCameraTracks(localTracksRef.current);
          clearMediaContainer(previewVideoRef.current);
          clearMediaContainer(localPipRef.current);
          camOffRef.current = true;
          setCamOff(true);
          publishMediaState();
          refreshLocalCameraPreview();
          return;
        }
        const Video = await import("twilio-video");
        const track = await Video.createLocalVideoTrack(
          buildVideoConstraint(videoDeviceIdRef.current) || true
        );
        try {
          await applyCurrentBackgroundToTracks([track]);
        } catch (e) {
          console.warn("[PublicVideo] camera background on enable:", e?.message || e);
        }
        const room = roomRef.current;
        if (room?.localParticipant) {
          try {
            await room.localParticipant.publishTrack(track);
          } catch (e) {
            console.warn("[PublicVideo] publish camera:", e?.message || e);
          }
          localTracksRef.current = [
            ...splitLocalCameraTracks(localTracksRef.current, isScreenShareTrack).rest,
            track,
          ];
        } else if (phaseRef.current === "incall") {
          localTracksRef.current = [
            ...splitLocalCameraTracks(localTracksRef.current, isScreenShareTrack).rest,
            track,
          ];
          publishVoTwilioWake();
        } else {
          previewTracksRef.current = [
            ...splitLocalCameraTracks(previewTracksRef.current, isScreenShareTrack).rest,
            track,
          ];
          setPreviewReady(true);
        }
        camOffRef.current = false;
        setCamOff(false);
        publishMediaState();
        refreshLocalCameraPreview();
      } catch (e) {
        console.warn("[PublicVideo] toggle camera:", e?.message || e);
        setScreenError(
          e?.name === "NotAllowedError"
            ? "Permita o uso da câmera para ligá-la de novo."
            : e?.message || "Não foi possível ligar a câmera."
        );
      } finally {
        camBusyRef.current = false;
        setCamBusy(false);
      }
    },
    [
      applyCurrentBackgroundToTracks,
      collectLocalVideoTracks,
      publishMediaState,
      publishVoTwilioWake,
      refreshLocalCameraPreview,
      releaseOwnedCameraTracks,
    ]
  );

  const collectLocalAudioTracks = useCallback(() => {
    const out = [];
    const seen = new Set();
    for (const t of [...previewTracksRef.current, ...localTracksRef.current]) {
      if (t?.kind === "audio" && !seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
    return out;
  }, []);

  const releaseOwnedAudioTracks = useCallback((tracks) => {
    const { audio, rest } = splitLocalAudioTracks(tracks);
    if (!audio.length) return rest;
    const room = roomRef.current;
    for (const track of audio) {
      try {
        room?.localParticipant?.unpublishTrack(track);
      } catch {
        /* ignore */
      }
      try {
        track.stop();
      } catch {
        /* ignore */
      }
      detachTrackElements(track);
    }
    return rest;
  }, []);

  const setLocalMicEnabled = useCallback(
    async (on) => {
      if (micBusyRef.current) return;
      const wantOn = Boolean(on);
      const hasMic = collectLocalAudioTracks().length > 0;
      if (wantOn === !mutedRef.current && wantOn === hasMic) return;
      micBusyRef.current = true;
      setMicBusy(true);
      try {
        if (!wantOn) {
          previewTracksRef.current = releaseOwnedAudioTracks(previewTracksRef.current);
          localTracksRef.current = releaseOwnedAudioTracks(localTracksRef.current);
          mutedRef.current = true;
          setMuted(true);
          publishMediaState();
          return;
        }
        const Video = await import("twilio-video");
        const track = await Video.createLocalAudioTrack(
          buildAudioConstraint(audioDeviceIdRef.current) || true
        );
        const room = roomRef.current;
        if (room?.localParticipant) {
          try {
            await room.localParticipant.publishTrack(track);
          } catch (e) {
            console.warn("[PublicVideo] publish mic:", e?.message || e);
          }
          localTracksRef.current = [...splitLocalAudioTracks(localTracksRef.current).rest, track];
        } else if (phaseRef.current === "incall") {
          localTracksRef.current = [...splitLocalAudioTracks(localTracksRef.current).rest, track];
          publishVoTwilioWake();
        } else {
          previewTracksRef.current = [
            ...splitLocalAudioTracks(previewTracksRef.current).rest,
            track,
          ];
        }
        mutedRef.current = false;
        setMuted(false);
        publishMediaState();
      } catch (e) {
        console.warn("[PublicVideo] toggle mic:", e?.message || e);
        setScreenError(
          e?.name === "NotAllowedError"
            ? "Permita o uso do microfone para ligá-lo de novo."
            : e?.message || "Não foi possível ligar o microfone."
        );
      } finally {
        micBusyRef.current = false;
        setMicBusy(false);
      }
    },
    [collectLocalAudioTracks, publishMediaState, publishVoTwilioWake, releaseOwnedAudioTracks]
  );

  const announceAudioDevice = useCallback((deviceId) => {
    const id = String(deviceId || "").trim();
    if (!id) return;
    const label =
      audioDevicesRef.current.find((d) => d.deviceId === id)?.label ||
      "microfone do sistema";
    setMediaSnack(`Microfone alterado para ${label}`);
  }, []);

  const applyAudioDeviceId = useCallback((deviceId, { persist = true } = {}) => {
    const id = String(deviceId || "").trim();
    audioDeviceIdRef.current = id;
    setAudioDeviceId(id);
    if (persist && id) setPreferredAudioDeviceId(id);
  }, []);

  const replaceInCallAudioTrack = useCallback(
    async (deviceId, { announce = false } = {}) => {
      if (phaseRef.current !== "incall") return false;
      if (mutedRef.current) return false;
      if (switchingAudioRef.current || micBusyRef.current) return false;
      const id = String(deviceId || "").trim();
      const currentTracks = collectLocalAudioTracks();
      const currentId = localTrackDeviceId(currentTracks[0]);
      if (id && currentId && id === currentId) return false;
      switchingAudioRef.current = true;
      try {
        const constraint = buildAudioConstraint(id) || true;
        let restarted = false;
        for (const track of currentTracks) {
          if (typeof track.restart !== "function") continue;
          try {
            await track.restart(constraint);
            restarted = true;
            break;
          } catch {
            /* replace below */
          }
        }
        if (!restarted) {
          const Video = await import("twilio-video");
          const track = await Video.createLocalAudioTrack(constraint);
          const room = roomRef.current;
          previewTracksRef.current = releaseOwnedAudioTracks(previewTracksRef.current);
          const rest = releaseOwnedAudioTracks(localTracksRef.current);
          if (room?.localParticipant) {
            try {
              await room.localParticipant.publishTrack(track);
            } catch (e) {
              console.warn("[PublicVideo] publish switched mic:", e?.message || e);
            }
          }
          localTracksRef.current = [...rest, track];
        }
        applyAudioDeviceId(id);
        if (announce) announceAudioDevice(id);
        return true;
      } catch (e) {
        console.warn("[PublicVideo] switch mic:", e?.message || e);
        return false;
      } finally {
        switchingAudioRef.current = false;
      }
    },
    [announceAudioDevice, applyAudioDeviceId, collectLocalAudioTracks, releaseOwnedAudioTracks]
  );

  const recoverFromLostAudioDevice = useCallback(async () => {
    if (phaseRef.current !== "incall" || mutedRef.current) return;
    if (switchingAudioRef.current || micBusyRef.current) return;
    const { audioInputs } = await enumerateVideoCallDevices();
    audioDevicesRef.current = audioInputs;
    setAudioDevices(audioInputs);
    const next = resolveAudioDeviceAfterChange({
      previousIds: new Set([audioDeviceIdRef.current]),
      devices: audioInputs,
      currentId: audioDeviceIdRef.current,
      preferredId: getPreferredAudioDeviceId(),
      currentLost: true,
    });
    prevAudioDeviceIdsRef.current = new Set(audioInputs.map((d) => d.deviceId).filter(Boolean));
    if (!next) return;
    applyAudioDeviceId(next);
    await replaceInCallAudioTrack(next, { announce: true });
  }, [applyAudioDeviceId, replaceInCallAudioTrack]);

  const watchLocalAudioEnded = useCallback(() => {
    unbindAudioEndedRef.current?.();
    const unbinds = collectLocalAudioTracks().map((track) =>
      bindLocalMediaTrackEnded(track, () => {
        if (switchingAudioRef.current || mutedRef.current || micBusyRef.current) return;
        void recoverFromLostAudioDevice();
      })
    );
    unbindAudioEndedRef.current = () => {
      unbinds.forEach((fn) => {
        try {
          fn();
        } catch {
          /* ignore */
        }
      });
    };
  }, [collectLocalAudioTracks, recoverFromLostAudioDevice]);

  useEffect(() => {
    if (!muted) return;
    if (!collectLocalAudioTracks().length) return;
    void setLocalMicEnabled(false);
  }, [muted, collectLocalAudioTracks, setLocalMicEnabled]);

  // Se o usuário está com o fundo "tema" e mudar o tema do app, atualiza o fundo.
  useEffect(() => {
    if (bgMode !== `preset:${THEME_VIDEO_BACKGROUND_ID}`) return undefined;
    if (!previewReady && phase !== "incall") return undefined;
    let cancelled = false;
    (async () => {
      try {
        const tracks = collectLocalVideoTracks();
        if (!tracks.length || cancelled) return;
        const bgImage = await resolvePresetBackgroundImage(themePreset, {
          logoSrc: bgLogoSrcRef.current,
          ...dimensionsFromVideoTracks(tracks),
        });
        if (cancelled) return;
        bgImageRef.current = bgImage;
        await applyVideoBackground({
          mode: "image",
          tracks,
          backgroundImage: bgImage,
          fitType: backgroundFitForPreset(themePreset),
        });
        if (!cancelled) refreshLocalCameraPreview();
      } catch (e) {
        console.warn("[PublicVideo] theme background refresh:", e?.message || e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [themePreset, bgMode, previewReady, phase, collectLocalVideoTracks, refreshLocalCameraPreview]);

  useEffect(() => () => {
    cleanupPreview();
    cleanupRoom();
  }, [cleanupPreview, cleanupRoom]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!company) {
          const guide = classifyJoinVideoError("", {
            missingCompany: true,
            role: joinRole,
            callContext,
          });
          setErrorGuide(guide);
          setErrorMsg(guide.body);
          setErrorCanRetryJoin(false);
          setPhase("error");
          return;
        }
        if (mode === "lobby") {
          const meta = await fetchPublicVideoLobby(lobbyToken, company);
          if (cancelled) return;
          setChannelName(meta?.channelName || "");
          setRecordingEnabled(
            meta?.recordingEnabled !== false && meta?.recordingConsentRequired !== false
          );
          setRequireCamera(parseRequireCameraFlag(meta?.requireCamera));
          setFromLobbyEntry(true);
          setCallContext(CALL_CONTEXT_SUPPORT);
          const q = company ? `?company=${encodeURIComponent(company)}` : "";
          setLobbyReturnPath(`/video/lobby/${encodeURIComponent(lobbyToken)}${q}`);
          setPhase("name");
        } else {
          const meta = await fetchPublicVideoRoom(token, company);
          if (cancelled) return;
          const nextContext = resolveCallContext({
            apiContext: meta?.callContext,
            searchParam: contextFromQuery,
            teamSpaceId: meta?.teamSpaceId,
            fromLobby: false,
          });
          const metaCtx = String(meta?.callContext || "").toLowerCase();
          const isVoSide = metaCtx === "vo_private" || metaCtx === "vo_huddle";
          const forcedVo =
            contextFromQuery === "virtual_office" ||
            (Number(meta?.virtualOfficeRoomId) > 0 && !isVoSide)
              ? CALL_CONTEXT_VIRTUAL_OFFICE
              : nextContext;
          setCallContext(forcedVo);
          setRecordingEnabled(
            meta?.recordingEnabled !== false && meta?.recordingConsentRequired !== false
          );
          if (meta?.recordingVisibility) {
            setRecordingVisibility(String(meta.recordingVisibility));
          }
          if (Number(meta?.virtualOfficeRoomId) > 0) {
            setVirtualOfficeRoomId(Number(meta.virtualOfficeRoomId));
          } else if (Number(meta?.parentVirtualOfficeRoomId) > 0) {
            setVirtualOfficeRoomId(Number(meta.parentVirtualOfficeRoomId));
          }
          const officeName = String(meta?.virtualOfficeRoomName || "").trim();
          if (officeName) setVoFloorName(officeName);
          if (Number(meta?.id) > 0) {
            setTwilioDbRoomId(Number(meta.id));
          }
          setRequireCamera(
            isVirtualOfficeCallContext(forcedVo) || isVoSideRoomCallContext(forcedVo)
              ? false
              : parseRequireCameraFlag(meta?.requireCamera)
          );
          if (meta?.status === "ended") {
            const reason = String(meta?.endReason || "").toLowerCase();
            if (
              meta?.missedNoAgent ||
              reason === "missed" ||
              reason === "no_agent" ||
              reason === "missed_no_agent"
            ) {
              setEndCause(meta?.missedNoAgent ? "missedNoAgent" : reason || "missed");
            } else {
              setEndCause("already_ended");
            }
            setPhase("ended");
            return;
          }
          setChannelName(meta?.channelName || "");
          setIsPrivateRoom(Boolean(meta?.isPrivate));
          setLeadId(
            Number.isFinite(Number(meta?.leadId)) && Number(meta.leadId) > 0
              ? Number(meta.leadId)
              : null
          );
          setRoomParticipants(
            Array.isArray(meta?.participantsInCall) ? meta.participantsInCall : []
          );
          setPhase("name");
        }
      } catch (e) {
        if (cancelled) return;
        const errData = e?.response?.data?.data || e?.response?.data || {};
        const status = e?.response?.status;
        if (status === 410 || errData?.status === "ended") {
          const reason = String(errData?.endReason || "").toLowerCase();
          if (
            errData?.missedNoAgent ||
            reason === "missed" ||
            reason === "no_agent" ||
            reason === "missed_no_agent"
          ) {
            setEndCause(errData?.missedNoAgent ? "missedNoAgent" : reason || "missed");
          } else {
            setEndCause("already_ended");
          }
          if (errData?.callContext) {
            setCallContext(
              resolveCallContext({
                apiContext: errData.callContext,
                searchParam: contextFromQuery,
                fromLobby: mode === "lobby",
              })
            );
          }
          setPhase("ended");
          return;
        }
        const errContext = resolveCallContext({
          apiContext: errData?.callContext,
          searchParam: contextFromQuery,
          fromLobby: mode === "lobby",
        });
        setCallContext(errContext);
        const guide = buildJoinErrorGuide(e, { role: joinRole, callContext: errContext });
        setErrorGuide(guide);
        setErrorMsg(guide.body);
        setErrorCanRetryJoin(Boolean(guide.canRetry));
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, token, lobbyToken, company, joinRole, contextFromQuery]);

  // Atualiza quem já está na sala (pré-join estilo Meet + espera in-call)
  useEffect(() => {
    if ((phase !== "name" && phase !== "incall") || mode === "lobby" || !token || !company) {
      return undefined;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        // Poll barato: só meta do webhook (sem Twilio REST a cada ciclo).
        const meta = await fetchPublicVideoRoom(token, company, { live: false });
        if (cancelled) return;
        if (meta?.status === "ended") {
          if (isVoFloorForHuddlesRef.current && voIdleHoldRef.current) {
            return;
          }
          const reason = String(meta?.endReason || "").toLowerCase();
          const missed =
            meta?.missedNoAgent ||
            reason === "missed" ||
            reason === "no_agent" ||
            reason === "missed_no_agent";
          if (phase === "name") {
            setEndCause(missed ? (meta?.missedNoAgent ? "missedNoAgent" : reason || "missed") : "already_ended");
            setPhase("ended");
          } else if (phase === "incall" && !leftBySelfRef.current) {
            leftBySelfRef.current = false;
            setEndCause(missed ? (meta?.missedNoAgent ? "missedNoAgent" : reason || "missed") : "remote");
            cleanupRoom();
            setPhase("ended");
          }
          return;
        }
        setRoomParticipants(
          Array.isArray(meta?.participantsInCall) ? meta.participantsInCall : []
        );
        if (meta?.requireCamera != null) {
          setRequireCamera(
            isVirtualOfficeContext || isVirtualOfficeCallContext(callContext)
              ? false
              : parseRequireCameraFlag(meta.requireCamera)
          );
        }
        if (meta?.leadId != null) {
          const id = Number(meta.leadId);
          if (Number.isFinite(id) && id > 0) setLeadId(id);
        }
      } catch (e) {
        if (cancelled) return;
        const errData = e?.response?.data?.data || e?.response?.data || {};
        if (e?.response?.status === 410 || errData?.status === "ended") {
          if (isVoFloorForHuddlesRef.current && voIdleHoldRef.current) {
            return;
          }
          const reason = String(errData?.endReason || "").toLowerCase();
          const missed =
            errData?.missedNoAgent ||
            reason === "missed" ||
            reason === "no_agent" ||
            reason === "missed_no_agent";
          if (phase === "name" || phase === "incall") {
            setEndCause(
              missed
                ? errData?.missedNoAgent
                  ? "missedNoAgent"
                  : reason || "missed"
                : phase === "incall"
                  ? "remote"
                  : "already_ended"
            );
            if (phase === "incall") cleanupRoom();
            setPhase("ended");
          }
          return;
        }
        console.warn("[PublicVideo] participants poll:", e?.message || e);
      }
    };
    refresh();
    const id = setInterval(refresh, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [phase, mode, token, company, cleanupRoom]);

  // Enumera devices no pré-join e na chamada (Meet: plug/unplug troca o mic na hora)
  useEffect(() => {
    if (phase !== "name" && phase !== "incall") return undefined;
    let cancelled = false;
    const refresh = async ({ fromDeviceChange = false } = {}) => {
      const { audioInputs, videoInputs } = await enumerateVideoCallDevices();
      if (cancelled) return;
      audioDevicesRef.current = audioInputs;
      setAudioDevices(audioInputs);
      setVideoDevices(videoInputs);
      const nextAudio = resolveAudioDeviceAfterChange({
        previousIds: prevAudioDeviceIdsRef.current,
        devices: audioInputs,
        currentId: audioDeviceIdRef.current,
        preferredId: getPreferredAudioDeviceId(),
      });
      const nextVideo = resolveVideoDeviceAfterChange({
        devices: videoInputs,
        currentId: videoDeviceIdRef.current,
        preferredId: getPreferredVideoDeviceId(),
      });
      prevAudioDeviceIdsRef.current = new Set(
        audioInputs.map((d) => d.deviceId).filter(Boolean)
      );
      const audioChanged = Boolean(nextAudio && nextAudio !== audioDeviceIdRef.current);
      applyAudioDeviceId(nextAudio);
      setVideoDeviceId(nextVideo || "");
      videoDeviceIdRef.current = nextVideo || "";
      if (nextVideo) setPreferredVideoDeviceId(nextVideo);
      if (phaseRef.current === "incall" && audioChanged) {
        await replaceInCallAudioTrack(nextAudio, { announce: fromDeviceChange });
      }
    };
    refresh();
    const onChange = () => {
      void refresh({ fromDeviceChange: true });
    };
    try {
      navigator.mediaDevices?.addEventListener?.("devicechange", onChange);
    } catch {
      /* ignore */
    }
    return () => {
      cancelled = true;
      try {
        navigator.mediaDevices?.removeEventListener?.("devicechange", onChange);
      } catch {
        /* ignore */
      }
    };
  }, [applyAudioDeviceId, phase, replaceInCallAudioTrack]);

  useEffect(() => {
    if (phase !== "incall") {
      unbindAudioEndedRef.current?.();
      return undefined;
    }
    watchLocalAudioEnded();
    return () => unbindAudioEndedRef.current?.();
  }, [audioDeviceId, muted, phase, watchLocalAudioEnded]);

  // Preview local no pré-join (estilo Meet)
  useEffect(() => {
    if (phase !== "name") return undefined;
    let cancelled = false;
    (async () => {
      setPreviewError("");
      try {
        const Video = await import("twilio-video");
        const tracks = await Video.createLocalTracks({
          audio: mutedRef.current ? false : buildAudioConstraint(audioDeviceIdRef.current),
          video:
            requireCameraRef.current || !camOffRef.current
              ? buildVideoConstraint(videoDeviceIdRef.current)
              : false,
        });
        if (cancelled) {
          stopTracks(tracks);
          return;
        }
        previewTracksRef.current = tracks;
        setPreviewReady(true);
        // Sempre inicia com o fundo padrão (logo XPro), salvo se o usuário já escolheu outro.
        const mode = bgModeRef.current || DEFAULT_VIDEO_BACKGROUND_MODE;
        if (mode && mode !== "none") {
          try {
            let backgroundImage = bgImageRef.current;
            if (mode !== "blur" && !backgroundImage) {
              const presetId = String(mode).startsWith("preset:")
                ? String(mode).slice("preset:".length)
                : null;
              const preset =
                (presetId === THEME_VIDEO_BACKGROUND_ID
                  ? themePresetRef.current
                  : null) ||
                VIDEO_BACKGROUND_PRESETS.find((p) => p.id === presetId) ||
                VIDEO_BACKGROUND_PRESETS.find((p) => p.id === "xpro");
              if (preset) {
                backgroundImage = await resolvePresetBackgroundImage(preset, {
                  logoSrc: bgLogoSrcRef.current,
                  ...dimensionsFromVideoTracks(tracks),
                });
                bgImageRef.current = backgroundImage;
                bgModeRef.current = `preset:${preset.id}`;
                setBgMode(`preset:${preset.id}`);
              }
            } else if (mode !== "blur" && backgroundImage) {
              const presetId = String(mode).startsWith("preset:")
                ? String(mode).slice("preset:".length)
                : null;
              const preset =
                (presetId === THEME_VIDEO_BACKGROUND_ID
                  ? themePresetRef.current
                  : null) ||
                VIDEO_BACKGROUND_PRESETS.find((p) => p.id === presetId);
              if (preset?.kind === "logo" || preset?.kind === "theme") {
                backgroundImage = await resolvePresetBackgroundImage(preset, {
                  logoSrc: bgLogoSrcRef.current,
                  ...dimensionsFromVideoTracks(tracks),
                });
                bgImageRef.current = backgroundImage;
              }
            }
            const previewPresetId = String(mode).startsWith("preset:")
              ? String(mode).slice("preset:".length)
              : null;
            const previewPreset =
              (previewPresetId === THEME_VIDEO_BACKGROUND_ID
                ? themePresetRef.current
                : null) ||
              VIDEO_BACKGROUND_PRESETS.find((p) => p.id === previewPresetId);
            await applyVideoBackground({
              mode: mode === "blur" ? "blur" : "image",
              tracks: tracks.filter((t) => t.kind === "video"),
              backgroundImage: mode === "blur" ? null : bgImageRef.current,
              blurRadius: 15,
              fitType: backgroundFitForPreset(previewPreset),
            });
            if (!cancelled) refreshLocalCameraPreview();
          } catch (e) {
            console.warn("[PublicVideo] preview background:", e?.message || e);
          }
        }
      } catch (mediaErr) {
        if (cancelled) return;
        if (requireCameraRef.current) {
          const classified = classifyRequiredCameraError(mediaErr);
          setPreviewError(classified.message);
        } else {
          const denied =
            mediaErr?.name === "NotAllowedError" ||
            /Permission|NotAllowed|denied/i.test(String(mediaErr?.message || ""));
          setPreviewError(
            denied
              ? "Permita câmera e microfone para ver sua imagem antes de entrar."
              : "Não foi possível abrir a câmera neste dispositivo."
          );
        }
        setPreviewReady(false);
      }
    })();
    return () => {
      cancelled = true;
      // Só libera se as tracks ainda forem do preview (não transferidas ao join).
      if (previewTracksRef.current.length) {
        cleanupPreview();
      }
    };
  }, [phase, cleanupPreview, audioDeviceId, videoDeviceId, refreshLocalCameraPreview]);

  // Anexa preview no DOM depois que o container existe (evita attach com ref null).
  useEffect(() => {
    if (phase !== "name" || !previewReady || camOff) return undefined;
    let cancelled = false;
    const mirror = shouldMirrorLocalPreview(bgModeRef.current);
    const id = requestAnimationFrame(() => {
      if (cancelled) return;
      const container = previewVideoRef.current;
      if (!container) return;
      previewTracksRef.current
        .filter((t) => t.kind === "video" && !isScreenShareTrack(t))
        .forEach((t) => attachLocalCameraPreview(t, container, { mirror }));
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [phase, previewReady, camOff, bgMode, localPreviewNonce]);

  // Anexa PIP local só depois do layout in-call montar.
  useEffect(() => {
    if (phase !== "incall" || camOff) return undefined;
    let cancelled = false;
    const mirror = shouldMirrorLocalPreview(bgModeRef.current);
    const id = requestAnimationFrame(() => {
      if (cancelled) return;
      const container = localPipRef.current;
      if (!container) return;
      localTracksRef.current
        .filter((t) => t.kind === "video" && !isScreenShareTrack(t))
        .forEach((t) => attachLocalCameraPreview(t, container, { mirror }));
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [phase, camOff, remoteCount, sharingScreen, bgMode, localPreviewNonce]);

  const presenting = sharingScreen || remotePresenting;
  const isVoFloorForHuddles =
    (isVirtualOfficeContext || isVirtualOfficeCallContext(callContext)) &&
    !isVoSideRoomCallContext(callContext);
  isVoFloorForHuddlesRef.current = isVoFloorForHuddles;
  const voFloorScene = shouldUseVoFloorScene({
    isVoFloor: isVoFloorForHuddles,
    presenting,
  });
  voFloorSceneRef.current = voFloorScene;

  useEffect(() => {
    if (!floorPeek) return;
    if (floorPeek.local && camOff) setFloorPeek(null);
  }, [floorPeek, camOff]);

  useEffect(() => {
    if (!floorPeek || floorPeek.local) return;
    const participant = roomRef.current?.participants?.get(floorPeek.sid);
    if (!firstEnabledCameraTrack(participant, isScreenShareTrack)) setFloorPeek(null);
  }, [floorPeek, remoteCameraOnCount]);

  useEffect(() => {
    if (!voFloorScene) setFloorPeek(null);
  }, [voFloorScene]);

  const floorPeekTrack = !floorPeek
    ? null
    : floorPeek.local
      ? localTracksRef.current.find(
          (t) => t.kind === "video" && !isScreenShareTrack(t) && t.isEnabled !== false
        ) || null
      : firstEnabledCameraTrack(
          roomRef.current?.participants?.get(floorPeek.sid),
          isScreenShareTrack
        );
  useEffect(() => {
    const tz = voJornadaAlerts?.schedule?.timezone;
    if (tz) setVoClockTimezone((prev) => prev || tz);
  }, [voJornadaAlerts?.schedule?.timezone]);

  useEffect(() => {
    if (!voFloorScene) return undefined;
    const tick = () => {
      const now = new Date();
      setVoFloorNow(now.getTime());
      setVoFloorClock(formatFloorClock(now, voClockTimezone || undefined));
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [voFloorScene, voClockTimezone]);

  const voPresencePeers = useMemo(
    () =>
      listVoPresencePeers(voFloorPresence, resolveAgentUserId(), {
        selfName: displayName,
        selfIdentity: localIdentityRef.current || localIdentity,
      }),
    [displayName, localIdentity, resolveAgentUserId, voFloorPresence]
  );
  const voPresenceGhosts = useMemo(
    () =>
      listVoPresenceGhosts(voFloorPresence, resolveAgentUserId(), {
        selfName: displayName,
        selfIdentity: localIdentityRef.current || localIdentity,
        remoteUserIds: remoteTileUserIds,
        remoteIdentities: remoteTileIdentities,
      }),
    [
      displayName,
      localIdentity,
      remoteTileIdentities,
      remoteTileUserIds,
      resolveAgentUserId,
      voFloorPresence,
    ]
  );
  const visibleRoomParticipants = useMemo(() => {
    const list = excludeSelfFromParticipants(roomParticipants, {
      identity:
        localIdentity ||
        localIdentityRef.current ||
        getStoredJoinIdentity(publicTokenRef.current || token),
    });
    if (!voFloorScene) return list;
    const hidden = userIdsOffVoFloorRug(voFloorPresence);
    if (!hidden.size) return list;
    return list.filter((row) => !hidden.has(Number(userIdFromAgentIdentity(row?.identity))));
  }, [localIdentity, roomParticipants, token, voFloorPresence, voFloorScene]);

  const chatMentionPeople = useMemo(() => {
    const liveIdentities = [];
    try {
      roomRef.current?.participants?.forEach?.((p) => {
        const identity = String(p?.identity || "").trim();
        if (!identity) return;
        liveIdentities.push({
          identity,
          name: labelForIdentity(identity),
          userId: userIdFromAgentIdentity(identity),
        });
      });
    } catch {
      /* ignore */
    }
    const people = mergeChatMentionPeople(
      isVoFloorForHuddles
        ? [voPresencePeers, voPresenceGhosts, liveIdentities, roomParticipants]
        : [roomParticipants, liveIdentities],
      {
        excludeIdentity: localIdentityRef.current || localIdentity,
        excludeUserId: resolveAgentUserId(),
      }
    );
    chatMentionPeopleRef.current = people;
    return people;
  }, [
    isVoFloorForHuddles,
    labelForIdentity,
    localIdentity,
    remoteCount,
    resolveAgentUserId,
    roomParticipants,
    voPresenceGhosts,
    voPresencePeers,
  ]);
  const presencePeerCount = voPresencePeers.length;
  const floorPeopleCount = 1 + Math.max(remoteCount, presencePeerCount);
  const floorCrowd = voFloorCrowdTier(Math.min(floorPeopleCount, MAX_VO_FLOOR_SEATS));
  voFloorCrowdRef.current = floorCrowd;
  const selfSeatFrame = voFloorSeatFrameSx({ camOn: !camOff, crowd: floorCrowd });
  const ghostSeatFrame = voFloorSeatFrameSx({ camOn: false, crowd: floorCrowd });
  const remoteLayout = layoutMode(remoteCount, { presenting, voFloorScene });
  const crowdWarn = shouldWarnCrowd(remoteCount);
  const overflowN = overflowParticipantCount(
    voFloorScene ? Math.max(remoteCount, presencePeerCount) : remoteCount,
    voFloorScene ? MAX_VO_FLOOR_OTHERS : undefined
  );
  const isNarrow = useMediaQuery("(max-width:600px)");
  const localPipOffsetMd =
    presenting && remoteCount > 0
      ? remoteLayout === "filmstrip"
        ? 276
        : 236
      : 12;

  useEffect(() => {
    if (phase !== "incall") return;
    const orderedSids = Array.from(remoteContainersRef.current.keys());
    const spotlightSid = resolveSpotlightSid(orderedSids, { pinnedSid, dominantSid });
    const visibleSids = new Set(
      selectVisibleRemoteSids(orderedSids, {
        pinnedSid,
        dominantSid,
        max: remoteLayout === "voFloor" ? MAX_VO_FLOOR_OTHERS : MAX_MAIN_GRID_TILES,
      })
    );
    remoteContainersRef.current.forEach((box, sid) => {
      syncTileFocus(box, {
        speaking: sid === dominantSid,
        pinned: sid === pinnedSid,
        spotlight: remoteLayout === "spotlight" && sid === spotlightSid,
        hiddenOverflow: !visibleSids.has(sid),
      });
    });
    applyRemoteVideoPriorities(roomRef.current, {
      remoteCount,
      focusSids: [spotlightSid, pinnedSid, dominantSid].filter(Boolean),
      isScreenShareTrack,
    });
  }, [dominantSid, pinnedSid, remoteCount, remoteLayout, phase]);

  useEffect(() => {
    if (phase !== "incall") return;
    applyRemoteTiles();
  }, [voFloorScene, floorCrowd, phase, applyRemoteTiles]);

  useEffect(() => {
    if (phase !== "incall") return undefined;
    unlockVoFloorAudio();
    const unlock = () => unlockVoFloorAudio();
    window.addEventListener("pointerdown", unlock);
    return () => window.removeEventListener("pointerdown", unlock);
  }, [phase]);

  useEffect(() => {
    if (phase !== "incall") return undefined;
    const id = requestAnimationFrame(() => {
      attachFeaturedScreen();
    });
    return () => cancelAnimationFrame(id);
  }, [phase, sharingScreen, remotePresenting, attachFeaturedScreen]);

  // Fundo com logo: tira o espelho do preview (marca ficaria invertida).
  useEffect(() => {
    const mirror = shouldMirrorLocalPreview(bgMode);
    const apply = (container) => {
      container?.querySelectorAll?.("video, canvas")?.forEach((el) => {
        el.style.transform = mirror ? "scaleX(-1)" : "";
      });
    };
    apply(previewVideoRef.current);
    apply(localPipRef.current);
  }, [bgMode]);

  // Reaplica mute/cam no preview
  useEffect(() => {
    previewTracksRef.current.forEach((t) => {
      if (t.kind === "audio") {
        if (muted) t.disable();
        else t.enable();
      }
      if (t.kind === "video" && !isScreenShareTrack(t)) {
        if (camOff && !requireCamera) t.disable();
        else t.enable();
      }
    });
  }, [muted, camOff, previewReady, requireCamera]);

  // Reaplica mute/cam na chamada
  useEffect(() => {
    if (phase !== "incall") return;
    localTracksRef.current.forEach((t) => {
      if (t.kind === "audio") {
        if (muted || voPausedForOtherMeeting) t.disable();
        else t.enable();
      }
      if (t.kind === "video" && !isScreenShareTrack(t)) {
        if ((camOff && !requireCamera) || voPausedForOtherMeeting) t.disable();
        else t.enable();
      }
    });
  }, [phase, muted, camOff, requireCamera, voPausedForOtherMeeting]);

  // Escritório Virtual: pausa ao entrar em outra reunião; notifica VO ao sair
  useEffect(() => {
    const voSession = readVirtualOfficeSession();
    if (!isVirtualOfficeContext && voSession?.roomId && phase === "incall") {
      const title =
        String(channelName || contextFromQuery || "Outra reunião").trim() || "Outra reunião";
      publishOtherMeetingStarted({
        title,
        ref: { context: contextFromQuery || null, token: token || null },
      });
      publishVirtualOfficePauseTracks({ title });
      void patchVirtualOfficePresenceOrRejoin(voSession.roomId, {
        status: "in_other_meeting",
        otherMeetingTitle: title,
        otherMeetingRef: { context: contextFromQuery || null },
      }).catch(() => {});
      return () => {
        publishOtherMeetingEnded();
        publishVirtualOfficeResumeTracks();
        void patchVirtualOfficePresence({ status: "present" }).catch(() => {});
      };
    }
    return undefined;
  }, [
    isVirtualOfficeContext,
    phase,
    channelName,
    contextFromQuery,
    token,
  ]);

  useEffect(() => {
    if (!isVirtualOfficeContext) return undefined;
    return subscribeVirtualOfficeBridge((msg) => {
      if (!msg) return;
      if (msg.type === "other_meeting_started" || msg.type === "pause_tracks") {
        setVoPausedForOtherMeeting(true);
        setVoOtherMeetingTitle(msg.title || "Outra reunião");
        setMuted(true);
        setCamOff(true);
      }
      if (msg.type === "other_meeting_ended" || msg.type === "resume_tracks") {
        setVoPausedForOtherMeeting(false);
        setVoOtherMeetingTitle("");
        setMuted(false);
        setCamOff(false);
      }
      if (msg.type === "session" && msg.session) {
        const st = String(msg.session.status || "").toLowerCase();
        if (isVoCommPausedStatus(st)) {
          setVoPausedForOtherMeeting(true);
          setVoOtherMeetingTitle(voCommStatusPauseTitle(st));
          setMuted(true);
          setCamOff(true);
        } else if (st === "present" && !msg.session.otherMeetingTitle) {
          setVoPausedForOtherMeeting(false);
          setVoOtherMeetingTitle("");
        }
      }
    });
  }, [isVirtualOfficeContext]);

  const ensureRemoteContainer = useCallback(
    (participant) => {
      const grid = remoteGridRef.current;
      if (!grid || !participant) return null;
      let box = remoteContainersRef.current.get(participant.sid);
      if (!box) {
        box = document.createElement("div");
        box.dataset.sid = participant.sid;
        const voFloor = voFloorSceneRef.current;
        Object.assign(box.style, {
          position: "relative",
          background: voFloor ? "transparent" : "#000",
          borderRadius: voFloor ? "28px" : "16px",
          overflow: voFloor ? "visible" : "hidden",
          minHeight: voFloor ? "" : "0",
          width: voFloor ? "" : "100%",
          height: voFloor ? "" : "auto",
          minWidth: voFloor ? "" : "0",
          maxWidth: voFloor ? "" : "100%",
          maxHeight: voFloor ? "" : "100%",
          aspectRatio: voFloor ? "" : CAMERA_TILE_ASPECT,
          cursor: "pointer",
        });
        box.title = voFloor ? "No piso" : "Fixar participante";
        grid.appendChild(box);
        remoteContainersRef.current.set(participant.sid, box);
        setRemoteCount(remoteContainersRef.current.size);
      } else if (box.parentNode !== grid) {
        grid.appendChild(box);
      }
      const identity = String(participant.identity || "");
      box.dataset.identity = identity;
      if (
        evictRemoteTilesForIdentity(remoteContainersRef.current, {
          identity,
          keepSid: participant.sid,
          sameUser: voFloorSceneRef.current,
        })
      ) {
        setRemoteCount(remoteContainersRef.current.size);
      }
      publishRemoteSeatKeys();
      box.style.cursor = "pointer";
      box.title = voFloorSceneRef.current
        ? `${labelForIdentity(identity)} · no piso`
        : "Fixar participante";
      box.onkeydown = (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        box.click();
      };
      box.onclick = (e) => {
        if (e.target.closest?.("button")) return;
        const sid = participant.sid;
        if (voFloorSceneRef.current) {
          const fromTracks = mediaStateFromParticipant(participant, isScreenShareTrack);
          const announced = identityMediaRef.current.get(identity);
          const { camOff: tileCamOff } = resolveTileMediaState(fromTracks, announced);
          const peerId = userIdFromAgentIdentity(identity);
          openVoPersonMenuRef.current?.({
            userId: peerId,
            identity,
            name: labelForIdentity(identity),
            avatarUrl: identityAvatarsRef.current.get(identity) || "",
            cargo:
              (peerId && cargoByUserRef.current.get(Number(peerId))) ||
              identityCargosRef.current.get(identity) ||
              "",
            sid,
            live: !tileCamOff,
            anchorEl: box,
          });
          return;
        }
        setPinnedSid((prev) => (prev === sid ? null : sid));
      };
      ensureParticipantTileChrome(box);
      applySeatBoxStyle(box, voFloorSceneRef.current);
      const fromTracks = mediaStateFromParticipant(participant, isScreenShareTrack);
      const announced = identityMediaRef.current.get(identity);
      const { camOff: tileCamOff, muted: tileMuted, speakerMuted: tileSpeakerMuted } =
        resolveTileMediaState(fromTracks, announced);
      const peerId = userIdFromAgentIdentity(identity);
      const person =
        Number(peerId) > 0
          ? voFloorPresenceRef.current.find((row) => Number(row.userId) === Number(peerId))
          : null;
      syncParticipantTile(box, {
        name: labelForIdentity(identity),
        avatarUrl: identityAvatarsRef.current.get(identity) || "",
        camOff: tileCamOff,
        muted: tileMuted,
        speakerMuted: tileSpeakerMuted,
        voFloor: voFloorSceneRef.current,
        birthday: Boolean(peerId && birthdayUserIdsRef.current.has(Number(peerId))),
        kudosEmoji: peerId ? kudosByUserRef.current.get(Number(peerId))?.emoji || "" : "",
        kudosLabel: peerId ? highlightSeatLabel(kudosByUserRef.current.get(Number(peerId))) : "",
        kudosTitle: peerId ? highlightSeatTitle(kudosByUserRef.current.get(Number(peerId))) : "",
        cargo:
          (peerId && cargoByUserRef.current.get(Number(peerId))) ||
          identityCargosRef.current.get(identity) ||
          "",
        crowd: voFloorCrowdRef.current,
        status: person?.status || "present",
        joinedAt: person?.joinedAt || person?.joined_at || null,
        now: voFloorNowRef.current,
        timeZone: person?.timezone || voClockTimezoneRef.current,
      });
      return box;
    },
    [labelForIdentity, publishRemoteSeatKeys]
  );

  const attachParticipant = useCallback(
    (participant) => {
      if (!participant) return;

      const attachOne = (track) => {
        if (!track || track.kind === "data") return;
        if (isScreenShareTrack(track)) {
          registerRemoteScreen(participant, track);
          return;
        }
        const box = ensureRemoteContainer(participant);
        if (box) {
          attachTrack(track, box, {
            mirror: false,
            playbackMuted: speakerMutedRef.current,
          });
          ensureRemoteContainer(participant);
        }
      };

      const bind = (boxReady) => {
        participant.tracks.forEach((pub) => {
          if (pub.isSubscribed && pub.track) attachOne(pub.track);
        });
        participant.removeAllListeners?.("trackSubscribed");
        participant.removeAllListeners?.("trackUnsubscribed");
        participant.removeAllListeners?.("trackEnabled");
        participant.removeAllListeners?.("trackDisabled");
        participant.on("trackSubscribed", (track) => {
          if (track?.kind === "data") {
            bindRemoteDataTrack(track);
            return;
          }
          if (isScreenShareTrack(track)) {
            registerRemoteScreen(participant, track);
            return;
          }
          const b = boxReady || ensureRemoteContainer(participant);
          if (b) {
            attachTrack(track, b, {
              mirror: false,
              playbackMuted: speakerMutedRef.current,
            });
          }
          ensureRemoteContainer(participant);
        });
        participant.on("trackUnsubscribed", (track) => {
          if (track?.kind === "data") return;
          detachTrackElements(track);
          if (isScreenShareTrack(track)) {
            unregisterRemoteScreen(track, participant);
          }
          ensureRemoteContainer(participant);
        });
        participant.on("trackDisabled", () => ensureRemoteContainer(participant));
        participant.on("trackEnabled", () => ensureRemoteContainer(participant));
        bindParticipantChatTracks(participant);
        ensureRemoteContainer(participant);
      };

      const box = ensureRemoteContainer(participant);
      if (!box) {
        requestAnimationFrame(() => {
          const retryBox = ensureRemoteContainer(participant);
          bind(retryBox);
        });
        bind(null);
        return;
      }
      bind(box);
    },
    [
      ensureRemoteContainer,
      registerRemoteScreen,
      unregisterRemoteScreen,
      bindRemoteDataTrack,
      bindParticipantChatTracks,
      labelForIdentity,
    ]
  );

  const connectWithToken = useCallback(
    async (jwt, roomName, existingTracks = []) => {
      const Video = await import("twilio-video");
      let tracks = Array.isArray(existingTracks) ? existingTracks.filter(Boolean) : [];

      if (!tracks.length) {
        try {
          tracks = await Video.createLocalTracks({
            audio: mutedRef.current ? false : buildAudioConstraint(audioDeviceIdRef.current),
            video:
              requireCameraRef.current || !camOffRef.current
                ? buildVideoConstraint(videoDeviceIdRef.current)
                : false,
          });
        } catch (mediaErr) {
          if (requireCameraRef.current) {
            throw new Error(classifyRequiredCameraError(mediaErr).message);
          }
          const denied =
            mediaErr?.name === "NotAllowedError" ||
            /Permission|NotAllowed|denied/i.test(String(mediaErr?.message || ""));
          throw new Error(
            denied
              ? "Precisamos de acesso à câmera e ao microfone para entrar na chamada."
              : "Não foi possível acessar câmera ou microfone neste dispositivo."
          );
        }
      }

      if (camOffRef.current && !requireCameraRef.current) {
        tracks = tracks.filter((t) => {
          if (t?.kind === "video" && !isScreenShareTrack(t)) {
            try {
              t.stop();
            } catch {
              /* ignore */
            }
            detachTrackElements(t);
            return false;
          }
          return Boolean(t);
        });
      }

      if (mutedRef.current) {
        tracks = tracks.filter((t) => {
          if (t?.kind === "audio") {
            try {
              t.stop();
            } catch {
              /* ignore */
            }
            detachTrackElements(t);
            return false;
          }
          return Boolean(t);
        });
      }

      tracks.forEach((t) => {
        if (t.kind === "audio") t.enable();
        if (t.kind === "video") {
          detachTrackElements(t);
          t.enable();
        }
      });

      localTracksRef.current = tracks;

      if (bgModeRef.current && bgModeRef.current !== "none") {
        try {
          let backgroundImage = bgImageRef.current;
          const mode = bgModeRef.current;
          const presetId = String(mode).startsWith("preset:")
            ? String(mode).slice("preset:".length)
            : null;
          const preset =
            (presetId === THEME_VIDEO_BACKGROUND_ID
              ? themePresetRef.current
              : null) ||
            (presetId
              ? VIDEO_BACKGROUND_PRESETS.find((p) => p.id === presetId)
              : null) ||
            (mode !== "blur" ? VIDEO_BACKGROUND_PRESETS.find((p) => p.id === "xpro") : null);
          if (mode !== "blur") {
            if (
              !backgroundImage ||
              preset?.kind === "logo" ||
              preset?.kind === "theme"
            ) {
              if (preset) {
                backgroundImage = await resolvePresetBackgroundImage(preset, {
                  logoSrc: bgLogoSrcRef.current,
                  ...dimensionsFromVideoTracks(tracks),
                });
                bgImageRef.current = backgroundImage;
              }
            }
          }
          await applyVideoBackground({
            mode: mode === "blur" ? "blur" : "image",
            tracks: tracks.filter((t) => t.kind === "video"),
            backgroundImage: mode === "blur" ? null : bgImageRef.current,
            blurRadius: 15,
            fitType: backgroundFitForPreset(preset),
          });
          refreshLocalCameraPreview();
        } catch (e) {
          console.warn("[PublicVideo] in-call background:", e?.message || e);
        }
      }

      const room = await Video.connect(jwt, {
        name: roomName,
        tracks,
        dominantSpeaker: true,
        receiveTranscriptions: true,
        networkQuality: { local: 1, remote: 0 },
        // Permite switchOff/switchOn manual em salas com muitos remotes.
        bandwidthProfile: {
          video: {
            mode: "collaboration",
            dominantSpeakerPriority: "high",
            clientTrackSwitchOffControl: "manual",
            contentPreferencesMode: "auto",
          },
        },
      });
      roomRef.current = room;
      setIsReconnecting(false);
      reconnectingRef.current = false;

      const identity = String(room.localParticipant?.identity || "").trim();
      if (identity) {
        setLocalIdentity(identity);
        localIdentityRef.current = identity;
        rememberParticipantName(identity, displayNameRef.current);
        const persistToken = publicTokenRef.current || token;
        if (persistToken) setStoredJoinIdentity(persistToken, identity);
      }
      watchLocalAudioEnded();

      try {
        const dataTrack = new Video.LocalDataTrack({ name: "in-call-chat" });
        await room.localParticipant.publishTrack(dataTrack);
        dataTrackRef.current = dataTrack;
        publishHelloOnDataTrack();
        publishVoArrived();
        setTimeout(() => publishHelloOnDataTrack(), 600);
        setTimeout(() => publishVoArrived(), 700);
      } catch (e) {
        console.warn("[PublicVideo] LocalDataTrack:", e?.message || e);
      }

      const pushLiveCaption = (text, speaker) => {
        const line = String(text || "").trim();
        if (!line) return;
        const who = String(speaker || "").trim();
        setLiveCaption(who ? `${who}: ${line}` : line);
        setCaptionHint("");
        if (liveCaptionClearRef.current) clearTimeout(liveCaptionClearRef.current);
        liveCaptionClearRef.current = setTimeout(() => setLiveCaption(""), 8000);
      };

      room.on("transcription", (ev) => {
        const text = String(ev?.transcription || ev?.text || "").trim();
        if (!text) return;
        const partial = ev?.partial === true || ev?.final === false;
        const participantSid =
          (typeof ev?.participant === "string" ? ev.participant : null) ||
          ev?.participantSid ||
          ev?.participant?.sid ||
          null;
        const speaker = resolveCaptionSpeakerLabel({
          identity: ev?.identity || null,
          participantSid,
          participant: ev?.participant,
          displayName: ev?.displayName || null,
          room,
          labelForIdentity,
          fallback:
            room.localParticipant?.sid === participantSid
              ? displayNameRef.current || "Você"
              : "Participante",
        });
        if (!partial) {
          queueTranscriptChunk({
            text,
            participantSid,
            identity: ev?.identity || null,
            displayName: speaker,
            final: true,
          });
        }
        pushLiveCaption(text, speaker);
      });

      try {
        room.localParticipant?.on?.("networkQualityLevelChanged", (level) => {
          setNetworkQuality(level == null ? null : Number(level));
        });
        const initialNq = room.localParticipant?.networkQualityLevel;
        if (initialNq != null) setNetworkQuality(Number(initialNq));
      } catch (e) {
        console.warn("[PublicVideo] networkQuality:", e?.message || e);
      }

      room.on("reconnecting", () => {
        reconnectingRef.current = true;
        setIsReconnecting(true);
      });
      room.on("reconnected", () => {
        reconnectingRef.current = false;
        setIsReconnecting(false);
      });

      room.on("dominantSpeakerChanged", (participant) => {
        setDominantSid(participant?.sid || null);
      });
      if (room.dominantSpeaker?.sid) {
        setDominantSid(room.dominantSpeaker.sid);
      }

      room.on("participantConnected", (p) => {
        attachParticipant(p);
        publishHelloOnDataTrack();
      });
      room.on("participantDisconnected", (p) => {
        if (
          isVoFloorForHuddlesRef.current &&
          !voIdleHoldRef.current &&
          !leftBySelfRef.current
        ) {
          const uid = Number(userIdFromAgentIdentity(p?.identity) || 0);
          const stillOnFloor = uid > 0 && voPresenceIdsRef.current.has(uid);
          if (!stillOnFloor) playVoFloorSound("leave");
        }
        const remoteRole = roleFromParticipantIdentity(p?.identity);
        if (remoteRole) {
          lastRemoteLeftRoleRef.current = remoteRole;
          setRemoteLeftRole(remoteRole);
        }
        unregisterRemoteScreen(null, p);
        detachParticipant(p, remoteContainersRef.current);
        const node = remoteContainersRef.current.get(p.sid);
        if (node) {
          node.remove();
          remoteContainersRef.current.delete(p.sid);
        }
        setRemoteCount(remoteContainersRef.current.size);
        publishRemoteSeatKeys();
        setPinnedSid((prev) => (prev === p.sid ? null : prev));
        setDominantSid((prev) => (prev === p.sid ? null : prev));
      });
      room.on("disconnected", async (_room, error) => {
        flushTranscript(true);
        if (voIdleHoldRef.current) {
          roomRef.current = null;
          return;
        }
        if (leftBySelfRef.current) {
          if (!endRequestedRef.current) setEndCause("self");
          cleanupRoom();
          setPhase("ended");
          return;
        }
        // Desconexão inesperada: só encerra se o servidor diz que a sala acabou.
        const tkn = publicTokenRef.current || token;
        let serverEnded = false;
        if (tkn && company) {
          try {
            const meta = await fetchPublicVideoRoom(tkn, company, { live: false });
            serverEnded = meta?.status === "ended";
          } catch (e) {
            const status = e?.response?.status;
            serverEnded = status === 410 || status === 404;
          }
        }
        if (serverEnded) {
          setEndCause("remote");
          if (lastRemoteLeftRoleRef.current) {
            setRemoteLeftRole(lastRemoteLeftRoleRef.current);
          }
          cleanupRoom();
          setPhase("ended");
          return;
        }
        // `disconnected` no Twilio = sessão local morta (não há auto-rejoin).
        // Overlay curto + tela ended com opção de entrar de novo (sala ainda ativa).
        reconnectingRef.current = true;
        setIsReconnecting(true);
        console.warn(
          "[PublicVideo] disconnected (room still active):",
          error?.message || error || "unknown"
        );
        setTimeout(() => {
          if (leftBySelfRef.current || endRequestedRef.current) return;
          if (roomRef.current) return;
          reconnectingRef.current = false;
          setIsReconnecting(false);
          setEndCause("self");
          cleanupRoom();
          setPhase("ended");
        }, 2500);
      });

      // Monta o layout in-call primeiro; depois anexa quem já está na sala.
      setPhase("incall");
      if (isVoFloorForHuddlesRef.current) unlockVoFloorAudio();
      loadChatHistory();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          room.participants.forEach((p) => attachParticipant(p));
        });
      });
    },
    [
      attachParticipant,
      cleanupRoom,
      muted,
      camOff,
      queueTranscriptChunk,
      flushTranscript,
      unregisterRemoteScreen,
      loadChatHistory,
      publishHelloOnDataTrack,
      publishVoArrived,
      rememberParticipantName,
      labelForIdentity,
      publishRemoteSeatKeys,
      token,
      company,
      refreshLocalCameraPreview,
      watchLocalAudioEnded,
    ]
  );

  const disconnectVoFloorKeepPresence = useCallback(() => {
    if (!isVirtualOfficeContext) return;
    voIdleHoldRef.current = true;
    voParkedAtRef.current = Date.now();
    setVoTwilioHeld(true);
    setRemoteWantsMedia(false);
    const room = roomRef.current;
    if (!room) return;
    try {
      room.disconnect();
    } catch {
      /* ignore */
    }
    roomRef.current = null;
    clearRemoteParticipantTiles(remoteContainersRef.current, remoteGridRef.current);
    setRemoteCount(0);
    remoteTileUserIdsRef.current = "";
    setRemoteTileUserIds([]);
    remoteTileIdentitiesRef.current = "";
    setRemoteTileIdentities([]);
    remoteCameraOnCountRef.current = 0;
    setRemoteCameraOnCount(0);
  }, [isVirtualOfficeContext]);

  const reconnectVoFloorKeepPresence = useCallback(async () => {
    const tkn = publicTokenRef.current || token;
    if (!isVirtualOfficeContext || !tkn || !company || roomRef.current || voTwilioBusyRef.current) {
      if (roomRef.current) voIdleHoldRef.current = false;
      return;
    }
    voTwilioBusyRef.current = true;
    try {
      const agentUserId = joinRole === "agent" ? resolveAgentUserId() : null;
      const joinResult = await fetchPublicVideoAccessToken(tkn, company, {
        displayName: displayNameRef.current || "",
        role: joinRole,
        ...(agentUserId ? { userId: agentUserId } : {}),
      });
      voIdleHoldRef.current = false;
      voParkedAtRef.current = 0;
      setVoTwilioHeld(false);
      await connectWithToken(
        joinResult.token,
        joinResult.roomName,
        localTracksRef.current
      );
    } catch (e) {
      console.warn("[PublicVideo] vo floor reconnect:", e?.message || e);
      voIdleHoldRef.current = true;
      setVoTwilioHeld(true);
    } finally {
      voTwilioBusyRef.current = false;
    }
  }, [
    company,
    connectWithToken,
    isVirtualOfficeContext,
    joinRole,
    resolveAgentUserId,
    token,
  ]);

  useEffect(() => {
    if (!isVoFloorForHuddles || phase !== "incall") return undefined;
    if (voPausedForOtherMeeting) {
      disconnectVoFloorKeepPresence();
      return undefined;
    }
    const keep = shouldKeepVoTwilioLive({
      isVoFloor: true,
      pausedForOtherMeeting: false,
      sharingScreen,
      localCamOn: !camOff,
      localMicOn: !muted,
      remoteWantsMedia,
    });
    if (keep) {
      if (!roomRef.current) void reconnectVoFloorKeepPresence();
      return undefined;
    }
    if (!roomRef.current) return undefined;
    const grace = voTwilioHoldGraceMs({
      keepLive: false,
      presencePeerCount,
      remoteTwilioCount: remoteCount,
    });
    const timer = window.setTimeout(() => {
      if (roomRef.current) disconnectVoFloorKeepPresence();
    }, grace);
    return () => window.clearTimeout(timer);
  }, [
    camOff,
    disconnectVoFloorKeepPresence,
    isVoFloorForHuddles,
    muted,
    phase,
    presencePeerCount,
    reconnectVoFloorKeepPresence,
    remoteCount,
    remoteWantsMedia,
    sharingScreen,
    voPausedForOtherMeeting,
  ]);

  useEffect(() => {
    if (!isVoFloorForHuddles || phase !== "incall" || voPausedForOtherMeeting) return undefined;
    if (roomRef.current || voTwilioBusyRef.current) return undefined;
    const join = shouldJoinVoTwilio({
      isVoFloor: true,
      pausedForOtherMeeting: false,
      sharingScreen,
      localCamOn: !camOff,
      localMicOn: !muted,
      remoteWantsMedia,
      wakeRequested: voWakeUntilRef.current > Date.now(),
    });
    if (join) void reconnectVoFloorKeepPresence();
    return undefined;
  }, [
    camOff,
    isVoFloorForHuddles,
    muted,
    phase,
    reconnectVoFloorKeepPresence,
    remoteWantsMedia,
    sharingScreen,
    voPausedForOtherMeeting,
    voTwilioHeld,
  ]);

  const isLobbyGuestForm = mode === "lobby" && joinRole !== "agent";
  const emailTrimmed = String(guestEmail || "").trim();
  const phoneTrimmed = String(guestPhone || "").trim();
  const hasContact = Boolean(emailTrimmed || phoneDigitsOnly(phoneTrimmed).length >= 8);
  const emailLooksValid =
    !emailTrimmed || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  const cameraReady = previewReady && localTracksHaveCamera(previewTracksRef.current) && !camOff;
  const cameraPolicyCopy = cameraRequiredJoinCopy();
  const canJoin =
    Boolean(String(displayName || "").trim()) &&
    (!isLobbyGuestForm || (hasContact && emailLooksValid)) &&
    (!recordingEnabled || recordingConsent) &&
    (!requireCamera || cameraReady);

  const handleJoin = async () => {
    const name = String(displayName || "").trim();
    if (!name) return;
    if (isVoFloorForHuddlesRef.current) unlockVoFloorAudio();
    if (recordingEnabled && !recordingConsent) {
      setErrorGuide(null);
      setErrorMsg(recordingConsentCopy({ callContext, recordingVisibility }).requiredHint);
      setErrorCanRetryJoin(true);
      setPhase("error");
      return;
    }
    if (requireCamera) {
      const hasVideo = localTracksHaveCamera(previewTracksRef.current);
      if (!hasVideo || camOff) {
        setPreviewError(previewError || cameraPolicyCopy.joinBlocked);
        return;
      }
    }
    if (isLobbyGuestForm) {
      if (!hasContact) {
        setErrorGuide(null);
        setErrorMsg(
          "Informe e-mail ou celular (ao menos um dos dois) para localizarmos seu cadastro."
        );
        setErrorCanRetryJoin(true);
        setPhase("error");
        return;
      }
      if (!emailLooksValid) {
        setErrorGuide(null);
        setErrorMsg("Informe um e-mail válido.");
        setErrorCanRetryJoin(true);
        setPhase("error");
        return;
      }
    }
    // Transfere ownership antes de sair do pré-join (evita stop no cleanup do preview).
    let tracksForJoin = previewTracksRef.current;
    previewTracksRef.current = [];
    setPhase("connecting");
    setErrorMsg("");
    setErrorGuide(null);
    try {
      let joinResult;
      const consentPayload = {
        recordingConsent: Boolean(recordingConsent || !recordingEnabled),
        cameraReady: Boolean(
          !requireCamera || localTracksHaveCamera(tracksForJoin)
        ),
        camOff: Boolean(requireCamera ? false : camOff),
      };
      if (mode === "lobby") {
        joinResult = await startPublicVideoLobby(lobbyToken, company, {
          displayName: name,
          email: emailTrimmed || undefined,
          phone: phoneTrimmed ? toE164Digits(phoneTrimmed) || phoneTrimmed : undefined,
          avatarUrl: localAvatarUrlRef.current || undefined,
          ...consentPayload,
        });
      } else {
        const agentUserId = joinRole === "agent" ? resolveAgentUserId() : null;
        joinResult = await fetchPublicVideoAccessToken(token, company, {
          displayName: name,
          role: joinRole,
          avatarUrl: localAvatarUrlRef.current || undefined,
          resumeIdentity:
            getStoredJoinIdentity(token) || localIdentityRef.current || undefined,
          ...(agentUserId ? { userId: agentUserId } : {}),
          ...consentPayload,
        });
      }

      // Lobby: navega de verdade para a sala (evita modo lobby + nova solicitação no “Entrar de novo”).
      if (mode === "lobby" && joinResult?.publicToken) {
        publicTokenRef.current = joinResult.publicToken;
        endRequestedRef.current = false;
        leftBySelfRef.current = false;
        lastRemoteLeftRoleRef.current = null;
        setEndCause(null);
        setRemoteLeftRole(null);
        const q = company ? `?company=${encodeURIComponent(company)}` : "";
        navigate(`/video/${encodeURIComponent(joinResult.publicToken)}${q}`, { replace: true });
      }
      if (joinResult?.publicToken) {
        publicTokenRef.current = joinResult.publicToken;
      }
      if (joinResult?.isPrivate != null) {
        setIsPrivateRoom(Boolean(joinResult.isPrivate));
      }
      if (joinResult?.identity) {
        setLocalIdentity(String(joinResult.identity));
        localIdentityRef.current = String(joinResult.identity);
        setStoredJoinIdentity(
          joinResult.publicToken || publicTokenRef.current || token,
          joinResult.identity
        );
        rememberParticipantName(joinResult.identity, name);
        const avatar = sanitizeAvatarUrl(joinResult.avatarUrl) || localAvatarUrlRef.current;
        if (avatar) {
          setLocalAvatarUrl(avatar);
          localAvatarUrlRef.current = avatar;
          rememberParticipantAvatar(joinResult.identity, avatar);
        }
      }

      let skipTwilio = false;
      if (isVoFloorForHuddlesRef.current && !requireCamera) {
        try {
          const voId =
            Number(virtualOfficeRoomIdRef.current) ||
            Number(readVirtualOfficeSession()?.roomId) ||
            0;
          const presenceRes = voId > 0 ? await fetchVirtualOfficePresence(voId) : null;
          const people = parsePresencePayload(presenceRes).people;
          const selfId = resolveAgentUserId();
          skipTwilio = shouldSkipVoTwilioOnJoin({
            isVoFloor: true,
            requireCamera,
            localCamOn: !camOff,
            localMicOn: !muted,
            presencePeerCount: countVoPresencePeers(people, selfId),
            liveVideoPeerCount: countVoLiveVideoPeers(people, selfId),
          });
        } catch {
          skipTwilio = false;
        }
      }
      if (skipTwilio) {
        if (camOff) {
          tracksForJoin = releaseOwnedCameraTracks(tracksForJoin);
        }
        if (muted) {
          tracksForJoin = releaseOwnedAudioTracks(tracksForJoin);
        }
        localTracksRef.current = tracksForJoin;
        voIdleHoldRef.current = true;
        voParkedAtRef.current = Date.now();
        setVoTwilioHeld(true);
        setPhase("incall");
        loadChatHistory();
        if (isVoFloorForHuddlesRef.current) {
          unlockVoFloorAudio();
          publishVoArrived();
        }
      } else {
        await connectWithToken(joinResult.token, joinResult.roomName, tracksForJoin);
      }
    } catch (e) {
      stopTracks(tracksForJoin);
      cleanupRoom();
      const code = extractApiCode(e);
      const isPrivateBlock = code === "video.private_forbidden";
      if (isPrivateBlock) {
        const guide = classifyJoinVideoError(extractApiMessage(e), {
          code,
          role: joinRole,
          callContext,
        });
        setPrivateForbidden(true);
        setErrorGuide(guide);
        setErrorMsg(guide.body || tTeam("activeVideo.forbiddenBody"));
        setErrorCanRetryJoin(false);
        setPhase("error");
        return;
      }
      if (code === "video.camera_required") {
        const guide = classifyJoinVideoError(cameraPolicyCopy.unavailable, {
          code,
          role: joinRole,
          callContext,
        });
        setPrivateForbidden(false);
        setErrorGuide(guide);
        setErrorMsg(guide.body);
        setErrorCanRetryJoin(true);
        setPhase("error");
        return;
      }
      const guide = buildJoinErrorGuide(e, { role: joinRole, callContext });
      setPrivateForbidden(false);
      setErrorGuide(guide);
      setErrorMsg(guide.body);
      setErrorCanRetryJoin(Boolean(guide.canRetry));
      setPhase("error");
    }
  };

  const toggleMute = useCallback(() => {
    void setLocalMicEnabled(mutedRef.current);
  }, [setLocalMicEnabled]);

  const toggleSpeakerMute = useCallback(() => {
    const next = !speakerMutedRef.current;
    speakerMutedRef.current = next;
    setSpeakerMuted(next);
    setVoSpeakerMuted(next);
    setVoFloorSpeakerMuted(next);
    applyRemoteSpeakerMute(
      [remoteGridRef.current, presentationRef.current],
      next
    );
    setMediaSnack(
      next
        ? "Áudio do escritório desligado — você não ouve quem está falando"
        : "Você volta a ouvir o escritório"
    );
    publishMediaState();
  }, [publishMediaState]);

  const toggleCam = useCallback(() => {
    void setLocalCameraEnabled(camOffRef.current);
  }, [setLocalCameraEnabled]);

  const toggleRoomPrivacy = useCallback(async () => {
    if (joinRole !== "agent" || privacyBusy) return;
    const tkn = publicTokenRef.current || token;
    if (!tkn || !company) return;
    const next = !isPrivateRoom;
    setPrivacyBusy(true);
    try {
      await setPublicVideoRoomPrivacy(tkn, company, {
        isPrivate: next,
        role: "agent",
        userId: resolveAgentUserId(),
      });
      setIsPrivateRoom(next);
      setPrivacySnack(
        next ? tTeam("activeVideo.snackbarPrivate") : tTeam("activeVideo.snackbarOpen")
      );
    } catch (e) {
      setScreenError(extractApiMessage(e) || tTeam("activeVideo.forbiddenTitle"));
    } finally {
      setPrivacyBusy(false);
    }
  }, [
    joinRole,
    privacyBusy,
    token,
    company,
    isPrivateRoom,
    resolveAgentUserId,
    tTeam,
  ]);

  const openVoPersonMenu = useCallback(
    (raw = {}) => {
      const resolved = resolveVoFloorPerson(voFloorPresenceRef.current, raw);
      const selfId = Number(resolveAgentUserId()) || 0;
      if (resolved.userId && selfId && Number(resolved.userId) === selfId) return;
      if (resolved.identity && resolved.identity === String(localIdentityRef.current || "")) {
        return;
      }
      const uid = Number(resolved.userId) || 0;
      setVoPersonMenu({
        ...resolved,
        sid: raw.sid || null,
        live: Boolean(raw.live),
        birthday: Boolean(uid && birthdayUserIdsRef.current.has(uid)),
        seal: uid ? kudosByUserRef.current.get(uid) || null : null,
        anchorEl: raw.anchorEl || null,
      });
    },
    [resolveAgentUserId]
  );
  openVoPersonMenuRef.current = openVoPersonMenu;

  const mentionPersonInChat = useCallback(
    (person) => {
      if (!person?.name && !person?.identity) return;
      setVoPersonMenu(null);
      setChatSeedMention({
        type: "user",
        name: person.name || "Colega",
        identity: person.identity || "",
        userId: person.userId || null,
      });
      openChatPanel();
    },
    [openChatPanel]
  );

  const openVoPrivateDialog = useCallback(async (opts = {}) => {
    const voId =
      Number(virtualOfficeRoomId) ||
      Number(virtualOfficeRoomIdRef.current) ||
      Number(readVirtualOfficeSession()?.roomId) ||
      0;
    if (!voId) {
      setScreenError("Sala do escritório virtual não encontrada.");
      return;
    }
    const admitMode = isVoSideRoomCallContext(callContext);
    const preselected = (Array.isArray(opts.participantUserIds) ? opts.participantUserIds : [])
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
    setVoPrivateMode(admitMode ? "admit" : "start");
    setVoPrivateError("");
    setVoPrivateSelected(preselected);
    setVoHuddleTitle(String(opts.title || "").trim() || "Café");
    setVoHuddleVisibility("public");
    setVoPrivateOpen(true);
    try {
      const res = await fetchVirtualOfficePresence(voId);
      const list = res?.data?.data || res?.data || [];
      const myId = Number(resolveAgentUserId()) || 0;
      setVoPrivatePresence(
        (Array.isArray(list) ? list : []).filter((p) => Number(p.userId) !== myId)
      );
    } catch (e) {
      setVoPrivateError(e?.response?.data?.message || e?.message || "Erro ao listar presentes.");
    }
  }, [virtualOfficeRoomId, resolveAgentUserId, callContext]);

  const startRoomWithPerson = useCallback(
    (person) => {
      const uid = Number(person?.userId);
      if (!Number.isFinite(uid) || uid <= 0) return;
      setVoPersonMenu(null);
      void openVoPrivateDialog({
        participantUserIds: [uid],
        title: `Café com ${voFloorFirstName(person.name)}`,
      });
    },
    [openVoPrivateDialog]
  );

  const startVoPrivateFromToolbar = useCallback(async () => {
    const voId =
      Number(virtualOfficeRoomId) ||
      Number(virtualOfficeRoomIdRef.current) ||
      Number(readVirtualOfficeSession()?.roomId) ||
      0;
    if (voPrivateMode === "admit" && !voPrivateSelected.length) return;
    setVoPrivateBusy(true);
    setVoPrivateError("");
    try {
      if (voPrivateMode === "admit") {
        const videoId = Number(twilioDbRoomId);
        if (!videoId) {
          setVoPrivateError("ID da conversa privada indisponível. Atualize a página.");
          return;
        }
        await admitVirtualOfficePrivateRoom(videoId, { userIds: voPrivateSelected });
        setVoPrivateOpen(false);
        return;
      }
      if (!voId) return;
      const joined = await joinVirtualOfficeRoom(voId, {
        deviceInfo: {
          ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
          source: "virtual_office_huddle_create",
        },
      });
      const joinedData = joined?.data?.data || joined?.data || {};
      if (joinedData?.room?.nome) setVoFloorName(String(joinedData.room.nome).trim());
      writeVirtualOfficeSession({
        roomId: voId,
        roomName: joinedData?.room?.nome || readVirtualOfficeSession()?.roomName || null,
        videoToken: joinedData?.video?.publicToken || readVirtualOfficeSession()?.videoToken || null,
        status: "present",
        joinedAt: Date.now(),
        attendancePolicy: joinedData?.attendancePolicy || readVirtualOfficeSession()?.attendancePolicy || null,
      });
      const res = await createVirtualOfficeHuddle(voId, {
        title: voHuddleTitle,
        visibility: voHuddleVisibility,
        participantUserIds: voPrivateSelected,
      });
      const data = res?.data?.data || res?.data || {};
      const joinUrl = data?.video?.joinUrl;
      setVoPrivateOpen(false);
      if (joinUrl) {
        publishVirtualOfficePauseTracks({ title: voHuddleTitle || "Sala do escritório" });
        openAgentVideoJoinUrl(joinUrl, {
          context: voHuddleVisibility === "public" ? "vo_huddle" : "vo_private",
          name: displayName || "",
        });
      }
    } catch (e) {
      setVoPrivateError(
        e?.response?.data?.message || e?.message || "Não foi possível iniciar a conversa."
      );
    } finally {
      setVoPrivateBusy(false);
    }
  }, [
    virtualOfficeRoomId,
    voPrivateSelected,
    displayName,
    voPrivateMode,
    twilioDbRoomId,
    voHuddleTitle,
    voHuddleVisibility,
  ]);

  const refreshVoHuddleRequests = useCallback(async () => {
    const videoId = Number(twilioDbRoomId);
    if (!videoId || !isVoSideRoomCallContext(callContext)) return;
    try {
      const res = await listVirtualOfficeHuddleJoinRequests(videoId);
      setVoHuddleRequests(res?.data?.data || res?.data || []);
    } catch {
      /* ignore poll errors */
    }
  }, [twilioDbRoomId, callContext]);

  const resolveVoFloorRoomId = useCallback(() => {
    return (
      Number(virtualOfficeRoomId) ||
      Number(virtualOfficeRoomIdRef.current) ||
      Number(readVirtualOfficeSession()?.roomId) ||
      0
    );
  }, [virtualOfficeRoomId]);

  const voFloorOtherCalls = useMemo(
    () =>
      peopleInOtherCalls({
        presence: voFloorPresence,
        huddles: voFloorHuddles,
        myUserId: resolveAgentUserId(),
      }),
    [voFloorPresence, voFloorHuddles, resolveAgentUserId]
  );
  const voBirthdayHonorees = useMemo(() => birthdayPeople(voFloorPresence), [voFloorPresence]);
  birthdayUserIdsRef.current = new Set(voBirthdayHonorees.map((row) => Number(row.userId)));
  cargoByUserRef.current = new Map(
    (voFloorPresence || [])
      .map((row) => [Number(row.userId), presenceCargoOf(row)])
      .filter(([, cargo]) => cargo)
  );
  const selfPresence = findSelfPresence(voFloorPresence, {
    userId: resolveAgentUserId(),
    name: displayName,
    identity: localIdentityRef.current || localIdentity,
  });
  const selfCargo = presenceCargoOf(selfPresence);
  if (selfCargo && localCargoRef.current !== selfCargo) {
    localCargoRef.current = selfCargo;
  }
  const selfStatus = normalizeVoCommStatus(selfPresence?.status || "present");
  const effectiveClockTz = resolveClockTimezone(
    voClockTimezone,
    selfPresence?.timezone,
    voJornadaAlerts?.schedule?.timezone
  );
  const clockApplies = isClockPolicy(readVirtualOfficeSession()?.attendancePolicy);

  useEffect(() => {
    if (phase !== "incall") return;
    applyRemoteTiles();
  }, [phase, applyRemoteTiles, voBirthdayHonorees, voFloorPresence, voFloorNow]);

  useEffect(() => {
    if (phase !== "incall" || !selfCargo) return;
    publishHelloOnDataTrack();
  }, [phase, selfCargo, publishHelloOnDataTrack]);

  useEffect(() => {
    if (!isVoFloorForHuddles || phase !== "incall") return;
    const st = String(selfPresence?.status || "").toLowerCase();
    if (st === "in_other_meeting") {
      setVoPausedForOtherMeeting(true);
      setVoOtherMeetingTitle(selfPresence?.otherMeetingTitle || "Outra reunião");
      setMuted(true);
      setCamOff(true);
    } else if (st === "present" && !selfPresence?.otherMeetingTitle) {
      setVoPausedForOtherMeeting(false);
      setVoOtherMeetingTitle("");
    }
  }, [isVoFloorForHuddles, phase, selfPresence]);

  const officeLife = useVoFloorOfficeLife({
    enabled: Boolean(isVoFloorForHuddles && phase === "incall" && joinRole === "agent"),
    returnPath: appReturnPath,
  });
  const floorHighlights = useVoFloorHighlights({
    enabled: Boolean(isVoFloorForHuddles && phase === "incall" && joinRole === "agent"),
    roomId: resolveVoFloorRoomId(),
    onFresh: (item) => {
      pushVoHouseBanner(item.copy);
      playVoFloorSound("kudos");
      if (item.emoji) pushVoReaction(item.emoji, item.userName);
    },
  });
  const floorSparks = useVoFloorSparks({
    enabled: Boolean(isVoFloorForHuddles && phase === "incall"),
    roomId: resolveVoFloorRoomId(),
    onShow: () => playVoFloorSound("spark"),
  });
  kudosByUserRef.current = new Map(
    floorHighlights.items.map((item) => [Number(item.userId), item])
  );
  floorHighlightsRefetchRef.current = floorHighlights.refetch;

  useEffect(() => {
    if (phase !== "incall") return;
    applyRemoteTiles();
  }, [phase, applyRemoteTiles, floorHighlights.items]);

  const refreshVoFloorHuddles = useCallback(async () => {
    const voId = resolveVoFloorRoomId();
    if (!voId || !isVoFloorForHuddles) return;
    try {
      const [huddlesRes, presenceRes, wishesRes] = await Promise.allSettled([
        listVirtualOfficeHuddles(voId),
        fetchVirtualOfficePresence(voId),
        listVirtualOfficeBirthdayWishes(voId),
      ]);
      if (huddlesRes.status === "fulfilled") {
        const huddles = huddlesRes.value?.data?.data || huddlesRes.value?.data || [];
        setVoFloorHuddles(Array.isArray(huddles) ? huddles : []);
      }
      if (presenceRes.status === "fulfilled") {
        const parsedPresence = parsePresencePayload(presenceRes.value);
        const people = (parsedPresence.people || []).map((row) => ({
          ...row,
          userCargo: presenceCargoOf(row) || row.userCargo || null,
        }));
        const nextTz = resolveClockTimezone(
          parsedPresence.timezone,
          people.find((row) => row.timezone)?.timezone
        );
        if (nextTz) setVoClockTimezone((prev) => (prev === nextTz ? prev : nextTz));
        const selfId = resolveAgentUserId();
        const nextPeers = listVoPresencePeers(people, selfId);
        const prevIds = voPresenceIdsRef.current;
        const nextIds = new Set(nextPeers.map((row) => Number(row.userId)));
        if (voIdleHoldRef.current && prevIds.size) {
          for (const row of nextPeers) {
            if (!prevIds.has(Number(row.userId))) {
              pushVoArrival(row.userName, row.twilioIdentity || `user-${row.userId}`);
            }
          }
          for (const id of prevIds) {
            if (!nextIds.has(id)) playVoFloorSound("leave");
          }
        }
        voPresenceIdsRef.current = nextIds;
        setVoFloorPresence(people);
      }
      if (wishesRes.status === "fulfilled") {
        const wishes = wishesRes.value?.data?.data || wishesRes.value?.data || [];
        setVoBirthdayWishes(Array.isArray(wishes) ? wishes : []);
      }
    } catch {
      /* ignore poll errors */
    }
  }, [
    isVoFloorForHuddles,
    pushVoArrival,
    resolveAgentUserId,
    resolveVoFloorRoomId,
  ]);

  const handleVoStatusChange = useCallback(
    async (next) => {
      const status = normalizeVoCommStatus(next);
      if (status === "in_other_meeting") return;
      setVoStatusMenuAnchor(null);
      const voId = resolveVoFloorRoomId();
      try {
        await patchVirtualOfficePresenceOrRejoin(voId, { status });
        const cur = readVirtualOfficeSession();
        if (cur) writeVirtualOfficeSession({ ...cur, status });
        if (isVoCommPausedStatus(status)) {
          publishVirtualOfficePauseTracks({ title: voCommStatusPauseTitle(status) });
        } else if (status === "present") {
          publishVirtualOfficeResumeTracks();
        }
        void refreshVoFloorHuddles();
        void voJornadaAlerts.refresh();
      } catch {
        setMediaSnack("Não foi possível atualizar o status.");
      }
    },
    [refreshVoFloorHuddles, resolveVoFloorRoomId, voJornadaAlerts.refresh]
  );

  const sendVoBirthdayWish = useCallback(
    async ({ gift, message }) => {
      const voId = resolveVoFloorRoomId();
      const target = voBirthdayTarget;
      if (!voId || !target?.userId) return;
      setVoBirthdayBusy(true);
      setVoBirthdayError("");
      try {
        const res = await sendVirtualOfficeBirthdayWish(voId, {
          toUserId: target.userId,
          gift,
          message,
        });
        const wish = res?.data?.data || res?.data || {};
        setVoBirthdayWishes((prev) => [wish, ...prev.filter((item) => item.id !== wish.id)].slice(0, 40));
        playVoFloorSound("react_party");
        pushVoReaction(birthdayGiftById(gift).emoji, displayNameRef.current, localIdentityRef.current);
        publishVoData({
          type: "vo-birthday-wish",
          id: wish.id,
          toUserId: target.userId,
          fromUserId: resolveAgentUserId(),
          fromName: displayNameRef.current,
          gift,
          message,
        });
        setVoBirthdayTarget(null);
      } catch (e) {
        setVoBirthdayError(e?.response?.data?.message || e?.message || "Não foi possível enviar.");
      } finally {
        setVoBirthdayBusy(false);
      }
    },
    [publishVoData, publishVoReaction, pushVoReaction, resolveAgentUserId, resolveVoFloorRoomId, voBirthdayTarget]
  );

  const cheerVoHighlight = useCallback(
    (emoji, item) => {
      if (!emoji || !item?.userId) return;
      playVoFloorSound(soundKindForReaction(emoji));
      pushVoReaction(emoji, displayNameRef.current, localIdentityRef.current);
      pushVoHouseBanner(cheerCopy(displayNameRef.current, item.userName, emoji));
      const reaction = {
        emoji,
        fromUserId: resolveAgentUserId(),
        fromName: displayNameRef.current,
      };
      setVoHighlightTarget((prev) =>
        prev && prev.kind === item.kind && Number(prev.userId) === Number(item.userId)
          ? appendHighlightReaction(prev, reaction)
          : prev
      );
      publishVoData({
        type: "vo-kudos-react",
        kind: item.kind,
        toUserId: item.userId,
        toName: item.userName,
        fromUserId: resolveAgentUserId(),
        fromName: displayNameRef.current,
        emoji,
      });
      void floorHighlightsRefetchRef.current?.();
    },
    [publishVoData, pushVoHouseBanner, pushVoReaction, resolveAgentUserId]
  );

  const speakToHighlight = useCallback(
    (item) => {
      if (!item?.userId) return;
      if (mutedRef.current) toggleMute();
      pushVoHouseBanner(speakCopy(displayNameRef.current, item.userName));
      publishVoData({
        type: "vo-kudos-speak",
        kind: item.kind,
        toUserId: item.userId,
        toName: item.userName,
        fromUserId: resolveAgentUserId(),
        fromName: displayNameRef.current,
      });
      setVoHighlightTarget(null);
    },
    [publishVoData, pushVoHouseBanner, resolveAgentUserId]
  );

  const cafeWithHighlight = useCallback(
    (item) => {
      if (!item?.userId) return;
      setVoHighlightTarget(null);
      void openVoPrivateDialog({
        participantUserIds: [item.userId],
        title: `Café com ${voFloorFirstName(item.userName)}`,
      });
    },
    [openVoPrivateDialog]
  );

  const joinVoFloorHuddle = useCallback(
    async (huddle) => {
      setVoFloorHuddleActingId(huddle.videoRoomId || huddle.id);
      setScreenError("");
      try {
        const res = await joinVirtualOfficeHuddle(huddle.videoRoomId || huddle.id);
        const data = res?.data?.data || res?.data || {};
        if (data.status === "joined" && data.joinUrl) {
          publishVirtualOfficePauseTracks({ title: huddle.title || "Sala do escritório" });
          openAgentVideoJoinUrl(data.joinUrl, {
            context: huddle.visibility === "public" ? "vo_huddle" : "vo_private",
            name: displayName || "",
          });
        }
        await refreshVoFloorHuddles();
      } catch (e) {
        setScreenError(e?.response?.data?.message || e?.message || "Não foi possível entrar na sala.");
      } finally {
        setVoFloorHuddleActingId(null);
      }
    },
    [displayName, refreshVoFloorHuddles]
  );

  useEffect(() => {
    if (phase !== "incall" || !isVoSideRoomCallContext(callContext)) return undefined;
    void refreshVoHuddleRequests();
    const id = setInterval(() => void refreshVoHuddleRequests(), 8000);
    return () => clearInterval(id);
  }, [phase, callContext, refreshVoHuddleRequests]);

  useEffect(() => {
    if (phase !== "incall" || !isVoFloorForHuddles) {
      setVoFloorHuddles([]);
      setVoFloorPresence([]);
      return undefined;
    }
    void refreshVoFloorHuddles();
    const id = setInterval(() => void refreshVoFloorHuddles(), voPresencePollMs(voTwilioHeld));
    return () => clearInterval(id);
  }, [phase, isVoFloorForHuddles, refreshVoFloorHuddles, voTwilioHeld]);

  useEffect(() => {
    if (phase !== "incall" || !isVoFloorForHuddles) return undefined;
    const beat = () => {
      if (voLeaveCompletedRef.current) return;
      void patchVirtualOfficePresence({}).catch(() => {});
    };
    beat();
    const id = window.setInterval(beat, VO_PRESENCE_HEARTBEAT_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") beat();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [phase, isVoFloorForHuddles]);

  useEffect(() => {
    if (phase !== "incall" || !isVoSideRoomCallContext(callContext)) return undefined;
    const voId =
      Number(virtualOfficeRoomId) ||
      Number(readVirtualOfficeSession()?.roomId) ||
      0;
    if (!(voId > 0)) return undefined;
    const beat = () => {
      void patchVirtualOfficePresence({}).catch(() => {});
    };
    beat();
    const id = window.setInterval(beat, VO_PRESENCE_HEARTBEAT_MS);
    return () => {
      window.clearInterval(id);
      publishVirtualOfficeResumeTracks();
      void patchVirtualOfficePresence({ status: "present" }).catch(() => {});
    };
  }, [phase, callContext, virtualOfficeRoomId]);

  useEffect(() => {
    if (phase !== "incall" || !isVoSideRoomCallContext(callContext)) return;
    const voId =
      Number(virtualOfficeRoomId) ||
      Number(readVirtualOfficeSession()?.roomId) ||
      0;
    const videoId = Number(twilioDbRoomId);
    if (!(voId > 0) || !(videoId > 0)) return;
    const kind = String(callContext || "vo_huddle").toLowerCase();
    const title = String(channelName || "Sala do escritório").trim() || "Sala do escritório";
    void patchVirtualOfficePresenceOrRejoin(voId, {
      status: "in_other_meeting",
      otherMeetingTitle: title,
      otherMeetingRef: { videoRoomId: videoId, kind },
    }).catch(() => {});
  }, [phase, callContext, virtualOfficeRoomId, twilioDbRoomId, channelName]);

  useEffect(() => {
    if (!socket || phase !== "incall" || !isVoFloorForHuddles) return undefined;
    const onVideoRoom = (raw) => {
      let payload = raw;
      if (typeof raw === "string") {
        try {
          payload = JSON.parse(raw);
        } catch {
          payload = null;
        }
      }
      const action = String(payload?.action || "");
      if (action === "vo_presence") {
        void refreshVoFloorHuddles();
        return;
      }
      if (action !== "vo_floor_signal") return;
      const signal = payload?.extra?.signal || payload?.signal;
      if (!signal || typeof signal !== "object") return;
      if (signal.type === "vo-twilio-wake") {
        voWakeUntilRef.current = Date.now() + 20000;
        if (!roomRef.current && !voPausedForOtherMeeting) {
          void reconnectVoFloorKeepPresence();
        }
        return;
      }
      if (signal.type === "vo-arrived") {
        void refreshVoFloorHuddles();
      }
      handleIncomingChatPayloadRef.current?.(signal);
    };
    socket.on("video_room", onVideoRoom);
    return () => socket.off("video_room", onVideoRoom);
  }, [isVoFloorForHuddles, phase, reconnectVoFloorKeepPresence, refreshVoFloorHuddles, socket, voPausedForOtherMeeting]);

  useEffect(() => {
    if (phase !== "incall") return undefined;
    const id = window.setInterval(() => {
      void loadChatHistory({ silent: true });
    }, 20000);
    return () => window.clearInterval(id);
  }, [phase, loadChatHistory]);

  // pagehide/webhook podem ter fechado a sessão do escritório sem sair do vídeo.
  useEffect(() => {
    if (phase !== "incall" || !isVoFloorForHuddles) return undefined;
    const voId =
      Number(virtualOfficeRoomId) ||
      Number(virtualOfficeRoomIdRef.current) ||
      Number(readVirtualOfficeSession()?.roomId) ||
      0;
    if (voId <= 0) return undefined;
    let cancelled = false;
    void joinVirtualOfficeRoom(voId, {
      deviceInfo: {
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        source: "virtual_office_video_ensure",
      },
    })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data || res?.data || {};
        if (data?.room?.nome) setVoFloorName(String(data.room.nome).trim());
        writeVirtualOfficeSession({
          roomId: voId,
          roomName: data?.room?.nome || null,
          videoToken: data?.video?.publicToken || null,
          status: "present",
          joinedAt: Date.now(),
          attendancePolicy: data?.attendancePolicy || null,
        });
        void refreshVoFloorHuddles();
      })
      .catch(() => {
        /* consentimento/rede: criar sala ainda tenta restaurar pela presença ao vivo */
      });
    return () => {
      cancelled = true;
    };
  }, [phase, isVoFloorForHuddles, refreshVoFloorHuddles, virtualOfficeRoomId]);

  useEffect(() => {
    if (voFloorName) return undefined;
    const voId =
      Number(virtualOfficeRoomId) ||
      Number(readVirtualOfficeSession()?.roomId) ||
      0;
    if (voId <= 0) return undefined;
    let cancelled = false;
    void listVirtualOfficeRooms()
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.data || res?.data || res || [];
        const list = Array.isArray(rows) ? rows : [];
        const match = list.find((row) => Number(row?.id) === voId);
        const name = String(match?.nome || "").trim();
        if (name) setVoFloorName(name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [voFloorName, virtualOfficeRoomId]);

  const resolveVoHuddleRequest = useCallback(
    async (requestId, action) => {
      const videoId = Number(twilioDbRoomId);
      if (!videoId) return;
      setVoPrivateBusy(true);
      try {
        await resolveVirtualOfficeHuddleJoinRequest(videoId, requestId, action);
        await refreshVoHuddleRequests();
      } catch (e) {
        setScreenError(e?.response?.data?.message || e?.message || "Não foi possível resolver o pedido.");
      } finally {
        setVoPrivateBusy(false);
      }
    },
    [twilioDbRoomId, refreshVoHuddleRequests]
  );

  const stopScreenShare = useCallback(async () => {
    cleanupLocalScreenShare();
    setSharingScreen(false);
    syncPresentationState();
    refreshLocalCameraPreview();
  }, [cleanupLocalScreenShare, syncPresentationState, refreshLocalCameraPreview]);

  stopScreenShareRef.current = stopScreenShare;

  const startScreenShare = useCallback(async () => {
    if (screenLocalRef.current.video) return;
    if (!roomRef.current && isVoFloorForHuddlesRef.current) {
      publishVoTwilioWake();
      await reconnectVoFloorKeepPresence();
    }
    if (!roomRef.current || screenLocalRef.current.video) return;
    if (!isScreenShareSupported()) {
      setScreenError(screenShareUnsupportedMessage());
      return;
    }
    setScreenError("");
    let videoTrack = null;
    let audioTrack = null;
    try {
      const Video = await import("twilio-video");
      const created = await createLocalScreenShareTracks(Video);
      videoTrack = created.videoTrack;
      audioTrack = created.audioTrack;
      const room = roomRef.current;
      if (!room?.localParticipant) {
        stopLocalScreenShareTracks({ videoTrack, audioTrack });
        return;
      }
      await room.localParticipant.publishTrack(videoTrack, { priority: "high" });
      if (audioTrack) {
        try {
          await room.localParticipant.publishTrack(audioTrack);
        } catch (e) {
          console.warn("[PublicVideo] screen audio:", e?.message || e);
        }
      }
      const endedHandler = () => {
        stopScreenShareRef.current?.();
      };
      try {
        videoTrack.mediaStreamTrack.addEventListener("ended", endedHandler);
      } catch {
        /* ignore */
      }
      try {
        videoTrack.once?.("stopped", endedHandler);
      } catch {
        /* ignore */
      }
      screenLocalRef.current = { video: videoTrack, audio: audioTrack, endedHandler };
      setSharingScreen(true);
      syncPresentationState();
      refreshLocalCameraPreview();
    } catch (e) {
      stopLocalScreenShareTracks({ videoTrack, audioTrack });
      const msg = friendlyScreenShareError(e);
      if (msg) setScreenError(msg);
    }
  }, [publishVoTwilioWake, reconnectVoFloorKeepPresence, refreshLocalCameraPreview, syncPresentationState]);

  const toggleScreenShare = () => {
    if (sharingScreen) stopScreenShare();
    else startScreenShare();
  };

  const leave = () => {
    leftBySelfRef.current = true;
    setEndCause("self");
    publishOtherMeetingEnded();
    publishVirtualOfficeResumeTracks();
    void patchVirtualOfficePresence({
      status: "present",
      otherMeetingTitle: null,
      otherMeetingRef: null,
    }).catch(() => {});
    const tkn = publicTokenRef.current || token;
    flushTranscript(true).finally(() => {
      if (tkn && company) {
        postPublicVideoLeave(tkn, company, {
          role: joinRole,
          displayName: displayNameRef.current || undefined,
          identity: localIdentityRef.current || undefined,
        }).catch((e) => console.warn("[PublicVideo] leave:", e?.message || e));
      }
    });
    try {
      closeVideoCallDocumentPip();
    } catch {
      /* ignore */
    }
    setPipActive(false);
    cleanupRoom();
    setPhase("ended");
  };

  const requestLeave = () => {
    if (isVirtualOfficeContext || isVirtualOfficeCallContext(callContext)) {
      setVoLeaveDialogOpen(true);
      return;
    }
    leave();
  };

  const signalLiveLeaveKeepalive = () => {
    const tkn = publicTokenRef.current || token;
    const co = companyRef.current || company;
    if (!tkn || !co) return;
    postPublicVideoLeaveKeepalive(tkn, co, {
      role: joinRoleRef.current || joinRole,
      displayName: displayNameRef.current || undefined,
      identity: localIdentityRef.current || undefined,
    });
  };

  const returnToAppNow = () => {
    if (returnFromAgentVideoToApp()) return;
    try {
      window.close();
    } catch {
      /* ignore */
    }
  };

  const closeOrReturnToApp = () => {
    if (appReturnPath) {
      returnToAppNow();
      return;
    }
    try {
      window.close();
    } catch {
      /* ignore */
    }
  };

  const dismissVoLeaveDialog = () => {
    if (voLeaveBusy) return;
    returnAfterLeaveRef.current = false;
    setVoLeaveDialogOpen(false);
  };

  const requestReturnToApp = () => {
    if (!appReturnPath) return;
    const current = phaseRef.current;
    const inLiveCall = current === "incall" || current === "connecting";
    if (
      inLiveCall &&
      (isVirtualOfficeContextRef.current || isVirtualOfficeCallContext(callContext))
    ) {
      returnAfterLeaveRef.current = true;
      setVoLeaveDialogOpen(true);
      return;
    }
    if (inLiveCall) {
      const confirmed =
        typeof window.confirm !== "function" ||
        window.confirm("Sair da chamada e voltar ao app?");
      if (!confirmed) return;
      leftBySelfRef.current = true;
      signalLiveLeaveKeepalive();
      publishOtherMeetingEnded();
      publishVirtualOfficeResumeTracks();
      patchVirtualOfficePresenceKeepalive({
        status: "present",
        otherMeetingTitle: null,
        otherMeetingRef: null,
      });
      try {
        closeVideoCallDocumentPip();
      } catch {
        /* ignore */
      }
      cleanupRoom();
      returnToAppNow();
      return;
    }
    returnToAppNow();
  };

  const confirmVirtualOfficeLeave = async (leaveKind) => {
    setVoLeaveBusy(true);
    const session = readVirtualOfficeSession();
    const roomId =
      Number(session?.roomId) ||
      Number(virtualOfficeRoomId) ||
      0;
    const kind = leaveKind === "break" ? "break" : "exit";
    const deviceInfo = { ua: navigator.userAgent, source: "virtual_office_video_leave" };
    try {
      if (roomId > 0) {
        const res = await leaveVirtualOfficeRoom(roomId, { leaveKind: kind, deviceInfo });
        const payload = res?.data?.data || res?.data || {};
        if (kind === "break" && payload.stayed) {
          const cur = readVirtualOfficeSession();
          if (cur) writeVirtualOfficeSession({ ...cur, status: "lunch" });
          publishVirtualOfficePauseTracks({ title: voCommStatusPauseTitle("lunch") });
          setMediaSnack("Intervalo registrado. Você continua no escritório em horário de almoço.");
          void refreshVoFloorHuddles();
          void voJornadaAlerts.refresh();
          setVoLeaveBusy(false);
          setVoLeaveDialogOpen(false);
          return;
        }
      }
    } catch (e) {
      console.warn("[PublicVideo] VO leave punch:", e?.message || e);
      if (kind === "break") {
        setVoLeaveBusy(false);
        setMediaSnack("Não foi possível registrar o intervalo. Tente de novo.");
        return;
      }
      // Best-effort se a chamada normal falhar (rede); evita sessão órfã no cockpit.
      if (roomId > 0) {
        leaveVirtualOfficeRoomKeepalive(roomId, { leaveKind: kind, deviceInfo });
      }
    }
    voLeaveCompletedRef.current = true;
    writeVirtualOfficeSession(null);
    setVoLeaveBusy(false);
    setVoLeaveDialogOpen(false);
    leave();
    if (returnAfterLeaveRef.current) {
      returnAfterLeaveRef.current = false;
      signalLiveLeaveKeepalive();
      returnToAppNow();
    }
  };

  // F5 / fechar aba: desconecta o Twilio (Meet some da sala; não deixa ghost).
  // Só beforeunload — pagehide também dispara ao trocar de aba no Safari/Chrome
  // e derrubaria a chamada. O piso VO já tem punch próprio.
  useEffect(() => {
    const disconnectLiveRoomOnUnload = () => {
      if (isVirtualOfficeContextRef.current) return;
      const current = phaseRef.current;
      if (current !== "incall" && current !== "connecting") return;
      try {
        roomRef.current?.disconnect?.();
      } catch {
        /* ignore */
      }
      if (!leftBySelfRef.current) {
        const tkn = publicTokenRef.current || token;
        const co = companyRef.current;
        if (tkn && co) {
          postPublicVideoLeaveKeepalive(tkn, co, {
            role: joinRoleRef.current,
            displayName: displayNameRef.current || undefined,
            identity: localIdentityRef.current || undefined,
          });
        }
      }
      publishOtherMeetingEnded();
      publishVirtualOfficeResumeTracks();
      patchVirtualOfficePresenceKeepalive({
        status: "present",
        otherMeetingTitle: null,
        otherMeetingRef: null,
      });
    };
    window.addEventListener("beforeunload", disconnectLiveRoomOnUnload);
    return () => window.removeEventListener("beforeunload", disconnectLiveRoomOnUnload);
  }, [token]);

  // Fechar aba/janela do vídeo: registra saída com motivo "fechou a janela".
  // No piso, pagehide também dispara ao trocar de aba — isso não é saída.
  useEffect(() => {
    const punchOnWindowClose = (ev) => {
      if (ev?.persisted) return;
      if (!isVirtualOfficeContextRef.current) return;
      if (shouldKeepVoOfficeOnPageHide({ isVirtualOffice: true })) return;
      if (voLeaveCompletedRef.current) return;
      const session = readVirtualOfficeSession();
      const roomId =
        Number(session?.roomId) ||
        Number(virtualOfficeRoomIdRef.current) ||
        0;
      if (roomId <= 0) return;
      voLeaveCompletedRef.current = true;
      try {
        roomRef.current?.disconnect?.();
      } catch {
        /* ignore */
      }
      leaveVirtualOfficeRoomKeepalive(roomId, {
        leaveKind: "window_closed",
        deviceInfo: {
          ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
          source: "virtual_office_video_window_closed",
          leaveReason: "window_closed",
          leaveReasonLabel: "Usuário fechou a janela",
        },
      });
      writeVirtualOfficeSession(null);
    };
    window.addEventListener("pagehide", punchOnWindowClose);
    return () => window.removeEventListener("pagehide", punchOnWindowClose);
  }, []);

  // Gesto de voltar do sistema: a sala foi aberta na mesma janela, então o
  // histórico ainda tem a tela anterior. Registra a saída antes de desmontar.
  useEffect(() => {
    if (!appReturnPath) return undefined;
    const onPop = () => {
      const current = phaseRef.current;
      if (current !== "incall" && current !== "connecting") return;
      if (leftBySelfRef.current) return;
      leftBySelfRef.current = true;
      signalLiveLeaveKeepalive();
      if (
        isVirtualOfficeContextRef.current &&
        !voLeaveCompletedRef.current &&
        !shouldKeepVoOfficeOnPageHide({ isVirtualOffice: true })
      ) {
        const session = readVirtualOfficeSession();
        const roomId =
          Number(session?.roomId) ||
          Number(virtualOfficeRoomIdRef.current) ||
          0;
        if (roomId > 0) {
          voLeaveCompletedRef.current = true;
          leaveVirtualOfficeRoomKeepalive(roomId, {
            leaveKind: "window_closed",
            deviceInfo: {
              ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
              source: "virtual_office_video_back",
              leaveReason: "window_closed",
              leaveReasonLabel: "Usuário saiu da tela de vídeo",
            },
          });
          writeVirtualOfficeSession(null);
        }
      }
      try {
        roomRef.current?.disconnect?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [appReturnPath]);

  const canEndForEveryone =
    !isVirtualOfficeContext &&
    !isVirtualOfficeCallContext(callContext) &&
    (joinRole === "agent" || isTeamCallContext(callContext) || isAgendaCallContext(callContext));

  const endForEveryone = () => {
    if (!canEndForEveryone) {
      requestLeave();
      return;
    }
    leftBySelfRef.current = true;
    setEndCause("ended_for_all");
    flushTranscript(true).finally(() => {
      // Em team, role costuma ser "guest"; não enviar guest no atendimento (API 403).
      // Sem role explícita → API trata como encerramento da sala (team/host).
      requestCallEnd({
        endedByRole: joinRole === "agent" ? "agent" : undefined,
      });
    });
    try {
      closeVideoCallDocumentPip();
    } catch {
      /* ignore */
    }
    setPipActive(false);
    cleanupRoom();
    setPhase("ended");
  };

  const openCallPip = useCallback(
    async ({ fromVisibility = false } = {}) => {
      if (!pipSupported || phase !== "incall") return false;
      if (pipOpeningRef.current) return false;
      if (isDocumentPipOpen()) {
        setPipActive(true);
        return true;
      }
      pipOpeningRef.current = true;
      setPipError("");
      try {
        const pipWindow = await openVideoCallDocumentPip({
          getRoom: () => roomRef.current,
          getState: () => ({
            muted: mutedRef.current,
            camOff: camOffRef.current,
            requireCamera: requireCameraRef.current,
            sharingScreen: sharingScreenRef.current,
            displayName: displayNameRef.current,
            mirrorLocal: shouldMirrorLocalPreview(bgModeRef.current),
            roomLabel: roomLabelRef.current,
            featuredSid: pinnedSidRef.current || dominantSidRef.current || null,
            nameForIdentity: (identity) =>
              identityNamesRef.current.get(String(identity || "").trim()) || null,
          }),
          actions: {
            toggleMute: () => {
              void setLocalMicEnabled(mutedRef.current);
            },
            toggleCam: () => {
              void setLocalCameraEnabled(camOffRef.current);
            },
            toggleScreenShare: () => {
              // Dispara pelo ref da UI principal (mesma lógica do botão).
              if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("xpro-video-pip-screen"));
              }
            },
            leave: () => {
              if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("xpro-video-pip-leave"));
              }
            },
            focusOpener: () => {
              try {
                window.focus();
              } catch {
                /* ignore */
              }
            },
          },
          copy: {
            brandName: brandName || "Vídeo XPro",
            unsupportedMessage: tChat("video.pipUnsupported"),
          },
        });
        setPipActive(true);
        const onPipGone = () => setPipActive(false);
        try {
          pipWindow?.addEventListener?.("pagehide", onPipGone);
          pipWindow?.addEventListener?.("unload", onPipGone);
        } catch {
          /* ignore */
        }
        return true;
      } catch (e) {
        if (!fromVisibility) {
          setPipError(
            e?.code === "video.pip_unsupported"
              ? tChat("video.pipUnsupported")
              : e?.message || tChat("video.pipOpenFailed")
          );
        }
        setPipActive(false);
        return false;
      } finally {
        pipOpeningRef.current = false;
      }
    },
    [pipSupported, phase, brandName, tChat, publishMediaState, setLocalCameraEnabled, setLocalMicEnabled]
  );

  const closeCallPip = useCallback(() => {
    closeVideoCallDocumentPip();
    setPipActive(false);
  }, []);

  const toggleCallPip = useCallback(() => {
    if (isDocumentPipOpen() || pipActive) {
      closeCallPip();
      return;
    }
    openCallPip();
  }, [pipActive, closeCallPip, openCallPip]);

  // Eventos vindos da janela PiP (actions)
  useEffect(() => {
    leaveRef.current = requestLeave;
  });

  useEffect(() => {
    toggleScreenShareRef.current = toggleScreenShare;
  });

  useEffect(() => {
    const onLeave = () => leaveRef.current?.();
    const onScreen = () => toggleScreenShareRef.current?.();
    window.addEventListener("xpro-video-pip-leave", onLeave);
    window.addEventListener("xpro-video-pip-screen", onScreen);
    return () => {
      window.removeEventListener("xpro-video-pip-leave", onLeave);
      window.removeEventListener("xpro-video-pip-screen", onScreen);
    };
  }, []);

  // Ao sair da aba: abre PiP automaticamente (quando o browser permitir).
  // No piso o escritório continua na aba; PiP automático fazia parecer que a pessoa saiu.
  useEffect(() => {
    if (phase !== "incall" || !pipSupported || isVoFloorForHuddles) return undefined;
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        openCallPip({ fromVisibility: true });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [phase, pipSupported, openCallPip, isVoFloorForHuddles]);

  const handleRetry = () => {
    const action = errorGuide?.primaryAction;
    if (action === "close") {
      closeOrReturnToApp();
      return;
    }
    if ((action === "retry" || errorCanRetryJoin) && company && action !== "reload") {
      setErrorMsg("");
      setErrorGuide(null);
      setPhase("name");
      return;
    }
    window.location.reload();
  };

  const handleErrorSecondary = () => {
    window.location.reload();
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(guestShareUrl || shareUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const shareInviteNative = async () => {
    const url = guestShareUrl || shareUrl;
    if (!url) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: roomLabel || "Chamada de vídeo",
          url,
          text: teamCall
            ? `Entre na chamada da equipe: ${url}`
            : `Entre na chamada de vídeo: ${url}`,
        });
        return;
      }
    } catch {
      /* fall through to copy */
    }
    await copyShareLink();
  };

  const useInviteInComposer = () => {
    const url = guestShareUrl || shareUrl;
    if (!url) return;
    const cta = teamCall ? "Entrar na chamada da equipe" : "Entrar na chamada";
    const body = teamCall
      ? "Entre na chamada da equipe:"
      : "Toque no botão abaixo para entrar na chamada de vídeo com nossa equipe.";
    window.dispatchEvent(
      new CustomEvent("xpro-chat-compose", {
        detail: {
          conversationId: leadId != null ? Number(leadId) : undefined,
          setText: body,
          joinUrl: url,
          buttonMessage: `${cta}|${url}`,
        },
      })
    );
    setInviteOpen(false);
    setPrivacySnack("Pronto para colar no campo de envio");
  };

  const toggleWaitSound = () => {
    const next = !waitSoundMuted;
    setWaitSoundMuted(next);
    setWaitSoundMutedState(next);
  };

  const onSelectAudioDevice = (deviceId) => {
    const id = String(deviceId || "");
    setAudioDeviceId(id);
    setPreferredAudioDeviceId(id);
  };

  const onSelectVideoDevice = (deviceId) => {
    const id = String(deviceId || "");
    setVideoDeviceId(id);
    setPreferredVideoDeviceId(id);
  };

  const networkQualityLabel = useMemo(() => {
    if (networkQuality == null || Number.isNaN(Number(networkQuality))) return null;
    const n = Number(networkQuality);
    if (n <= 0) return { text: "Reconectando…", color: "#fdd663" };
    if (n <= 2) return { text: "Conexão ruim", color: "#f28b82" };
    if (n === 3) return { text: "Conexão instável", color: "#fdd663" };
    return { text: "Conexão boa", color: "#81c995" };
  }, [networkQuality]);

  // Som suave enquanto convidado aguarda sozinho
  useEffect(() => {
    if (phase !== "incall" || remoteCount > 0 || !isGuestWaiter || waitSoundMuted) {
      return undefined;
    }
    const tick = () => {
      if (!isWaitSoundMuted()) playWaitBeep();
    };
    const first = setTimeout(tick, 2500);
    const id = setInterval(tick, 18000);
    return () => {
      clearTimeout(first);
      clearInterval(id);
    };
  }, [phase, remoteCount, isGuestWaiter, waitSoundMuted]);

  // Atalhos: M microfone, D áudio dos colegas, C câmera, Esc foca sair
  useEffect(() => {
    if (phase !== "incall" && phase !== "name") return undefined;
    const onKey = (e) => {
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "Escape") {
        leaveButtonRef.current?.focus?.();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = String(e.key || "").toLowerCase();
      if (k === "m") {
        e.preventDefault();
        toggleMute();
      } else if (
        k === "d" &&
        (isVirtualOfficeContextRef.current || isAnyVirtualOfficeVideoContext(callContext))
      ) {
        e.preventDefault();
        toggleSpeakerMute();
      } else if (k === "c" && !requireCameraRef.current) {
        e.preventDefault();
        toggleCam();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    if (!captionsOn) return undefined;
    if (liveCaption) return undefined;
    setCaptionHint("Aguardando legendas…");
    const id = setTimeout(() => {
      setCaptionHint((prev) =>
        liveCaption ? prev : "Legendas em breve — ative a transcrição da sala se disponível."
      );
    }, 12000);
    return () => clearTimeout(id);
  }, [captionsOn, liveCaption]);

  const initials = String(displayName || "Você")
    .trim()
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase() || "")
    .slice(0, 2)
    .join("") || "?";

  const showSpeakerMute =
    isVirtualOfficeContext || isAnyVirtualOfficeVideoContext(callContext);

  const HeaderBar = ({ dark = false }) => (
    <Box
      component="header"
      sx={{
        px: { xs: 1.25, md: 3 },
        py: { xs: 1, md: 1.5 },
        ...(appReturnPath
          ? {
              pt: "max(12px, env(safe-area-inset-top))",
              pl: "max(8px, env(safe-area-inset-left))",
            }
          : null),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        borderBottom: dark ? "none" : `1px solid ${alpha("#000", 0.08)}`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        {appReturnPath ? (
          <Button
            onClick={requestReturnToApp}
            startIcon={<ArrowBackIcon />}
            aria-label={
              phase === "incall" || phase === "connecting"
                ? "Sair da chamada e voltar ao app"
                : "Voltar ao app"
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              minWidth: 0,
              px: 1,
              flexShrink: 0,
              color: dark ? "#e8eaed" : "text.primary",
            }}
          >
            Voltar
          </Button>
        ) : null}
        <Box
          component="img"
          src={dark ? brand?.assets?.logo?.dark || logoSrc : logoSrc}
          alt={brandName}
          sx={{ height: 28, objectFit: "contain" }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            title={voFloorScene ? voFloorName || "Escritório" : `${brandName} Video`}
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.92rem", md: "1.05rem" },
              color: dark ? "#e8eaed" : "text.primary",
              lineHeight: 1.2,
            }}
          >
            {voFloorScene ? voFloorName || "Escritório" : `${brandName} Video`}
          </Typography>
          {!dark ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {roomLabel}
            </Typography>
          ) : crowdWarn ? (
            <Typography
              variant="caption"
              sx={{ color: "#fdd663", display: "block", lineHeight: 1.3 }}
              noWrap
            >
              {crowdCapacityCopy({ callContext }).banner}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexShrink: 0 }}>
        {dark && recordingEnabled ? (
          <Chip
            size="small"
            label="REC"
            aria-label="Gravação ativa nesta chamada"
            sx={{
              height: 24,
              fontWeight: 800,
              fontSize: "0.7rem",
              letterSpacing: "0.06em",
              bgcolor: alpha("#ea4335", 0.95),
              color: "#fff",
              "& .MuiChip-label": { px: 1 },
            }}
          />
        ) : null}
        {dark && networkQualityLabel && !voFloorScene ? (
          <Chip
            size="small"
            label={networkQualityLabel.text}
            aria-label={networkQualityLabel.text}
            sx={{
              height: 24,
              fontWeight: 600,
              fontSize: "0.72rem",
              bgcolor: alpha(networkQualityLabel.color, 0.18),
              color: networkQualityLabel.color,
              border: `1px solid ${alpha(networkQualityLabel.color, 0.45)}`,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        ) : null}
        {dark && speakerMuted && showSpeakerMute ? (
          <Chip
            size="small"
            icon={<VolumeOffOutlinedIcon sx={{ "&&": { fontSize: 14 } }} />}
            label="Áudio off"
            aria-label="Áudio do escritório desligado"
            sx={{
              height: 24,
              fontWeight: 700,
              fontSize: "0.72rem",
              bgcolor: alpha("#ea4335", 0.18),
              color: "#f28b82",
              border: `1px solid ${alpha("#ea4335", 0.4)}`,
              "& .MuiChip-label": { px: 0.75 },
              "& .MuiChip-icon": { color: "#f28b82" },
            }}
          />
        ) : null}
        {dark && voFloorScene ? (
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <Typography sx={{ color: "#e8eaed", fontSize: { xs: "0.78rem", md: "0.82rem" }, fontWeight: 700 }} noWrap>
              {voFloorClock}
            </Typography>
            <Typography
              sx={{
                display: { xs: "none", sm: "block" },
                color: "#c9c0b4",
                fontSize: "0.68rem",
                fontWeight: 600,
              }}
              noWrap
            >
              {voFloorSceneTokens(voFloorDaypart()).label}
            </Typography>
          </Box>
        ) : null}
        {dark && !voFloorScene ? (
          <Typography sx={{ color: "#9aa0a6", fontSize: "0.85rem", maxWidth: 180 }} noWrap>
            {roomLabel}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );

  const mediaCopy = mediaControlCopy({
    camOff,
    muted,
    requireCamera,
    teamJoinMutedHint: teamCall && muted,
    virtualOffice: isVirtualOfficeContext || isVirtualOfficeCallContext(callContext),
    speakerMuted,
  });
  const mediaControls = (
    <Stack direction="row" spacing={1.25} justifyContent="center" alignItems="center">
      <ControlButton
        activeOff={muted}
        title={mediaCopy.micTitle}
        onClick={toggleMute}
        disabled={micBusy}
      >
        {muted ? <MicOffIcon /> : <MicIcon />}
      </ControlButton>
      {requireCamera ? null : (
      <ControlButton
        activeOff={camOff}
        title={mediaCopy.camTitle}
        onClick={toggleCam}
        disabled={camBusy}
      >
        {camOff ? <VideocamOffIcon /> : <VideocamIcon />}
      </ControlButton>
      )}
      {showSpeakerMute ? (
        <ControlButton
          activeOff={speakerMuted}
          title={mediaCopy.speakerTitle}
          onClick={toggleSpeakerMute}
        >
          {speakerMuted ? <VolumeOffOutlinedIcon /> : <VolumeUpOutlinedIcon />}
        </ControlButton>
      ) : null}
      <VideoBackgroundControlButton
        active={bgMode !== "none"}
        title={
          bgSupported
            ? "Fundo da câmera"
            : "Efeitos de fundo não suportados neste navegador"
        }
        disabled={camOff || bgBusy}
        onClick={(e) => setBgAnchorEl(e.currentTarget)}
      />
    </Stack>
  );

  const screenShareButton = (
    <ControlButton
      activeOn={sharingScreen}
      disabled={!screenSupported}
      title={
        !screenSupported
          ? screenShareUnsupportedMessage()
          : sharingScreen
            ? "Parar de compartilhar a tela"
            : "Compartilhar tela"
      }
      onClick={toggleScreenShare}
    >
      {sharingScreen ? <StopScreenShareIcon /> : <ScreenShareIcon />}
    </ControlButton>
  );

  const isVoFloorContext =
    isVirtualOfficeContext || isVirtualOfficeCallContext(callContext);
  const isVoSideCtx = isVoSideRoomCallContext(callContext);
  // Floor e salas internas do VO: sem toggle Team de privacy.
  const privacyButton =
    joinRole === "agent" && !isVoFloorContext && !isVoSideCtx ? (
      <ControlButton
        activeOn={isPrivateRoom}
        disabled={privacyBusy}
        title={isPrivateRoom ? tTeam("activeVideo.makeOpen") : tTeam("activeVideo.makePrivate")}
        onClick={toggleRoomPrivacy}
      >
        {isPrivateRoom ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
      </ControlButton>
    ) : null;

  const voPrivateButton =
    joinRole === "agent" && isVoFloorContext && phase === "incall" ? (
      <ControlButton
        disabled={voPrivateBusy}
        title="Abrir uma sala no escritório"
        onClick={() => void openVoPrivateDialog()}
      >
        <MeetingRoomOutlinedIcon />
      </ControlButton>
    ) : null;

  const voStatusButton =
    isVoFloorForHuddles && phase === "incall" ? (
      <ControlButton
        activeOn={selfStatus !== "present" && selfStatus !== "in_other_meeting"}
        disabled={selfStatus === "in_other_meeting"}
        title={
          selfStatus === "in_other_meeting"
            ? "Status bloqueado enquanto você está em outra reunião"
            : `Status: ${voCommStatusLabel(selfStatus)}`
        }
        onClick={(e) => setVoStatusMenuAnchor(e.currentTarget)}
      >
        {selfStatus === "focus" ? (
          <CenterFocusStrongOutlinedIcon />
        ) : selfStatus === "lunch" ? (
          <RestaurantOutlinedIcon />
        ) : selfStatus === "snack" ? (
          <LocalCafeOutlinedIcon />
        ) : selfStatus === "away" ? (
          <PersonOffOutlinedIcon />
        ) : selfStatus === "in_other_meeting" ? (
          <EventBusyOutlinedIcon />
        ) : (
          <GroupsOutlinedIcon />
        )}
      </ControlButton>
    ) : null;

  const voReactionButton =
    isVoFloorForHuddles && phase === "incall" ? (
      <ControlButton
        title="Reagir no piso"
        onClick={(e) => setVoReactionMenuAnchor(e.currentTarget)}
      >
        <EmojiEmotionsOutlinedIcon />
      </ControlButton>
    ) : null;

  const voHuddleRequestsButton =
    joinRole === "agent" && isVoSideCtx && phase === "incall" && voHuddleRequests.length ? (
      <ControlButton
        disabled={voPrivateBusy}
        title="Pedidos para entrar nesta sala"
        onClick={() => setVoHuddleRequestsOpen(true)}
      >
        <PersonAddAlt1OutlinedIcon />
      </ControlButton>
    ) : null;

  const voAdmitButton =
    joinRole === "agent" && isVoSideCtx && phase === "incall" ? (
      <ControlButton
        disabled={voPrivateBusy}
        title="Incluir colega do escritório nesta conversa"
        onClick={() => void openVoPrivateDialog()}
      >
        <PersonAddAlt1OutlinedIcon />
      </ControlButton>
    ) : null;

  const chatButton =
    phase === "incall" ? (
      <VideoInCallChatButton
        open={chatOpen}
        unreadCount={chatUnread}
        title={tChat("video.inCallChat")}
        onClick={() => (chatOpen ? closeChatPanel() : openChatPanel())}
      />
    ) : null;

  const pipButton =
    phase === "incall" && pipSupported ? (
      <ControlButton
        activeOn={pipActive}
        title={pipActive ? tChat("video.pipClose") : tChat("video.pipOpen")}
        onClick={toggleCallPip}
      >
        <PictureInPictureAltIcon />
      </ControlButton>
    ) : null;

  const peopleButton =
    phase === "incall" ? (
      <ControlButton title="Pessoas" onClick={() => setPeopleOpen(true)}>
        <PeopleOutlineIcon />
      </ControlButton>
    ) : null;

  const captionsButton = (
    <ControlButton
      activeOn={captionsOn}
      title={captionsOn ? "Ocultar legendas" : "Legendas"}
      onClick={() => {
        void requestLiveCaptions(!captionsOn);
      }}
    >
      {captionsOn ? <ClosedCaptionOutlinedIcon /> : <ClosedCaptionDisabledOutlinedIcon />}
    </ControlButton>
  );

  const endForEveryoneButton = canEndForEveryone ? (
    <ControlButton danger title="Encerrar para todos" onClick={endForEveryone}>
      <CallEndIcon />
    </ControlButton>
  ) : null;

  const closeMoreMenu = () => setMoreMenuAnchor(null);

  const backgroundPicker = (
    <VideoBackgroundPicker
      open={Boolean(bgAnchorEl)}
      anchorEl={bgAnchorEl}
      onClose={() => setBgAnchorEl(null)}
      mode={bgMode}
      busy={bgBusy}
      supported={bgSupported}
      dark={phase === "incall"}
      themePreset={themePreset}
      onSelectNone={() => {
        setCameraBackground({ mode: "none" });
        setBgAnchorEl(null);
      }}
      onSelectBlur={() => {
        setCameraBackground({ mode: "blur" });
        setBgAnchorEl(null);
      }}
      onSelectPreset={(preset) => {
        setCameraBackground({ mode: "image", presetId: preset.id, preset });
        setBgAnchorEl(null);
      }}
      onSelectCustomFile={async (file) => {
        try {
          const image = await loadImageFromFile(file);
          await setCameraBackground({ mode: "image", image });
          setBgAnchorEl(null);
        } catch (e) {
          setBgError(e?.message || "Não foi possível carregar a imagem.");
        }
      }}
    />
  );

  // —— Loading / connecting ——
  if (phase === "loading" || phase === "connecting") {
    const loadCopy = connectingCopy({ callContext, joinRole, phase });
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          bgcolor: "#f8f9fa",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <HeaderBar />
        <Box sx={{ flex: 1, display: "grid", placeItems: "center", px: 2 }}>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: "center", maxWidth: 360 }}>
            <CircularProgress size={36} sx={{ color: primary }} />
            <Typography variant="h6" fontWeight={700}>
              {loadCopy.title}
            </Typography>
            <Typography color="text.secondary">{loadCopy.body}</Typography>
          </Stack>
        </Box>
      </Box>
    );
  }

  // —— Error ——
  if (phase === "error") {
    const errCopy = errorScreenCopy({ callContext, joinRole });
    const title = privateForbidden
      ? errorGuide?.title || tTeam("activeVideo.forbiddenTitle")
      : errorGuide?.title || errCopy.title;
    const body = privateForbidden
      ? errorMsg || errorGuide?.body || tTeam("activeVideo.forbiddenBody")
      : errorMsg || errorGuide?.body || errCopy.fallbackBody;
    const nextStep = !privateForbidden && errorGuide?.action ? errorGuide.action : null;
    const supportCode = errorGuide?.supportCode || null;
    const primaryLabel = privateForbidden
      ? "Fechar"
      : errorGuide?.primaryLabel ||
        (errorCanRetryJoin ? "Tentar novamente" : "Recarregar página");
    const showReloadSecondary =
      !privateForbidden &&
      errorGuide?.primaryAction !== "reload";
    return (
      <Box sx={{ minHeight: "100dvh", bgcolor: "#f8f9fa", display: "flex", flexDirection: "column" }}>
        <HeaderBar />
        <Box sx={{ flex: 1, display: "grid", placeItems: "center", px: 2, py: 4 }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 440,
              bgcolor: "#fff",
              borderRadius: 3,
              p: 3.5,
              boxShadow: "0 1px 3px rgba(60,64,67,0.15), 0 4px 12px rgba(60,64,67,0.08)",
              textAlign: "center",
            }}
          >
            <Avatar sx={{ bgcolor: alpha("#d93025", 0.12), color: "#d93025", width: 52, height: 52, mx: "auto", mb: 2 }}>
              {privateForbidden ? <LockOutlinedIcon /> : <ErrorOutlineIcon />}
            </Avatar>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {title}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: nextStep ? 1.5 : 3, lineHeight: 1.55 }}>
              {body}
            </Typography>
            {nextStep ? (
              <Typography color="text.primary" sx={{ mb: 3, lineHeight: 1.55, fontWeight: 600 }}>
                {nextStep}
              </Typography>
            ) : null}
            {supportCode ? (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: "block", mb: 2.5, fontFamily: "ui-monospace, monospace" }}
              >
                Código: {supportCode}
              </Typography>
            ) : null}
            <Stack spacing={1.25}>
              {privateForbidden ? (
                <Button
                  variant="contained"
                  onClick={closeOrReturnToApp}
                  sx={{ textTransform: "none", fontWeight: 700, py: 1.2, borderRadius: 999, bgcolor: primary }}
                >
                  {appReturnPath ? "Voltar ao app" : "Fechar"}
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={handleRetry}
                    sx={{ textTransform: "none", fontWeight: 700, py: 1.2, borderRadius: 999, bgcolor: primary }}
                  >
                    {primaryLabel}
                  </Button>
                  {showReloadSecondary ? (
                    <Button onClick={handleErrorSecondary} sx={{ textTransform: "none" }}>
                      Recarregar página
                    </Button>
                  ) : null}
                </>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  // —— Ended ——
  if (phase === "ended") {
    const endedCopy = endedCallCopy({
      callContext,
      joinRole,
      endCause: endCause || "self",
      fromLobbyEntry,
      remoteRole: remoteLeftRole,
      attendancePolicy: readVirtualOfficeSession()?.attendancePolicy || null,
    });
    const preJoinEnded = preJoinCopy({
      callContext,
      joinRole,
      fromLobbyEntry,
      isLobbyGuestForm: false,
      roomLabel,
    });
    return (
      <Box sx={{ minHeight: "100dvh", bgcolor: "#f8f9fa", display: "flex", flexDirection: "column" }}>
        <HeaderBar />
        <Box sx={{ flex: 1, display: "grid", placeItems: "center", px: 2, py: 4 }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 440,
              bgcolor: "#fff",
              borderRadius: 3,
              p: 3.5,
              boxShadow: "0 1px 3px rgba(60,64,67,0.15), 0 4px 12px rgba(60,64,67,0.08)",
              textAlign: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {endedCopy.title}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.55 }}>
              {endedCopy.body}
            </Typography>
            <Stack spacing={1.25} alignItems="center">
              <Button
                variant="contained"
                onClick={async () => {
                  setErrorMsg("");
                  endRequestedRef.current = false;
                  leftBySelfRef.current = false;
                  lastRemoteLeftRoleRef.current = null;
                  setRemoteLeftRole(null);
                  if (mode === "lobby" || !token || !company) {
                    setEndCause(null);
                    setPhase("name");
                    return;
                  }
                  try {
                    const meta = await fetchPublicVideoRoom(token, company, { live: false });
                    if (String(meta?.status || "").toLowerCase() === "ended") {
                      setEndCause("already_ended");
                      setPhase("ended");
                      return;
                    }
                    if (meta?.callContext) {
                      setCallContext(
                        resolveCallContext({
                          apiContext: meta.callContext,
                          searchParam: contextFromQuery,
                          teamSpaceId: meta.teamSpaceId,
                          fromLobby: false,
                        })
                      );
                    }
                    setEndCause(null);
                    setPhase("name");
                  } catch (e) {
                    const errData = e?.response?.data?.data || e?.response?.data || {};
                    if (e?.response?.status === 410 || errData?.status === "ended") {
                      setEndCause("already_ended");
                      setPhase("ended");
                      return;
                    }
                    setEndCause(null);
                    setPhase("name");
                  }
                }}
                sx={{ textTransform: "none", fontWeight: 700, py: 1.2, borderRadius: 999, bgcolor: primary }}
              >
                {preJoinEnded.rejoinButton}
              </Button>
              {appReturnPath ? (
                <Button
                  variant="outlined"
                  onClick={requestReturnToApp}
                  startIcon={<ArrowBackIcon />}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
                >
                  Voltar ao app
                </Button>
              ) : null}
              {lobbyReturnPath && joinRole !== "agent" ? (
                <Button
                  variant="text"
                  onClick={() => {
                    window.location.assign(lobbyReturnPath);
                  }}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Abrir nova solicitação no lobby
                </Button>
              ) : null}
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  // —— Pre-join (Meet-like light layout) ——
  if (phase === "name") {
    const joinCopy = preJoinCopy({
      callContext,
      joinRole,
      fromLobbyEntry,
      isLobbyGuestForm,
      roomLabel,
      requireCamera,
    });
    const consentCopy = recordingConsentCopy({ callContext, recordingVisibility });
    const previewStatusLine = (() => {
      const parts = [displayName.trim() || "Você"];
      if (requireCamera) parts.push(cameraPolicyCopy.previewHint);
      else if (camOff) parts.push("Câmera desligada");
      if (muted) parts.push("microfone desligado");
      return parts.join(" · ");
    })();

    return (
      <Box
        sx={{
          height: "100dvh",
          maxHeight: "100dvh",
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <HeaderBar />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0,1.15fr) minmax(280px,380px)" },
              gap: { xs: 2.5, md: 4 },
              alignItems: { xs: "stretch", md: "start" },
              px: { xs: 2, md: 4 },
              py: { xs: 2, md: 3 },
              maxWidth: 1120,
              mx: "auto",
              width: "100%",
              minHeight: { md: "100%" },
            }}
          >
            {/* Preview */}
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: { xs: "4/3", md: "16/10" },
                  maxHeight: { md: "min(58vh, 520px)" },
                  borderRadius: "16px",
                  overflow: "hidden",
                  bgcolor: "#202124",
                  boxShadow: "0 1px 2px rgba(60,64,67,0.3), 0 2px 6px rgba(60,64,67,0.15)",
                }}
              >
                <Box
                  ref={previewVideoRef}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: "none",
                    visibility: camOff || !previewReady ? "hidden" : "visible",
                    "& video, & canvas": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
                  }}
                />
                {(camOff || !previewReady) && (
                  camOff ? (
                    <VideoCameraOffPresence
                      self
                      name={displayName.trim() || "Você"}
                      avatarUrl={localAvatarUrl}
                      muted={muted}
                      primary={primary}
                    />
                  ) : (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1.5}
                    sx={{ position: "absolute", inset: 0, bgcolor: "#3c4043" }}
                  >
                    <Avatar
                      src={localAvatarUrl || undefined}
                      sx={{
                        width: 96,
                        height: 96,
                        bgcolor: alpha(primary, 0.9),
                        fontSize: "2rem",
                        fontWeight: 700,
                      }}
                    >
                      {displayName.trim() ? initials : <PersonIcon sx={{ fontSize: 40 }} />}
                    </Avatar>
                    {previewError ? (
                      <Typography sx={{ color: "#e8eaed", px: 3, textAlign: "center", fontSize: "0.9rem" }}>
                        {previewError}
                      </Typography>
                    ) : null}
                  </Stack>
                  )
                )}
                {!camOff && previewReady && muted ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: 12,
                      zIndex: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.6,
                      color: "#fff",
                      bgcolor: "rgba(234,67,53,0.92)",
                      px: 1.15,
                      py: 0.4,
                      borderRadius: 999,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                    }}
                  >
                    <MicOffIcon sx={{ fontSize: 15 }} />
                    Microfone desligado
                  </Box>
                ) : null}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 16,
                    zIndex: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    pointerEvents: "none",
                    "& > *": { pointerEvents: "auto" },
                  }}
                >
                  {bgError ? (
                    <Typography
                      sx={{
                        color: "#fce8e6",
                        bgcolor: "rgba(217,48,37,0.85)",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        fontSize: "0.75rem",
                        maxWidth: "90%",
                        textAlign: "center",
                      }}
                    >
                      {bgError}
                    </Typography>
                  ) : null}
                  {mediaControls}
                </Box>
              </Box>
              <Typography
                sx={{
                  mt: 1,
                  color: "text.secondary",
                  fontSize: "0.8rem",
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                {previewStatusLine}
              </Typography>
              {backgroundPicker}
            </Box>

            {/* Join panel — CTA sticky no rodapé do painel */}
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: 440, md: "none" },
                mx: { xs: "auto", md: 0 },
                display: "flex",
                flexDirection: "column",
                minHeight: { md: "min(70vh, 640px)" },
                maxHeight: { md: "calc(100dvh - 88px)" },
                position: { md: "sticky" },
                top: { md: 12 },
                alignSelf: { md: "start" },
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: { xs: "visible", md: "auto" },
                  pr: { md: 0.5 },
                  pb: 1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: "1.5rem", md: "1.65rem" },
                      letterSpacing: "-0.02em",
                      color: "#202124",
                      lineHeight: 1.25,
                    }}
                  >
                    {joinCopy.title}
                  </Typography>
                  {requireCamera && !previewError ? (
                    <Chip
                      size="small"
                      icon={<VideocamIcon sx={{ fontSize: "16px !important" }} />}
                      label={cameraPolicyCopy.bannerTitle}
                      sx={{
                        fontWeight: 700,
                        bgcolor: alpha("#e37400", 0.12),
                        color: "#b06000",
                        "& .MuiChip-icon": { color: "#b06000" },
                      }}
                    />
                  ) : null}
                </Stack>
                <Typography color="text.secondary" sx={{ mb: 1.75, lineHeight: 1.45, fontSize: "0.92rem" }}>
                  {joinCopy.subtitle}
                </Typography>

                {previewError ? (
                  <Alert severity="error" icon={<VideocamIcon fontSize="inherit" />} sx={{ mb: 1.75, py: 0.5 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                      {previewError}
                    </Typography>
                  </Alert>
                ) : null}

                {!isLobbyGuestForm ? (
                  <VideoPreJoinParticipants
                    participants={visibleRoomParticipants}
                    primary={primary}
                    waitingLabel={tChat("video.preJoinWaiting")}
                    oneInCallLabel={tChat("video.preJoinOneInCall")}
                    manyInCallLabel={tChat("video.preJoinManyInCall")}
                    andOthersLabel={tChat("video.preJoinAndOthers")}
                  />
                ) : null}

                {(audioDevices.length > 0 || videoDevices.length > 0) ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    sx={{ mb: 1.5 }}
                  >
                    {videoDevices.length ? (
                      <FormControl fullWidth size="small">
                        <InputLabel id="xpro-video-cam-label">Câmera</InputLabel>
                        <Select
                          labelId="xpro-video-cam-label"
                          label="Câmera"
                          value={videoDeviceId || ""}
                          onChange={(e) => onSelectVideoDevice(e.target.value)}
                          aria-label="Selecionar câmera"
                        >
                          {videoDevices.map((d) => (
                            <MenuItem key={d.deviceId || d.label} value={d.deviceId}>
                              {d.label || "Câmera"}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : null}
                    {audioDevices.length ? (
                      <FormControl fullWidth size="small">
                        <InputLabel id="xpro-video-mic-label">Microfone</InputLabel>
                        <Select
                          labelId="xpro-video-mic-label"
                          label="Microfone"
                          value={audioDeviceId || ""}
                          onChange={(e) => onSelectAudioDevice(e.target.value)}
                          aria-label="Selecionar microfone"
                        >
                          {audioDevices.map((d) => (
                            <MenuItem key={d.deviceId || d.label} value={d.deviceId}>
                              {d.label || "Microfone"}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : null}
                  </Stack>
                ) : null}

                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  label={joinCopy.nameLabel}
                  placeholder={joinCopy.namePlaceholder}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && canJoin && handleJoin()}
                  sx={{ mb: 1.5 }}
                />

                {isLobbyGuestForm ? (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      label="E-mail"
                      placeholder="seu@email.com"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      error={Boolean(emailTrimmed) && !emailLooksValid}
                      helperText={
                        emailTrimmed && !emailLooksValid
                          ? "E-mail inválido"
                          : "Obrigatório se não informar o celular"
                      }
                      sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ mb: 1.5 }}>
                      <PhoneField
                        name="celular"
                        label="Celular"
                        placeholder="(11) 99999-9999"
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        helperText="Obrigatório se não informar o e-mail"
                      />
                    </Box>
                  </>
                ) : null}

                {recordingEnabled ? (
                  <Box
                    sx={{
                      mb: 1.25,
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                      border: `1px solid ${alpha("#000", 0.08)}`,
                      bgcolor: "#f8f9fa",
                    }}
                  >
                    <FormControlLabel
                      sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                      control={
                        <Checkbox
                          checked={recordingConsent}
                          onChange={(e) => setRecordingConsent(e.target.checked)}
                          sx={{ pt: 0.1, mr: 0.5 }}
                          inputProps={{
                            "aria-label": consentCopy.checkboxLabel,
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.84rem",
                              fontWeight: 600,
                              color: "#202124",
                              lineHeight: 1.35,
                            }}
                          >
                            {consentCopy.checkboxLabel}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.35,
                              fontSize: "0.72rem",
                              color: "text.secondary",
                              lineHeight: 1.4,
                            }}
                          >
                            {consentCopy.helper}
                          </Typography>
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setConsentDetailsOpen((v) => !v);
                            }}
                            sx={{
                              mt: 0.35,
                              fontSize: "0.72rem",
                              display: "inline-block",
                              textAlign: "left",
                            }}
                          >
                            {consentDetailsOpen ? "Ocultar detalhes" : consentCopy.detailsToggle}
                          </Link>
                          <Collapse in={consentDetailsOpen}>
                            <Typography
                              sx={{
                                mt: 0.75,
                                fontSize: "0.7rem",
                                color: "text.disabled",
                                lineHeight: 1.4,
                              }}
                            >
                              {consentCopy.details}
                            </Typography>
                          </Collapse>
                        </Box>
                      }
                    />
                  </Box>
                ) : null}

                {joinCopy.showShare ? (
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                      {joinCopy.shareLabel}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          flex: 1,
                          px: 1.25,
                          py: 0.85,
                          borderRadius: 1.5,
                          bgcolor: "#f1f3f4",
                          fontSize: "0.75rem",
                          color: "#3c4043",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {guestShareUrl || shareUrl}
                      </Box>
                      <Button
                        size="small"
                        onClick={copyShareLink}
                        startIcon={copiedLink ? <CheckIcon /> : <ContentCopyIcon />}
                        sx={{ textTransform: "none", flexShrink: 0 }}
                        aria-label={copiedLink ? "Link copiado" : "Copiar link"}
                      >
                        {copiedLink ? "Copiado" : "Copiar"}
                      </Button>
                      {typeof navigator !== "undefined" && navigator.share ? (
                        <IconButton
                          size="small"
                          onClick={shareInviteNative}
                          aria-label="Compartilhar link"
                        >
                          <IosShareOutlinedIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </Stack>
                  </Box>
                ) : null}
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  pt: 1.25,
                  pb: { xs: 1, md: 0.5 },
                  borderTop: `1px solid ${alpha("#000", 0.08)}`,
                  bgcolor: "#fff",
                  position: { xs: "sticky", md: "static" },
                  bottom: 0,
                  zIndex: 2,
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!canJoin}
                  onClick={handleJoin}
                  startIcon={<VideocamIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "1rem",
                    py: 1.25,
                    borderRadius: 999,
                    bgcolor: primary,
                    boxShadow: "none",
                    "&:hover": { bgcolor: theme.palette.primary.dark || primary, boxShadow: "none" },
                  }}
                >
                  {joinCopy.joinButton}
                </Button>
                <Typography
                  sx={{
                    mt: 0.85,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    lineHeight: 1.4,
                    textAlign: "center",
                  }}
                >
                  {joinCopy.footer}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // —— In-call (Meet-like dark) ——
  const waitingCopy = waitingInCallCopy({
    callContext,
    joinRole,
    fromLobbyEntry,
    attendancePolicy: readVirtualOfficeSession()?.attendancePolicy || null,
  });
  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
        bgcolor: "#202124",
        color: "#e8eaed",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <HeaderBar dark />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          px: { xs: 1, md: 2.5 },
          pb: { xs: "calc(88px + env(safe-area-inset-bottom))", md: 12 },
          pt: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            position: "relative",
            minHeight: { xs: 280, md: 0 },
            display: "flex",
            flexDirection: { xs: "column", md: presenting ? "row" : "column" },
            gap: 1.5,
            minWidth: 0,
            borderRadius: voFloorScene ? "16px" : 0,
            overflow: voFloorScene ? "hidden" : "visible",
          }}
        >
          {voFloorScene ? (
            <VoFloorScene daypart={voFloorDaypart()} peopleCount={floorPeopleCount} />
          ) : null}
          {voFloorScene && floorPeek ? (
            <VoFloorCamPeek
              name={floorPeek.name}
              cargo={floorPeek.cargo}
              mirrored={Boolean(floorPeek.local && shouldMirrorLocalPreview(bgMode))}
              track={floorPeekTrack}
              onClose={() => setFloorPeek(null)}
            />
          ) : null}
          {voFloorScene ? (
              <Box
              data-vo-floor-top-rail="1"
              sx={{
                position: "absolute",
                top: { xs: 6, md: 10 },
                left: { xs: 6, md: 12 },
                right: { xs: 6, md: 12 },
                zIndex: 6,
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: { xs: 0.75, md: 2 },
                maxHeight: { xs: "34%", md: "none" },
                overflowY: { xs: "auto", md: "visible" },
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                pointerEvents: "none",
                pr: { xs: 0.25, md: 0 },
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  flex: { md: "1 1 280px" },
                  minWidth: 0,
                  maxWidth: { xs: "100%", md: 360 },
                  pointerEvents: "auto",
                }}
              >
                <VoFloorPrompt
                  prompt={promptForDate()}
                  answers={voPromptAnswers}
                  myAnswer={
                    voPromptAnswers.find(
                      (item) => item.identity === (localIdentityRef.current || localIdentity)
                    )?.answer || ""
                  }
                  hidden={voPromptHidden}
                  onAnswer={(answer) => {
                    const identity =
                      localIdentityRef.current ||
                      roomRef.current?.localParticipant?.identity ||
                      "";
                    const name = voFloorFirstName(displayNameRef.current || "Você", "Você");
                    if (!identity) return;
                    setVoPromptAnswers((prev) => {
                      const next = prev.filter((item) => item.identity !== identity);
                      next.push({ identity, name, answer });
                      return next.slice(-12);
                    });
                    playVoFloorSound("prompt");
                    publishVoData({
                      type: "vo-prompt-answer",
                      from: identity,
                      fromName: displayNameRef.current || name,
                      answer,
                      promptId: promptForDate().id,
                    });
                  }}
                  onDismiss={() => {
                    writeVoFloorPromptHidden(
                      typeof window !== "undefined" ? window.localStorage : null
                    );
                    setVoPromptHidden(true);
                  }}
                />
                {isVoFloorForHuddles && (voFloorHuddles.length || voFloorOtherCalls.length) ? (
                  <Box
                    sx={{
                      p: { xs: 1, md: 1.25 },
                      borderRadius: 2,
                      bgcolor: "rgba(32,33,36,0.88)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    <Stack spacing={{ xs: 1, md: 2 }}>
                      {voFloorHuddles.length ? (
                        <VirtualOfficeHuddleGrid
                          huddles={voFloorHuddles}
                          canAct
                          actingId={voFloorHuddleActingId}
                          onJoin={joinVoFloorHuddle}
                          onRequest={joinVoFloorHuddle}
                          onCreate={() => void openVoPrivateDialog()}
                          tone="dark"
                          compact
                        />
                      ) : null}
                      <VirtualOfficeOtherCallsList people={voFloorOtherCalls} tone="dark" compact />
                    </Stack>
                  </Box>
                ) : null}
              </Stack>
              <Stack
                spacing={1}
                sx={{
                  flex: { md: "0 1 320px" },
                  minWidth: 0,
                  maxWidth: { xs: "100%", md: 340 },
                  ml: { md: "auto" },
                  pointerEvents: "auto",
                  alignItems: { xs: "stretch", md: "flex-end" },
                }}
              >
                <Button
                  onClick={() => void openVoPrivateDialog()}
                  startIcon={<MeetingRoomOutlinedIcon />}
                  title="Abre uma sala no escritório para conversar, fora do piso"
                  sx={{
                    display: { xs: "inline-flex", md: "none" },
                    alignSelf: "flex-start",
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.4,
                    minHeight: 32,
                    bgcolor: "#e8c48a",
                    color: "#3a2414",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.28)",
                    "&:hover": { bgcolor: "#f3d7a8" },
                  }}
                >
                  Abrir sala
                </Button>
                {voBirthdayHonorees.length ? (
                  <VoBirthdayBar
                    people={voBirthdayHonorees}
                    wishes={voBirthdayWishes}
                    selfUserId={resolveAgentUserId()}
                    onCongratulate={(person) => {
                      setVoBirthdayError("");
                      setVoBirthdayTarget(person);
                    }}
                  />
                ) : null}
                {floorHighlights.items.length ? (
                  <VoHighlightsBar
                    items={floorHighlights.items}
                    onOpen={(item) => {
                      setVoHighlightTarget(item);
                      groupHighlightReactions(item?.reactions).forEach((group, index) => {
                        window.setTimeout(() => {
                          pushVoReaction(group.emoji, group.people[0]?.fromName);
                        }, index * 160);
                      });
                    }}
                  />
                ) : null}
              </Stack>
            </Box>
          ) : null}
          {isVoFloorForHuddles ? (
            <VoFloorReactions
              reactions={voReactions}
              arrivals={voArrivals}
              banners={voHouseBanners}
            />
          ) : null}
          {voFloorScene && floorSparks.visible ? (
            <VoFloorSpark
              spark={floorSparks.spark}
              onDismiss={floorSparks.dismiss}
              onEcho={() => {
                playVoFloorSound("spark");
                pushVoReaction("✨", displayNameRef.current, localIdentityRef.current);
                publishVoData({
                  type: "vo-spark-echo",
                  from: localIdentityRef.current || localIdentity,
                  fromName: displayNameRef.current || "Colega",
                });
                floorSparks.dismiss();
              }}
            />
          ) : null}
          {voFloorScene ? (
            <Button
              onClick={() => void openVoPrivateDialog()}
              startIcon={<MeetingRoomOutlinedIcon />}
              title="Abre uma sala no escritório para conversar, fora do piso"
              sx={{
                position: "absolute",
                right: { xs: 8, md: 18 },
                bottom: { xs: 8, md: "28%" },
                zIndex: 5,
                display: { xs: "none", md: "inline-flex" },
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 999,
                px: 1.75,
                bgcolor: "#e8c48a",
                color: "#3a2414",
                boxShadow: "0 6px 18px rgba(0,0,0,0.28)",
                "&:hover": { bgcolor: "#f3d7a8" },
              }}
            >
              Abrir sala
            </Button>
          ) : null}
          <Box
            sx={{
              display: presenting ? "flex" : "none",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 220, md: 0 },
              position: "relative",
            }}
          >
            <Box
              ref={presentationRef}
              sx={{
                position: "relative",
                flex: 1,
                borderRadius: "16px",
                overflow: "hidden",
                bgcolor: "#000",
                minHeight: { xs: 220, md: 0 },
                "& video": {
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  background: "#000",
                  display: "block",
                },
              }}
            />
            {presenterLabel ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  position: "absolute",
                  left: 12,
                  bottom: 12,
                  zIndex: 2,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: "rgba(32,33,36,0.82)",
                  maxWidth: "calc(100% - 24px)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: "#e8eaed",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {presenterLabel}
                </Typography>
                {sharingScreen ? (
                  <Button
                    size="small"
                    onClick={stopScreenShare}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      color: "#fce8e6",
                      minWidth: 0,
                      px: 1,
                    }}
                  >
                    Parar
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </Box>

          {voFloorScene && !presenting ? (
            <Box data-vo-rug-row="1" sx={voFloorRugSx(floorCrowd)}>
              <Box
                data-vo-self-seat="1"
                sx={{
                  pointerEvents: "auto",
                  position: "relative",
                  ...selfSeatFrame,
                }}
              >
                <VoFloorSeat
                  bare
                  self
                  live={!camOff}
                  crowd={floorCrowd}
                  name={displayName.trim() || "Você"}
                  avatarUrl={localAvatarUrl}
                  muted={muted}
                  camOff={camOff}
                  speakerMuted={speakerMuted}
                  primary={primary}
                  cargo={selfCargo || localCargoRef.current}
                  status={selfStatus}
                  joinedAt={selfPresence?.joinedAt || selfPresence?.joined_at || null}
                  now={voFloorNow}
                  timeZone={effectiveClockTz}
                  onOpen={
                    camOff
                      ? (e) => setVoStatusMenuAnchor(e?.currentTarget || null)
                      : () =>
                          setFloorPeek({
                            local: true,
                            name: displayName.trim() || "Você",
                            cargo: selfCargo || localCargoRef.current || "",
                          })
                  }
                >
                  <Box
                    ref={localPipRef}
                    sx={{
                      width: "100%",
                      height: "100%",
                      opacity: camOff ? 0 : 1,
                      bgcolor: "transparent",
                      "& video, & canvas": {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        borderRadius: "50%",
                      },
                    }}
                  />
                </VoFloorSeat>
              </Box>
              <Box
                ref={remoteGridRef}
                sx={(() => {
                  const gridSx = remoteGridSx(remoteLayout, { remoteCount, crowd: floorCrowd });
                  return {
                    ...gridSx,
                    pointerEvents: "auto",
                    display:
                      presenting && remoteCount === 0 && !voPresencePeers.length
                        ? "none"
                        : gridSx.display,
                  };
                })()}
              />
              {voPresenceGhosts
                .slice(0, Math.max(0, MAX_VO_FLOOR_OTHERS - remoteCount))
                .map((person) => {
                  const ident = String(person.twilioIdentity || person.identity || "").trim();
                  const media = (ident && peerMediaByIdentity[ident]) || {};
                  return (
                    <Box
                      key={`presence-${person.userId}`}
                      data-vo-ghost-seat="1"
                      sx={{
                        pointerEvents: "auto",
                        position: "relative",
                        ...ghostSeatFrame,
                      }}
                    >
                      <VoFloorSeat
                        bare
                        crowd={floorCrowd}
                        name={person.userName || "Colega"}
                        avatarUrl={person.userPhoto}
                        status={person.status || "present"}
                        muted={media.muted != null ? Boolean(media.muted) : true}
                        camOff={media.camOff != null ? Boolean(media.camOff) : true}
                        speakerMuted={Boolean(media.speakerMuted)}
                        primary={primary}
                        cargo={presenceCargoOf(person)}
                        joinedAt={person.joinedAt || person.joined_at || null}
                        now={voFloorNow}
                        timeZone={person.timezone || effectiveClockTz}
                        birthday={birthdayUserIdsRef.current.has(Number(person.userId))}
                        kudosEmoji={kudosByUserRef.current.get(Number(person.userId))?.emoji || ""}
                        kudosLabel={highlightSeatLabel(kudosByUserRef.current.get(Number(person.userId)))}
                        kudosTitle={highlightSeatTitle(kudosByUserRef.current.get(Number(person.userId)))}
                        onOpen={(e) =>
                          openVoPersonMenu({
                            userId: person.userId,
                            identity: person.twilioIdentity || person.identity,
                            name: person.userName || "Colega",
                            avatarUrl: person.userPhoto,
                            cargo: presenceCargoOf(person),
                            live: false,
                            anchorEl: e?.currentTarget || null,
                          })
                        }
                      />
                    </Box>
                  );
                })}
            </Box>
          ) : (
            <Box
              ref={remoteGridRef}
              sx={(() => {
                const gridSx = remoteGridSx(remoteLayout, { remoteCount });
                return {
                  ...gridSx,
                  display:
                    presenting && remoteCount === 0 ? "none" : gridSx.display,
                };
              })()}
            />
          )}
          {overflowN > 0 ? (
            <Button
              onClick={() => setPeopleOpen(true)}
              sx={{
                position: "absolute",
                left: voFloorScene ? { xs: 8, md: 16 } : 12,
                bottom: voFloorScene ? { xs: 8, md: 18 } : 12,
                zIndex: 3,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                color: "#e8eaed",
                bgcolor: "rgba(32,33,36,0.88)",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: 999,
                px: 1.5,
                py: 0.5,
                minWidth: 0,
                "&:hover": { bgcolor: "rgba(60,64,67,0.95)" },
              }}
            >
              +{overflowN} {voFloorScene ? "no piso" : "na chamada"}
            </Button>
          ) : null}
          {!voFloorScene && remoteCount === 0 && !sharingScreen ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflowY: "auto",
                py: 2,
                bgcolor: "#3c4043",
                backgroundImage: `radial-gradient(ellipse at 30% 20%, ${alpha(primary, 0.35)}, transparent 55%), radial-gradient(ellipse at 80% 80%, ${alpha("#5f6368", 0.5)}, transparent 50%)`,
                ...(isGuestWaiter
                  ? {
                      "@keyframes xproWaitPulse": {
                        "0%, 100%": { boxShadow: `inset 0 0 0 0 ${alpha(primary, 0)}` },
                        "50%": { boxShadow: `inset 0 0 0 3px ${alpha(primary, 0.45)}` },
                      },
                      animation: "xproWaitPulse 2.4s ease-in-out infinite",
                    }
                  : null),
              }}
            >
              <Stack
                spacing={1.25}
                alignItems="center"
                sx={{
                  px: 3,
                  textAlign: "center",
                  maxWidth: 420,
                  width: "100%",
                }}
              >
                <Typography sx={{ fontSize: { xs: "1.35rem", md: "1.6rem" }, fontWeight: 400 }}>
                  {waitingCopy.title}
                </Typography>
                <Typography sx={{ color: "#bdc1c6", fontSize: "0.95rem", lineHeight: 1.5 }}>
                  {waitingCopy.body}
                </Typography>
                {visibleRoomParticipants.length ? (
                  <Box sx={{ width: "100%", maxWidth: 360, textAlign: "left", mt: 0.5 }}>
                    <VideoPreJoinParticipants
                      participants={visibleRoomParticipants}
                      primary={primary}
                      waitingLabel={tChat("video.preJoinWaiting")}
                      oneInCallLabel={tChat("video.preJoinOneInCall")}
                      manyInCallLabel={tChat("video.preJoinManyInCall")}
                      andOthersLabel={tChat("video.preJoinAndOthers")}
                    />
                  </Box>
                ) : null}
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                  {waitingCopy.showCopyLink ? (
                    <Button
                      variant="contained"
                      onClick={copyShareLink}
                      startIcon={copiedLink ? <CheckIcon /> : <ContentCopyIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 999,
                        bgcolor: primary,
                        px: 2.5,
                      }}
                    >
                      {copiedLink ? "Link copiado" : "Copiar link da chamada"}
                    </Button>
                  ) : null}
                  {isGuestWaiter ? (
                    <Button
                      variant="outlined"
                      onClick={toggleWaitSound}
                      startIcon={
                        waitSoundMuted ? <VolumeOffOutlinedIcon /> : <VolumeUpOutlinedIcon />
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 999,
                        color: "#e8eaed",
                        borderColor: "rgba(255,255,255,0.28)",
                      }}
                      aria-label={
                        waitSoundMuted
                          ? "Ativar som de espera"
                          : "Silenciar som de espera"
                      }
                    >
                      {waitSoundMuted ? "Som de espera off" : "Som de espera"}
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Box>
          ) : null}
          {!voFloorScene && isVoFloorForHuddles && (voFloorHuddles.length || voFloorOtherCalls.length) ? (
            <Box
              sx={{
                position: "absolute",
                left: 12,
                top: 12,
                zIndex: 4,
                width: { xs: "min(100% - 24px, 320px)", sm: 300 },
                maxHeight: { xs: "38%", md: "48%" },
                overflowY: "auto",
                p: 1.25,
                borderRadius: 2,
                bgcolor: "rgba(32,33,36,0.88)",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Stack spacing={2}>
                {voFloorHuddles.length ? (
                  <VirtualOfficeHuddleGrid
                    huddles={voFloorHuddles}
                    canAct
                    actingId={voFloorHuddleActingId}
                    onJoin={joinVoFloorHuddle}
                    onRequest={joinVoFloorHuddle}
                    onCreate={() => void openVoPrivateDialog()}
                    tone="dark"
                  />
                ) : null}
                <VirtualOfficeOtherCallsList people={voFloorOtherCalls} tone="dark" />
              </Stack>
            </Box>
          ) : null}
          {!voFloorScene || presenting ? (
            <>
              <Box
                ref={localPipRef}
                sx={{
                  position: "absolute",
                  right: { xs: 12, md: localPipOffsetMd },
                  bottom: 12,
                  width: { xs: 112, sm: 160 },
                  height: { xs: 84, sm: 112 },
                  borderRadius: "12px",
                  overflow: "hidden",
                  bgcolor: "#3c4043",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                  zIndex: 3,
                  "& video, & canvas": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
                }}
              />
              {camOff ? (
                <Box
                  sx={{
                    position: "absolute",
                    right: { xs: 12, md: localPipOffsetMd },
                    bottom: 12,
                    width: { xs: 112, sm: 160 },
                    height: { xs: 84, sm: 112 },
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.12)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                    zIndex: 4,
                  }}
                >
                  <VideoCameraOffPresence
                    self
                    compact
                    name={displayName.trim() || "Você"}
                    avatarUrl={localAvatarUrl}
                    muted={muted}
                    primary={primary}
                    virtualOffice={false}
                    cargo={selfCargo || localCargoRef.current}
                  />
                </Box>
              ) : muted ? (
                <Box
                  sx={{
                    position: "absolute",
                    right: { xs: 12, md: localPipOffsetMd },
                    bottom: 12,
                    width: { xs: 112, sm: 160 },
                    height: { xs: 84, sm: 112 },
                    borderRadius: "12px",
                    pointerEvents: "none",
                    zIndex: 4,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 6,
                      bottom: 6,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.4,
                      bgcolor: "rgba(234,67,53,0.92)",
                      color: "#fff",
                      px: 0.7,
                      py: 0.15,
                      borderRadius: 999,
                      fontSize: "0.62rem",
                      fontWeight: 700,
                    }}
                  >
                    <MicOffIcon sx={{ fontSize: 12 }} />
                    Mudo
                  </Box>
                </Box>
              ) : null}
            </>
          ) : null}
        </Box>
      </Box>

      {/* Bottom control bar */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          py: { xs: 1, md: 2 },
          pb: { xs: "max(8px, env(safe-area-inset-bottom))", md: 2 },
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 0.75, md: 1.25 }}
          alignItems="center"
          sx={{
            pointerEvents: "auto",
            px: { xs: 1, md: 2 },
            py: { xs: 0.85, md: 1.25 },
            maxWidth: "calc(100% - 12px)",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            borderRadius: 999,
            bgcolor: "#3c4043",
            boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
          }}
        >
          {mediaControls}
          {screenShareButton}
          <ControlButton
            title="Convidar"
            onClick={() => setInviteOpen(true)}
          >
            <PersonAddAlt1OutlinedIcon />
          </ControlButton>
          {peopleButton}
          {chatButton}
          {voStatusButton}
          {voReactionButton}
          {isNarrow ? (
            <>
              <ControlButton
                title="Mais"
                onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
              >
                <MoreVertIcon />
              </ControlButton>
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={closeMoreMenu}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              >
                {appReturnPath ? (
                  <MenuItem
                    onClick={() => {
                      closeMoreMenu();
                      requestReturnToApp();
                    }}
                  >
                    <ListItemIcon>
                      <ArrowBackIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Voltar ao app</ListItemText>
                  </MenuItem>
                ) : null}
                <MenuItem
                  onClick={() => {
                    void requestLiveCaptions(!captionsOn);
                    closeMoreMenu();
                  }}
                >
                  <ListItemIcon>
                    {captionsOn ? (
                      <ClosedCaptionOutlinedIcon fontSize="small" />
                    ) : (
                      <ClosedCaptionDisabledOutlinedIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText>{captionsOn ? "Ocultar legendas" : "Legendas"}</ListItemText>
                </MenuItem>
                {joinRole === "agent" &&
                !(isVirtualOfficeContext || isVirtualOfficeCallContext(callContext)) &&
                !isVoSideRoomCallContext(callContext) ? (
                  <MenuItem
                    disabled={privacyBusy}
                    onClick={() => {
                      toggleRoomPrivacy();
                      closeMoreMenu();
                    }}
                  >
                    <ListItemIcon>
                      {isPrivateRoom ? (
                        <LockOutlinedIcon fontSize="small" />
                      ) : (
                        <LockOpenOutlinedIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText>
                      {isPrivateRoom
                        ? tTeam("activeVideo.makeOpen")
                        : tTeam("activeVideo.makePrivate")}
                    </ListItemText>
                  </MenuItem>
                ) : null}
                {joinRole === "agent" &&
                (isVirtualOfficeContext || isVirtualOfficeCallContext(callContext)) &&
                phase === "incall" ? (
                  <MenuItem
                    disabled={voPrivateBusy}
                    onClick={() => {
                      closeMoreMenu();
                      void openVoPrivateDialog();
                    }}
                  >
                    <ListItemIcon>
                      <MeetingRoomOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Abrir sala</ListItemText>
                  </MenuItem>
                ) : null}
                {joinRole === "agent" && isVoSideRoomCallContext(callContext) && phase === "incall" ? (
                  <MenuItem
                    disabled={voPrivateBusy}
                    onClick={() => {
                      closeMoreMenu();
                      void openVoPrivateDialog();
                    }}
                  >
                    <ListItemIcon>
                      <PersonAddAlt1OutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Incluir colega</ListItemText>
                  </MenuItem>
                ) : null}
                {pipSupported ? (
                  <MenuItem
                    onClick={() => {
                      toggleCallPip();
                      closeMoreMenu();
                    }}
                  >
                    <ListItemIcon>
                      <PictureInPictureAltIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {pipActive ? tChat("video.pipClose") : tChat("video.pipOpen")}
                    </ListItemText>
                  </MenuItem>
                ) : null}
                {canEndForEveryone ? (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        endForEveryone();
                        closeMoreMenu();
                      }}
                      sx={{ color: "error.main" }}
                    >
                      <ListItemIcon>
                        <CallEndIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText>Encerrar para todos</ListItemText>
                    </MenuItem>
                  </>
                ) : null}
              </Menu>
            </>
          ) : (
            <>
              {captionsButton}
              {privacyButton}
              {voPrivateButton}
              {voHuddleRequestsButton}
              {voAdmitButton}
              {pipButton}
              {endForEveryoneButton}
            </>
          )}
          <ControlButton
            danger
            title={
              isVirtualOfficeContext || isVirtualOfficeCallContext(callContext)
                ? virtualOfficeCopy(readVirtualOfficeSession()?.attendancePolicy).leaveButton
                : "Sair da chamada"
            }
            onClick={requestLeave}
            buttonRef={leaveButtonRef}
          >
            <LogoutIcon />
          </ControlButton>
        </Stack>
      </Box>
      <Menu
        anchorEl={voPersonMenu?.anchorEl}
        open={Boolean(voPersonMenu)}
        onClose={() => setVoPersonMenu(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: "#2d3033",
              color: "#e8eaed",
              minWidth: { xs: 0, md: 280 },
              width: { xs: "min(100vw - 24px, 360px)", md: "auto" },
              maxWidth: "calc(100vw - 16px)",
              "& .MuiMenuItem-root": { cursor: "pointer", py: 1.05 },
              "& .MuiListItemIcon-root": { color: "#dadce0", minWidth: 36 },
            },
          },
        }}
      >
        {voPersonMenu ? (
          <Box sx={{ px: 2, pt: 1.25, pb: 0.9, display: "flex", alignItems: "center", gap: 1.25 }}>
            <Avatar
              src={voPersonMenu.avatarUrl || undefined}
              sx={{ width: 40, height: 40, bgcolor: primary, fontSize: "0.85rem", fontWeight: 700 }}
            >
              {String(voPersonMenu.name || "?")
                .trim()
                .split(/\s+/)
                .map((part) => part[0]?.toUpperCase() || "")
                .slice(0, 2)
                .join("")}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }} noWrap>
                {voFloorFirstName(voPersonMenu.name)}
              </Typography>
              <Typography sx={{ color: "#9aa0a6", fontSize: "0.75rem" }} noWrap>
                {voPersonMenu.cargo || voCommStatusLabel(voPersonMenu.status) || "No piso agora"}
              </Typography>
              {floorJoinCaption({
                joinedAt: voPersonMenu.joinedAt,
                now: voFloorNow,
                timeZone: voPersonMenu.timezone || effectiveClockTz,
              }) ? (
                <Typography sx={{ color: "#9aa0a6", fontSize: "0.7rem" }} noWrap>
                  {floorJoinCaption({
                    joinedAt: voPersonMenu.joinedAt,
                    now: voFloorNow,
                    timeZone: voPersonMenu.timezone || effectiveClockTz,
                  })}
                </Typography>
              ) : null}
            </Box>
          </Box>
        ) : null}
        <Typography sx={{ px: 2, pb: 0.4, fontSize: "0.68rem", fontWeight: 700, color: "#9aa0a6", letterSpacing: 0.4, textTransform: "uppercase" }}>
          O que você quer fazer?
        </Typography>
        <MenuItem
          onClick={() => mentionPersonInChat(voPersonMenu)}
        >
          <ListItemIcon>
            <ChatBubbleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={`Falar com ${voFloorFirstName(voPersonMenu?.name)} no chat`}
            secondary="Abre o chat e já marca a pessoa"
            secondaryTypographyProps={{ sx: { color: "#9aa0a6" } }}
          />
        </MenuItem>
        <MenuItem
          disabled={!voPersonMenu?.userId}
          onClick={() => startRoomWithPerson(voPersonMenu)}
        >
          <ListItemIcon>
            <MeetingRoomOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Chamar para uma sala"
            secondary={
              voPersonMenu?.userId
                ? `Vocês dois saem para conversar à parte`
                : "Indisponível para este perfil"
            }
            secondaryTypographyProps={{ sx: { color: "#9aa0a6" } }}
          />
        </MenuItem>
        {voPersonMenu?.live ? (
          <MenuItem
            onClick={() => {
              const peek = voPersonMenu;
              setVoPersonMenu(null);
              setFloorPeek({
                sid: peek.sid,
                identity: peek.identity,
                name: peek.name,
                cargo: peek.cargo || "",
              });
            }}
          >
            <ListItemIcon>
              <VideocamIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Ver câmera maior"
              secondary={`Abre a câmera de ${voFloorFirstName(voPersonMenu?.name)}`}
              secondaryTypographyProps={{ sx: { color: "#9aa0a6" } }}
            />
          </MenuItem>
        ) : null}
        {voPersonMenu?.birthday ? (
          <MenuItem
            onClick={() => {
              const person = voPersonMenu;
              setVoPersonMenu(null);
              setVoBirthdayError("");
              setVoBirthdayTarget({
                userId: person.userId,
                userName: person.name,
                isBirthday: true,
              });
            }}
          >
            <ListItemIcon>
              <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>🎂</Typography>
            </ListItemIcon>
            <ListItemText
              primary="Parabenizar"
              secondary={`Hoje é aniversário de ${voFloorFirstName(voPersonMenu?.name)}`}
              secondaryTypographyProps={{ sx: { color: "#9aa0a6" } }}
            />
          </MenuItem>
        ) : null}
        {voPersonMenu?.seal ? (
          <MenuItem
            onClick={() => {
              const seal = voPersonMenu.seal;
              setVoPersonMenu(null);
              setVoHighlightTarget(seal);
            }}
          >
            <ListItemIcon>
              <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>{voPersonMenu.seal.emoji || "🏅"}</Typography>
            </ListItemIcon>
            <ListItemText
              primary="Ver selo do dia"
              secondary={highlightSeatTitle(voPersonMenu.seal)}
              secondaryTypographyProps={{ sx: { color: "#9aa0a6" } }}
            />
          </MenuItem>
        ) : null}
      </Menu>
      <Menu
        anchorEl={voReactionMenuAnchor}
        open={Boolean(voReactionMenuAnchor)}
        onClose={() => setVoReactionMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {VO_FLOOR_REACTIONS.map((emoji) => (
          <MenuItem
            key={emoji}
            onClick={() => {
              publishVoReaction(emoji);
              setVoReactionMenuAnchor(null);
            }}
          >
            <Typography sx={{ fontSize: "1.35rem", lineHeight: 1 }}>{emoji}</Typography>
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={voStatusMenuAnchor}
        open={Boolean(voStatusMenuAnchor)}
        onClose={() => setVoStatusMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {voCommStatusOptions({ clockApplies, currentStatus: selfStatus }).map((opt) => (
          <MenuItem
            key={opt.value}
            selected={selfStatus === opt.value}
            disabled={selfStatus === "in_other_meeting"}
            onClick={() => void handleVoStatusChange(opt.value)}
          >
            <ListItemText
              primary={opt.label}
              secondary={opt.value === "lunch" || opt.value === "snack" ? opt.hint : undefined}
              secondaryTypographyProps={{ sx: { whiteSpace: "normal", maxWidth: 260 } }}
            />
          </MenuItem>
        ))}
        {selfStatus === "in_other_meeting" ? (
          <MenuItem disabled>
            <ListItemText
              primary={voCommStatusLabel("in_other_meeting")}
              secondary="Volte da reunião para alterar o status."
            />
          </MenuItem>
        ) : null}
      </Menu>
      <VoBirthdayDialog
        open={Boolean(voBirthdayTarget)}
        person={voBirthdayTarget}
        busy={voBirthdayBusy}
        error={voBirthdayError}
        onClose={() => (voBirthdayBusy ? null : setVoBirthdayTarget(null))}
        onSend={(payload) => void sendVoBirthdayWish(payload)}
      />
      <VoHighlightDialog
        item={voHighlightTarget}
        selfUserId={resolveAgentUserId()}
        onClose={() => setVoHighlightTarget(null)}
        onReact={cheerVoHighlight}
        onSpeak={speakToHighlight}
        onCafe={cafeWithHighlight}
      />
      <Dialog
        open={voPrivateOpen}
        onClose={() => (voPrivateBusy ? null : setVoPrivateOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {voPrivateMode === "admit"
            ? "Incluir colega na conversa"
            : "Nova sala no escritório"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {voPrivateMode === "admit"
              ? "Inclua colegas que estão no piso do escritório. Eles receberão um convite."
              : "O piso permanece aberto. Pública: qualquer colega entra. Privada: eles pedem para entrar."}
          </Typography>
          {voPrivateMode === "start" ? (
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {["Café", "Reunião rápida", "Conversa"].map((preset) => (
                  <Chip
                    key={preset}
                    label={preset}
                    color={voHuddleTitle === preset ? "primary" : "default"}
                    variant={voHuddleTitle === preset ? "filled" : "outlined"}
                    onClick={() => setVoHuddleTitle(preset)}
                  />
                ))}
              </Stack>
              <TextField
                size="small"
                label="Nome da sala"
                value={voHuddleTitle}
                onChange={(e) => setVoHuddleTitle(e.target.value)}
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="vo-video-huddle-vis">Visibilidade</InputLabel>
                <Select
                  labelId="vo-video-huddle-vis"
                  label="Visibilidade"
                  value={voHuddleVisibility}
                  onChange={(e) => setVoHuddleVisibility(e.target.value)}
                >
                  <MenuItem value="public">Pública</MenuItem>
                  <MenuItem value="private">Privada</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          ) : null}
          {voPrivateError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {voPrivateError}
            </Alert>
          ) : null}
          <Stack>
            {voPrivatePresence.map((p) => {
              const uid = Number(p.userId);
              const disabled = isVoCommBusyStatus(p.status);
              const checked = voPrivateSelected.includes(uid);
              return (
                <FormControlLabel
                  key={p.id || uid}
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={disabled || voPrivateBusy}
                      onChange={() => {
                        setVoPrivateSelected((prev) =>
                          checked ? prev.filter((id) => id !== uid) : [...prev, uid]
                        );
                      }}
                    />
                  }
                  label={`${p.userName || `Usuário ${uid}`}${
                    disabled
                      ? ` (${p.status === "focus" ? "concentrado" : p.status === "lunch" ? "almoço" : p.status === "snack" ? "lanche" : "indisponível"})`
                      : ""}
                  }`}
                />
              );
            })}
            {!voPrivatePresence.length ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum outro colega presente no floor.
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoPrivateOpen(false)} disabled={voPrivateBusy}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={
              voPrivateBusy ||
              (voPrivateMode === "admit" && !voPrivateSelected.length) ||
              (voPrivateMode === "start" && !String(voHuddleTitle || "").trim())
            }
            onClick={() => void startVoPrivateFromToolbar()}
          >
            {voPrivateMode === "admit" ? "Incluir" : "Abrir sala"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={voHuddleRequestsOpen}
        onClose={() => setVoHuddleRequestsOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Pedidos para entrar</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Colegas no escritório pediram para entrar nesta sala.
          </Typography>
          <Stack spacing={1.25}>
            {voHuddleRequests.map((req) => (
              <Stack
                key={req.id}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography fontWeight={600}>
                  {req.requesterName || `Usuário ${req.requesterUserId}`}
                </Typography>
                <Stack direction="row" spacing={0.75}>
                  <Button
                    size="small"
                    onClick={() => void resolveVoHuddleRequest(req.id, "deny")}
                    disabled={voPrivateBusy}
                  >
                    Recusar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => void resolveVoHuddleRequest(req.id, "approve")}
                    disabled={voPrivateBusy}
                  >
                    Liberar
                  </Button>
                </Stack>
              </Stack>
            ))}
            {!voHuddleRequests.length ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum pedido no momento.
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoHuddleRequestsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <VirtualOfficeJornadaAlerts
        alerts={voJornadaAlerts}
        onLeave={(kind) => void confirmVirtualOfficeLeave(kind)}
        onReturn={() => void handleVoStatusChange("present")}
        leaveBusy={voLeaveBusy}
      />
      <Dialog
        open={voLeaveDialogOpen}
        onClose={dismissVoLeaveDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {virtualOfficeCopy(readVirtualOfficeSession()?.attendancePolicy).leaveTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {virtualOfficeCopy(readVirtualOfficeSession()?.attendancePolicy).leaveBody}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 2, pb: 2 }}>
          {virtualOfficeCopy(readVirtualOfficeSession()?.attendancePolicy).leaveBreak ? (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FreeBreakfastOutlinedIcon />}
              disabled={voLeaveBusy}
              onClick={() => confirmVirtualOfficeLeave("break")}
            >
              {virtualOfficeCopy(readVirtualOfficeSession()?.attendancePolicy).leaveBreak}
            </Button>
          ) : null}
          <Button
            fullWidth
            variant="contained"
            color="warning"
            startIcon={<LogoutIcon />}
            disabled={voLeaveBusy}
            onClick={() => confirmVirtualOfficeLeave("exit")}
          >
            {virtualOfficeCopy(readVirtualOfficeSession()?.attendancePolicy).leaveExit}
          </Button>
          <Button fullWidth disabled={voLeaveBusy} onClick={dismissVoLeaveDialog}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
      {(bgError || screenError || pipError) && phase === "incall" ? (
        <Typography
          sx={{
            position: "fixed",
            left: "50%",
            bottom: 88,
            transform: "translateX(-50%)",
            color: "#fce8e6",
            bgcolor: "rgba(217,48,37,0.9)",
            px: 1.75,
            py: 0.65,
            borderRadius: 999,
            fontSize: "0.78rem",
            maxWidth: "90%",
            textAlign: "center",
            zIndex: 5,
          }}
        >
          {pipError || screenError || bgError}
        </Typography>
      ) : null}
      {voPausedForOtherMeeting && phase === "incall" ? (
        <Typography
          sx={{
            position: "fixed",
            left: "50%",
            top: 24,
            transform: "translateX(-50%)",
            color: "#202124",
            bgcolor: "rgba(251,188,5,0.95)",
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: "0.85rem",
            maxWidth: "90%",
            textAlign: "center",
            zIndex: 6,
          }}
        >
          Escritório Virtual pausado
          {voOtherMeetingTitle ? ` — em reunião: ${voOtherMeetingTitle}` : ""}.
        </Typography>
      ) : null}
      <Snackbar
        open={Boolean(privacySnack)}
        autoHideDuration={3200}
        onClose={() => setPrivacySnack("")}
        message={privacySnack}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
      <Snackbar
        open={Boolean(mediaSnack)}
        autoHideDuration={2800}
        onClose={() => setMediaSnack("")}
        message={mediaSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      {backgroundPicker}
      {phase === "incall" ? (
        <VideoInCallChat
          open={chatOpen}
          onClose={closeChatPanel}
          messages={chatMessages}
          draft={chatDraft}
          onDraftChange={setChatDraft}
          onSend={sendChatMessage}
          onReact={reactToChatMessage}
          sending={chatSending}
          loading={chatLoading}
          localIdentity={localIdentity}
          people={chatMentionPeople}
          title={tChat("video.inCallChat")}
          placeholder={tChat("video.inCallChatPlaceholder")}
          emptyHint={tChat("video.inCallChatEmpty")}
          closeLabel={tChat("video.inCallChatClose")}
          youLabel={tChat("video.inCallChatYou")}
          mentionEveryoneLabel={tChat("video.inCallChatMentionAll")}
          mentionHint={tChat("video.inCallChatMentionHint")}
          replyLabel={tChat("video.inCallChatReply")}
          reactLabel={tChat("video.inCallChatReact")}
          replyingToLabel={tChat("video.inCallChatReplyingTo")}
          cancelReplyLabel={tChat("video.inCallChatCancelReply")}
          attachLabel={tChat("video.inCallChatAttach")}
          removeFileLabel={tChat("video.inCallChatRemoveFile")}
          openFileLabel={tChat("video.inCallChatOpenFile")}
          dropHint={tChat("video.inCallChatDrop")}
          mediaTooLargeLabel={tChat("video.inCallChatMediaTooLarge")}
          snippetLabels={{
            image: tChat("video.inCallChatSnippetImage"),
            video: tChat("video.inCallChatSnippetVideo"),
            audio: tChat("video.inCallChatSnippetAudio"),
            file: tChat("video.inCallChatSnippetFile"),
          }}
          seedMention={chatSeedMention}
          onSeedMentionConsumed={() => setChatSeedMention(null)}
        />
      ) : null}

      {isReconnecting ? (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 20,
            bgcolor: "rgba(32,33,36,0.72)",
            display: "grid",
            placeItems: "center",
            pointerEvents: "none",
          }}
        >
          <Stack spacing={1.5} alignItems="center" sx={{ px: 3, textAlign: "center" }}>
            <CircularProgress size={36} sx={{ color: "#fff" }} />
            <Typography sx={{ color: "#e8eaed", fontWeight: 700, fontSize: "1.1rem" }}>
              Reconectando…
            </Typography>
            <Typography sx={{ color: "#bdc1c6", fontSize: "0.9rem" }}>
              A conexão caiu temporariamente. Não saia da chamada.
            </Typography>
          </Stack>
        </Box>
      ) : null}

      {captionsOn ? (
        <Box
          role="region"
          aria-label="Legendas"
          aria-live="polite"
          sx={{
            position: "fixed",
            left: "50%",
            bottom: { xs: 96, md: 104 },
            transform: "translateX(-50%)",
            zIndex: 6,
            maxWidth: "min(720px, 92vw)",
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: "rgba(0,0,0,0.72)",
            color: "#fff",
            textAlign: "center",
            fontSize: "0.95rem",
            lineHeight: 1.4,
            pointerEvents: "none",
          }}
        >
          {liveCaption || captionHint || "Legendas"}
        </Box>
      ) : null}

      <Drawer
        anchor="right"
        open={peopleOpen}
        onClose={() => setPeopleOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 360 },
            bgcolor: "#292a2d",
            color: "#e8eaed",
          },
        }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>Pessoas</Typography>
          <Typography sx={{ color: "#9aa0a6", fontSize: "0.8rem", mt: 0.25 }}>
            {voFloorScene ? `${floorPeopleCount} no piso` : `${remoteCount + 1} na chamada`}
          </Typography>
        </Box>
        <List dense sx={{ px: 0.5, pb: 2 }}>
          <ListItemButton disabled sx={{ opacity: 1, cursor: "default" }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Avatar
                src={localAvatarUrl || undefined}
                sx={{ width: 32, height: 32, bgcolor: primary, fontSize: "0.75rem" }}
              >
                {initials}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                    {displayName.trim() || "Você"} (você)
                  </Typography>
                  {muted ? (
                    <Chip size="small" icon={<MicOffIcon />} label="Mudo" sx={{ height: 22 }} />
                  ) : null}
                  {speakerMuted ? (
                    <Chip size="small" icon={<VolumeOffOutlinedIcon />} label="Áudio off" sx={{ height: 22 }} />
                  ) : null}
                  {camOff ? (
                    <Chip size="small" icon={<VideocamOffIcon />} label="Câm. off" sx={{ height: 22 }} />
                  ) : null}
                </Stack>
              }
            />
          </ListItemButton>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 0.5 }} />
          {Array.from(roomRef.current?.participants?.values?.() || []).map((p) => {
            const sid = p.sid;
            const identity = String(p.identity || "");
            const name = labelForIdentity(identity);
            const fromTracks = mediaStateFromParticipant(p, isScreenShareTrack);
            const announced = identityMediaRef.current.get(identity) || peerMediaByIdentity[identity];
            const { camOff: tileCamOff, muted: tileMuted, speakerMuted: tileSpeakerMuted } =
              resolveTileMediaState(fromTracks, announced);
            const isPinned = pinnedSid === sid;
            const isSpeaking = dominantSid === sid;
            const peerUserId =
              userIdFromAgentIdentity(identity) ||
              Number(
                (voFloorPresence || []).find(
                  (row) =>
                    String(row.userName || "").trim() === String(name || "").trim()
                )?.userId
              ) ||
              0;
            return (
              <ListItemButton
                key={sid}
                onClick={() => setPinnedSid((prev) => (prev === sid ? null : sid))}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar
                    src={identityAvatarsRef.current.get(identity) || undefined}
                    sx={{ width: 32, height: 32, bgcolor: "#5f6368", fontSize: "0.75rem" }}
                  >
                    {String(name || "?")
                      .trim()
                      .split(/\s+/)
                      .map((part) => part[0]?.toUpperCase() || "")
                      .slice(0, 2)
                      .join("") || "?"}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{name}</Typography>
                      {peerUserId && birthdayUserIdsRef.current.has(Number(peerUserId)) ? (
                        <Chip size="small" label="🎂 Aniversário" sx={{ height: 22, bgcolor: alpha("#f3c4d8", 0.4) }} />
                      ) : null}
                      {peerUserId && cargoByUserRef.current.get(Number(peerUserId)) ? (
                        <Chip
                          size="small"
                          label={cargoByUserRef.current.get(Number(peerUserId))}
                          sx={{ height: 22, bgcolor: alpha("#e8c48a", 0.28) }}
                        />
                      ) : null}
                      {peerUserId && honoreeSeal(floorHighlights.items, peerUserId) ? (
                        <Chip
                          size="small"
                          label={`${honoreeSeal(floorHighlights.items, peerUserId).emoji} Selo do dia`}
                          sx={{ height: 22, bgcolor: alpha("#e8c48a", 0.35) }}
                        />
                      ) : null}
                      {isSpeaking ? (
                        <Chip size="small" label="Falando" sx={{ height: 22, bgcolor: alpha("#8ab4f8", 0.25) }} />
                      ) : null}
                      {isPinned ? (
                        <Chip
                          size="small"
                          icon={<PushPinIcon sx={{ fontSize: "14px !important" }} />}
                          label="Fixado"
                          sx={{ height: 22, bgcolor: alpha("#fdd663", 0.2) }}
                        />
                      ) : null}
                      {tileMuted ? (
                        <Chip size="small" icon={<MicOffIcon />} label="Mudo" sx={{ height: 22 }} />
                      ) : null}
                      {tileSpeakerMuted ? (
                        <Chip size="small" icon={<VolumeOffOutlinedIcon />} label="Áudio off" sx={{ height: 22 }} />
                      ) : null}
                      {tileCamOff ? (
                        <Chip size="small" icon={<VideocamOffIcon />} label="Câm. off" sx={{ height: 22 }} />
                      ) : null}
                    </Stack>
                  }
                />
                <ListItemIcon sx={{ minWidth: isVoFloorForHuddles && peerUserId ? 92 : 36, justifyContent: "flex-end" }}>
                  {isVoFloorForHuddles && peerUserId ? (
                    <Button
                      size="small"
                      startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                      disabled={officeLife.sayHiBusyId === peerUserId}
                      onClick={(e) => {
                        e.stopPropagation();
                        void officeLife.sayHiToUser(peerUserId).catch((err) => {
                          setScreenError(
                            err?.response?.data?.message || err?.message || "Não foi possível abrir o chat."
                          );
                        });
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        color: "#e8c48a",
                        minWidth: 0,
                        mr: 0.5,
                      }}
                    >
                      Oi
                    </Button>
                  ) : null}
                  {isVoFloorForHuddles &&
                  peerUserId &&
                  birthdayUserIdsRef.current.has(Number(peerUserId)) &&
                  Number(peerUserId) !== Number(resolveAgentUserId()) ? (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVoBirthdayError("");
                        setVoBirthdayTarget({
                          userId: peerUserId,
                          userName: name,
                          isBirthday: true,
                        });
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 800,
                        fontSize: "0.72rem",
                        color: "#f3c4d8",
                        minWidth: 0,
                        mr: 0.5,
                      }}
                    >
                      🎂
                    </Button>
                  ) : null}
                  {isPinned ? (
                    <PushPinIcon fontSize="small" sx={{ color: "#fdd663" }} />
                  ) : (
                    <PushPinOutlinedIcon fontSize="small" sx={{ color: "#9aa0a6" }} />
                  )}
                </ListItemIcon>
              </ListItemButton>
            );
          })}
          {voFloorScene
            ? voPresenceGhosts.map((person) => (
                <ListItemButton key={`presence-list-${person.userId}`} disabled sx={{ opacity: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      src={person.userPhoto || undefined}
                      sx={{ width: 32, height: 32, bgcolor: "#5f6368", fontSize: "0.75rem" }}
                    >
                      {String(person.userName || "?")
                        .trim()
                        .split(/\s+/)
                        .map((part) => part[0]?.toUpperCase() || "")
                        .slice(0, 2)
                        .join("")}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                        {person.userName || "Colega"}
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ color: "#9aa0a6", fontSize: "0.75rem" }}>
                        {presenceCargoOf(person) || "No piso"}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))
            : null}
        </List>
      </Drawer>

      <Drawer
        anchor="bottom"
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxWidth: 560,
            mx: "auto",
            px: 2.5,
            pt: 2,
            pb: 3,
          },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 0.5 }}>
          Convidar
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 2, lineHeight: 1.45 }}>
          {teamCall
            ? "Copie o link da sala da equipe para reenviar no chat interno."
            : "Copie o link do convidado para enviar pelo canal ou compartilhar."}
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 1.1,
            borderRadius: 2,
            bgcolor: "#f1f3f4",
            fontSize: "0.8rem",
            color: "#3c4043",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mb: 1.5,
          }}
        >
          {guestShareUrl || shareUrl}
        </Box>
        <Stack spacing={1}>
          <Button
            variant="contained"
            onClick={copyShareLink}
            startIcon={copiedLink ? <CheckIcon /> : <ContentCopyIcon />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, bgcolor: primary }}
          >
            {copiedLink
              ? "Link copiado"
              : teamCall
                ? "Copiar link da equipe"
                : "Copiar link do convidado"}
          </Button>
          {typeof navigator !== "undefined" && navigator.share ? (
            <Button
              variant="outlined"
              onClick={shareInviteNative}
              startIcon={<IosShareOutlinedIcon />}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 999 }}
            >
              Compartilhar
            </Button>
          ) : null}
          {joinRole === "agent" && !teamCall ? (
            <Button
              variant="outlined"
              onClick={useInviteInComposer}
              startIcon={<EditNoteOutlinedIcon />}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 999 }}
            >
              Usar no campo de envio
            </Button>
          ) : null}
          <Button onClick={() => setInviteOpen(false)} sx={{ textTransform: "none" }}>
            Fechar
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
}
